/*!
 * STILL 16.0 · storefront script for the nocta-v8-* sections
 *
 * 16.0: while the shop sells with cash on delivery, every buy button opens the
 * Releasit order form with the picked lens, pair or set (26-cod, "STILL · Utánvét").
 *
 * Every STILL section includes this file. The browser loads it once and only the
 * first copy runs. Sections mount themselves and clean up after themselves, so
 * the theme editor can add, remove, reorder and re-render them freely:
 *   shopify:section:load    → mount the section again with its new settings
 *   shopify:section:unload  → remove its listeners, timers, photo layers and overlays
 *   shopify:block:select    → show that block (an FAQ answer, a review)
 *
 * 10.0: the frame is shown with real product photos (front, angled, side, hinge
 * close-up, folded, lying; one set per lens) from the STILL · Photos section, or
 * the ones that come with the theme. They turn, crossfade and zoom with opacity
 * and transforms only. No 3D library is loaded.
 * 13.0: "Inside the frame" is the teardown film alone, a seamless ping-pong loop. The
 * gallery's turning frame is a sequence of real frames with the lens colour multiplied
 * in; the lens-material close-ups are rendered frames tied to the scroll. No library
 * is loaded (GSAP is gone).
 */
(function () {
  'use strict';
  if (window.Still && window.Still.version) return;

  /* ============================================================
     helpers
     ============================================================ */
  const SCRIPT = document.currentScript;
  const html = document.documentElement;
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.prototype.slice.call((r || document).querySelectorAll(s));
  const clamp = (v, a, b) => Math.min(b == null ? 1 : b, Math.max(a == null ? 0 : a, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const smooth = t => t * t * (3 - 2 * t);
  const easeIO = t => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const easeOut = t => 1 - Math.pow(1 - t, 3);
  const mq = q => (window.matchMedia ? window.matchMedia(q) : { matches: false });
  const reduceQ = mq('(prefers-reduced-motion: reduce)');
  let reduce = reduceQ.matches;
  try { reduceQ.addEventListener('change', e => { reduce = e.matches; }); } catch (e) { /* old Safari */ }
  const finePtr = mq('(hover: hover) and (pointer: fine)').matches;
  const vib = ms => { try { navigator.vibrate && navigator.vibrate(ms); } catch (e) { /* not supported */ } };
  const designMode = () => !!(window.Shopify && window.Shopify.designMode) || html.hasAttribute('data-nx-design');
  const locale = () => (html.getAttribute('lang') || navigator.language || 'en').replace('_', '-');
  const pad = n => String(n).padStart(2, '0');
  /* Hungarian dates read differently from the Intl defaults: a weekday "on" takes a suffix
     (hétfőn, kedden…), and a date "on" the day too (október 25-én). Used when the shop's
     language is Hungarian; other languages keep Intl's own wording. */
  const isHu = l => /^hu\b/i.test(String(l || ''));
  const HU_ON = ['vasárnap', 'hétfőn', 'kedden', 'szerdán', 'csütörtökön', 'pénteken', 'szombaton'];
  const huDaySuffix = d => (d === 1 ? 'jén' : d === 2 ? 'án' : [3, 6, 8, 13, 16, 18, 20, 23, 26, 28, 30].indexOf(d) >= 0 ? 'án' : 'én');
  function huDate(d, opt) {                     // d: a Date at noon UTC on that day
    const mon = new Intl.DateTimeFormat('hu-HU', { month: opt && opt.short ? 'short' : 'long', timeZone: 'UTC' }).format(d);
    const wd = new Intl.DateTimeFormat('hu-HU', { weekday: 'long', timeZone: 'UTC' }).format(d);
    const day = d.getUTCDate();
    if (opt && opt.on) return wd + ', ' + mon + ' ' + day + '-' + huDaySuffix(day);    // vasárnap, október 25-én
    return mon + ' ' + day + '., ' + wd;                                                 // okt. 7., szerda (as in the translation document)
  }
  const hexMix = (a, b, t) => { const h = s => [1, 3, 5].map(i => parseInt(s.slice(i, i + 2), 16)); const x = h(a), y = h(b); return '#' + x.map((v, i) => Math.round(lerp(v, y[i], t)).toString(16).padStart(2, '0')).join(''); };
  const reported = new Set();
  function report(where, e) {
    const k = where + ':' + (e && e.message);
    if (reported.has(k)) return; reported.add(k);
    try { console.warn('[nocta] ' + where, e); } catch (_) { /* no console */ }
  }
  /* What scrolls the page: usually the window, but some themes scroll a wrapper instead
     (Horizon scrolls its .page-wrapper from 990 px up, so window.scrollY stays 0 there).
     Found by walking up from a STILL section; looked for again after every resize. */
  let scrollBox;                                        // undefined: not looked for yet; null: the window
  function pageScroller() {
    if (scrollBox !== undefined && (scrollBox === null || scrollBox.isConnected)) return scrollBox;
    scrollBox = null;
    const from = document.querySelector('[data-nocta]');
    for (let el = from && from.parentElement; el && el !== document.body && el !== html; el = el.parentElement) {
      const o = getComputedStyle(el).overflowY;
      if ((o === 'auto' || o === 'scroll' || o === 'overlay') && el.scrollHeight > el.clientHeight + 1) { scrollBox = el; break; }
    }
    return scrollBox;
  }
  window.addEventListener('resize', () => { scrollBox = undefined; }, { passive: true });
  const pageY = () => { const s = pageScroller(); return s ? s.scrollTop : (window.scrollY || window.pageYOffset || 0); };
  const pageH = () => { const s = pageScroller(); return s ? s.scrollHeight : html.scrollHeight; };
  /* where an element's top is, in the page scroller's coordinates */
  const pageTop = el => { const s = pageScroller(); return el.getBoundingClientRect().top - (s ? s.getBoundingClientRect().top : 0) + pageY(); };
  function jumpTo(y) {           // instant, even when the theme sets scroll-behavior: smooth
    const s = pageScroller(), el = s || html, b = el.style.scrollBehavior;
    el.style.scrollBehavior = 'auto';
    (s || window).scrollTo(0, Math.max(0, y)); el.style.scrollBehavior = b;
  }
  function glideTo(y) { (pageScroller() || window).scrollTo({ top: Math.max(0, y), behavior: reduce ? 'auto' : 'smooth' }); }

  const strongEl = (txt, tag) => { const s = document.createElement(tag || 'strong'); s.textContent = txt; return s; };
  /* fills an element from a merchant template such as "Only [count] left", escaping everything */
  function fillTpl(el, str, tokens) {
    if (!el) return;
    el.textContent = '';
    String(str == null ? '' : str).split(/(\[[a-z_]+\])/g).forEach(part => {
      if (!part) return;
      const m = /^\[([a-z_]+)\]$/.exec(part);
      if (m && tokens && Object.prototype.hasOwnProperty.call(tokens, m[1])) {
        const t = tokens[m[1]];
        el.appendChild(t && t.nodeType ? t : document.createTextNode(t == null ? '' : String(t)));
      } else el.appendChild(document.createTextNode(part));
    });
  }
  /* replaces [tokens] inside text nodes of rich text the merchant wrote */
  function fillTokens(el, map) {
    if (!el) return;
    const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT), nodes = [];
    while (w.nextNode()) nodes.push(w.currentNode);
    nodes.forEach(n => { n.nodeValue = n.nodeValue.replace(/\[([a-z_]+)\]/g, (all, k) => (k in map ? map[k] : all)); });
  }

  /* Shopify money format ("{{amount_no_decimals_with_space_separator}} Ft", "{{amount_with_comma_separator}} kr", …) */
  function formatMoney(cents, format) {
    cents = Math.round(Number(cents) || 0);
    format = String(format || '{{amount_no_decimals_with_space_separator}} Ft').replace(/<[^>]*>/g, '');   // 16.0: the shop sells in forints
    const rx = /\{\{\s*(\w+)\s*\}\}/, m = format.match(rx);
    if (!m) return format;
    const num = (d, ts, ds) => {
      const n = (cents / 100).toFixed(d).split('.');
      return n[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + ts) + (n[1] ? ds + n[1] : '');
    };
    const map = {
      amount: () => num(2, ',', '.'),
      amount_no_decimals: () => num(0, ',', '.'),
      amount_with_comma_separator: () => num(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => num(0, '.', ','),
      amount_with_apostrophe_separator: () => num(2, "'", '.'),
      amount_no_decimals_with_space_separator: () => num(0, ' ', ','),
      amount_with_space_separator: () => num(2, ' ', ','),
      amount_with_period_and_space_separator: () => num(2, ' ', '.')
    };
    // 17.0: a price never breaks over two lines ("9 990 / Ft"): its spaces do not break
    return format.replace(rx, (map[m[1]] || map.amount)()).replace(/ /g, '\u00a0');
  }
  const perNight = cents => Math.round((Number(cents) || 0) / 365);   // Liquid rounds the same way
  /* small amounts (per night) keep their decimals even in no-decimal currencies */
  function formatSmall(cents, format) {
    const out = formatMoney(cents, format);
    if (/no_decimals/.test(format || '') && cents < 100) return formatMoney(cents, String(format).replace(/amount_no_decimals(_with_comma_separator|_with_space_separator)?/, (a, b) => 'amount' + (b || '')));
    return out;
  }

  /* ============================================================
     the three lenses (colour only: text comes from the sections)
     ============================================================ */
  const LENS = {
    day:  { dot: '#E3EEF8', glow: '#9fc1ee', sim: { bg: 'rgba(214,228,248,.5)', f: 'saturate(.97) brightness(1.02)', gbg: 'rgba(238,233,224,.5)', g: 'brightness(.95) contrast(1.06)' } },
    play: { dot: '#EBD83A', glow: '#efd23c', sim: { bg: '#f7d83c', f: 'saturate(1.05) contrast(1.06)', gbg: '#f9d955', g: 'brightness(.97) contrast(1.06)' } },
    dusk: { dot: '#F2651C', glow: '#ff7a2a', sim: { bg: '#ff7418', f: 'saturate(1.1) brightness(.97)', gbg: '#ffa257', g: 'brightness(.93) contrast(1.08)' } }
  };
  const KEYS = ['day', 'play', 'dusk'];
  const SET_DOT = 'linear-gradient(90deg,' + LENS.day.dot + ',' + LENS.play.dot + ',' + LENS.dusk.dot + ')';
  /* which lens a name means, whole words only, English or Hungarian (like snippets/nocta-v8-lens) */
  const LW = (words) => new RegExp('(?:^|[^a-z0-9áéíóöőúüű])(' + words + ')(?=$|[^a-z0-9áéíóöőúüű])');
  const LK = [['set', LW('set|bundle|trio|all three|full day|szett|készlet|keszlet')], ['dusk', LW('havana|dusk|amber|orange|night|borostyán|borostyan')],
    ['play', LW('lisbon|lisboa|lisszabon|play|yellow|gaming|sárga|sarga')], ['day', LW('oslo|day|clear|átlátszó|atlatszo')]];
  function lensKey(s) {
    s = String(s || '').toLowerCase();
    for (const [k, rx] of LK) if (rx.test(s)) return k;
    return null;
  }
  /* a copy of a drawn scene for the lens to look through; its ids are dropped (they would be
     duplicates) and its gradients keep pointing at the original's, which stays in the page */
  function cloneScene(base) {
    const c = base.cloneNode(true);
    c.setAttribute('aria-hidden', 'true');
    $$('[id]', c).forEach(n => n.removeAttribute('id'));
    return c;
  }
  const lensDot = k => (k === 'set' ? SET_DOT : (LENS[k] || {}).dot || '#dcdce0');
  /* "Oslo + Lisbon": the two lenses of a pair, in the order they are named */
  const pairLenses = t => String(t || '').split(/\s*\+\s*|\s*&\s*|\s+és\s+|\s+and\s+/i).map(x => lensKey(x)).filter(k => k && LENS[k] && k !== 'set');
  const lensAtHour = h => (h >= 5 && h < 13 ? 'day' : h >= 13 && h < 19 ? 'play' : 'dusk');   // 17.0: Oslo reggel, Lisbon délután, Havana este

  /* lens outline of the drawn glasses (the late-screen simulator, the lens cards, the amber wash) */
  function lensPoints(side, g, N) {
    N = N || 160;
    const planes = [[0, 1, .405 + g * 1.35], [.6 * side, .8, .455 + g], [-.6 * side, .8, .52 + g], [.8 * side, -.6, .5 + g]];
    let pts = [];
    for (let i = 0; i < N; i++) {
      const t = i / N * Math.PI * 2;
      let x = Math.cos(t) * (.535 + g), y = Math.sin(t) * (.47 + g), s = 1;
      for (const [nx, ny, d] of planes) { const dp = nx * x + ny * y; if (dp > d) s = Math.min(s, d / dp); }
      pts.push([x * s, y * s]);
    }
    for (let k = 0; k < 7; k++) pts = pts.map((p, i) => { const a = pts[(i - 1 + N) % N], b = pts[(i + 1) % N]; return [(a[0] + 2 * p[0] + b[0]) / 4, (a[1] + 2 * p[1] + b[1]) / 4]; });
    return pts;
  }
  const svgPath = (pts, sc, ox, oy) => 'M' + pts.map(p => (p[0] * sc + ox).toFixed(1) + ' ' + (-p[1] * sc + oy).toFixed(1)).join('L') + 'Z';
  const LP = { in: [lensPoints(-1, 0, 90), lensPoints(1, 0, 90)], rim: [lensPoints(-1, .04, 90), lensPoints(1, .04, 90)] };
  /* draws the pair of glasses into an svg (3 paths) and returns the clip path for the lenses */
  function drawGlasses(paths, x, y, sc) {
    const sw = (.085 * sc).toFixed(1), bx = .17 * sc, by = y - .29 * sc;
    paths[0].setAttribute('d', svgPath(LP.rim[0], sc, x - .64 * sc, y));
    paths[1].setAttribute('d', svgPath(LP.rim[1], sc, x + .64 * sc, y));
    paths[2].setAttribute('d', 'M' + (x - bx) + ' ' + (by + .03 * sc) + ' Q' + x + ' ' + (by - .06 * sc) + ' ' + (x + bx) + ' ' + (by + .03 * sc) +
      ' M' + (x - 1.2 * sc) + ' ' + (y - .2 * sc) + ' l' + (-.22 * sc) + ' ' + (-.03 * sc) + ' M' + (x + 1.2 * sc) + ' ' + (y - .2 * sc) + ' l' + (.22 * sc) + ' ' + (-.03 * sc));
    paths.forEach(p => p.setAttribute('stroke-width', sw));
    return "path('" + svgPath(LP.in[0], sc, x - .64 * sc, y) + ' ' + svgPath(LP.in[1], sc, x + .64 * sc, y) + "')";
  }
  /* Glasses that move over a scene with transforms only (8.1). 15.0: the glasses are the real frame
     (nocta-frame-cut: the clear studio front shot w1, cut out, no floor shadow) instead of a drawn
     outline; the lens window is the photo's own lens openings, traced from it (FCUT.lens: points in
     units of the photo's width, from its centre). The photo and the lens window are sized once per
     size, then moved; the scene seen through the lens moves back the other way inside it, so it stays
     in place. No repaint while they move. */
  const FCUT = { aspect: 0.3554, lens: [[[-0.2717,-0.1549],[-0.2909,-0.1531],[-0.3096,-0.1499],[-0.3277,-0.1454],[-0.3451,-0.1395],[-0.3619,-0.1317],[-0.3776,-0.1219],[-0.3913,-0.1096],[-0.402,-0.0946],[-0.4102,-0.078],[-0.416,-0.0605],[-0.4195,-0.042],[-0.421,-0.0229],[-0.4206,-0.0035],[-0.4186,0.0156],[-0.4151,0.0341],[-0.4097,0.0518],[-0.4023,0.0687],[-0.3932,0.0849],[-0.3823,0.1003],[-0.3694,0.1145],[-0.3548,0.1269],[-0.3391,0.1372],[-0.3225,0.1452],[-0.3051,0.1514],[-0.2873,0.1565],[-0.2689,0.159],[-0.25,0.1583],[-0.2308,0.1566],[-0.212,0.1539],[-0.194,0.1492],[-0.1766,0.1429],[-0.1601,0.1347],[-0.1445,0.1242],[-0.1299,0.1116],[-0.1165,0.0976],[-0.1048,0.0827],[-0.0951,0.0669],[-0.0875,0.0502],[-0.0817,0.0326],[-0.0776,0.0144],[-0.0754,-0.0046],[-0.0753,-0.0242],[-0.0775,-0.0432],[-0.0823,-0.0612],[-0.0897,-0.078],[-0.0997,-0.0938],[-0.1123,-0.1082],[-0.1269,-0.1205],[-0.1426,-0.1306],[-0.1593,-0.1386],[-0.1766,-0.1448],[-0.1946,-0.1495],[-0.2133,-0.1525],[-0.2325,-0.1543],[-0.2521,-0.1551]],[[0.2309,-0.1547],[0.2117,-0.1528],[0.1929,-0.1497],[0.1747,-0.1454],[0.1572,-0.1394],[0.1405,-0.1315],[0.1245,-0.1217],[0.1097,-0.1097],[0.0968,-0.0956],[0.0863,-0.0799],[0.0786,-0.0631],[0.0736,-0.0451],[0.0712,-0.0261],[0.071,-0.0066],[0.0727,0.0127],[0.0764,0.0312],[0.082,0.0489],[0.0894,0.0658],[0.0988,0.0818],[0.1103,0.0968],[0.1235,0.111],[0.138,0.1237],[0.1536,0.1342],[0.1701,0.1426],[0.1875,0.149],[0.2056,0.1537],[0.2244,0.1565],[0.2437,0.1577],[0.263,0.1573],[0.2819,0.1549],[0.3002,0.1508],[0.3179,0.1452],[0.3346,0.1373],[0.3504,0.127],[0.3651,0.1146],[0.3784,0.1005],[0.3898,0.0852],[0.3993,0.0692],[0.4072,0.0524],[0.4137,0.0351],[0.4178,0.0172],[0.4193,-0.0012],[0.4191,-0.02],[0.4176,-0.0392],[0.4144,-0.0579],[0.409,-0.0757],[0.4008,-0.0923],[0.3896,-0.1075],[0.3761,-0.1208],[0.3607,-0.1311],[0.3439,-0.1389],[0.3264,-0.1451],[0.3083,-0.1497],[0.2897,-0.1529],[0.2704,-0.1548],[0.2506,-0.1553]]] };
  function cutPath(fw) {
    // each outline joined with smooth curves through the midpoints of its points
    return FCUT.lens.map(P => {
      const n = P.length, pt = i => P[(i + n) % n], mid = i => [(pt(i)[0] + pt(i + 1)[0]) / 2 * fw, (pt(i)[1] + pt(i + 1)[1]) / 2 * fw];
      let d = 'M' + mid(-1)[0].toFixed(1) + ' ' + mid(-1)[1].toFixed(1);
      for (let i = 0; i < n; i++) d += 'Q' + (pt(i)[0] * fw).toFixed(1) + ' ' + (pt(i)[1] * fw).toFixed(1) + ' ' + mid(i)[0].toFixed(1) + ' ' + mid(i)[1].toFixed(1);
      return d + 'Z';
    }).join(' ');
  }
  function photoRig(img, lensEl) {
    let inner = lensEl.querySelector(':scope > .nx-lens-in');
    if (!inner) { inner = document.createElement('div'); inner.className = 'nx-lens-in'; while (lensEl.firstChild) inner.appendChild(lensEl.firstChild); lensEl.appendChild(inner); }
    let size = '', at = '', fw = 0;
    return {
      inner,
      /* x, y: the centre of the glasses; w: the frame's width in px; sw, sh: the scene's size */
      set(x, y, w, sw, sh) {
        const k = w.toFixed(1) + ',' + sw.toFixed(1) + ',' + sh.toFixed(1);
        if (k !== size) {
          size = k; at = ''; fw = w;
          img.style.width = w.toFixed(1) + 'px'; img.style.height = (w * FCUT.aspect).toFixed(1) + 'px';
          const clip = "path('" + cutPath(w) + "')";
          lensEl.style.clipPath = clip; lensEl.style.webkitClipPath = clip;
          inner.style.width = sw.toFixed(1) + 'px'; inner.style.height = sh.toFixed(1) + 'px';
        }
        const a = x.toFixed(1) + 'px,' + y.toFixed(1) + 'px';
        if (a === at) return; at = a;
        lensEl.style.transform = 'translate3d(' + a + ',0)';
        img.style.transform = 'translate3d(' + (x - fw / 2).toFixed(1) + 'px,' + (y - fw * FCUT.aspect / 2).toFixed(1) + 'px,0)';
        inner.style.transform = 'translate3d(' + (-x).toFixed(1) + 'px,' + (-y).toFixed(1) + 'px,0)';
      },
      reset() { size = at = ''; }
    };
  }
  /* finger or mouse over a scene: a mouse follows at once; a finger moves the glasses with a tap,
     or with a sideways drag once it has clearly gone sideways, so a vertical swipe always scrolls */
  function pointTo(el, bag, cb) {
    let s = null;
    const at = e => { const r = el.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
    bag.on(el, 'pointerdown', e => {
      if (e.button > 0) return;
      if (e.pointerType === 'mouse') { s = { mouse: true }; try { el.setPointerCapture(e.pointerId); } catch (_) { /* old browsers */ } cb('down', at(e), true); return; }
      s = { x: e.clientX, y: e.clientY, side: false, id: e.pointerId };
    });
    bag.on(el, 'pointermove', e => {
      if (e.pointerType === 'mouse') { cb('move', at(e), true, !!s); return; }
      if (!s || s.mouse || e.pointerId !== s.id) return;
      const dx = e.clientX - s.x, dy = e.clientY - s.y;
      if (!s.side) { if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) { s = null; return; } if (Math.abs(dx) > 10) s.side = true; else return; }
      cb('move', at(e));
    });
    bag.on(el, 'pointerup', e => { if (s && !s.mouse && !s.side) cb('move', at(e)); if (s) cb('end', null, !!s.mouse); s = null; });
    bag.on(el, 'pointercancel', () => { if (s) cb('end'); s = null; });
    bag.on(el, 'pointerleave', e => { if (e.pointerType === 'mouse') { s = null; cb('leave', null, true); } });
  }

  /* ============================================================
     PHOTOS (10.0): the frame is real product photography, not a
     3D model. Every lens has the same six shots from one shoot
     (front, angled, side, hinge close-up, folded, lying), so a lens
     change is a crossfade between two identical pictures and a turn
     is a short sequence of angles. The photos have a transparent
     background, so they sit on any section, light or dark. A stage
     keeps its photos as layers in its slot and changes only their
     opacity and transform: nothing is redrawn while the page
     scrolls, and a photo loads only when its section is near.
     ============================================================ */
  const VIEWS = ['front', 'angled', 'side', 'hinge', 'folded', 'lying'];
  /* where each shot sits on a 2 × 3 photo sheet laid out like the supplied ones
     (px of a 1448 × 1086 sheet; every box is 2:1, like the single photos) */
  const SHEET_PX = { front: [-6, 24, 740, 370], angled: [724, 26, 740, 370], side: [-4, 322, 740, 370], folded: [706, 337, 740, 370], hinge: [22, 689, 714, 357], lying: [716, 660, 740, 370] };
  /* a turn, as angles -2 … 2: front, angled, side. The angled and side shots show the frame
     turned to the left; the same shots mirrored turn it to the right. */
  const TURN = { '-2': ['side', 1], '-1': ['angled', 1], '0': ['front', 0], '1': ['angled', 0], '2': ['side', 0] };
  const TURN_SEQ = [0, 1, 2, 1, 0, -1, -2, -1];
  /* where the parts are on the angled shot (fractions of its box), for the tour's labels */

  const PH = { conf: { lenses: {} }, ver: 0, base: '' };
  /* the photos the merchant uploaded (STILL · Photos section), read again when it changes in the editor */
  function readPhotos() {
    let c = null;
    const el = $('script[data-nx-photos]');
    if (el) { try { c = JSON.parse(el.textContent); } catch (e) { report('photos', e); } }
    PH.conf = c && typeof c === 'object' ? c : {};
    if (!PH.conf.lenses || typeof PH.conf.lenses !== 'object') PH.conf.lenses = {};
    PH.base = (SCRIPT && SCRIPT.getAttribute('data-photo')) || PH.base || '';
    PH.ver++;
  }
  /* a photo that comes with the theme: asset_url gave the front Oslo shot, the rest sit next to it
     (each also as a lighter copy, -s, for phones and small boxes) */
  const themePhoto = name => (PH.base ? PH.base.replace(/nocta-photo-day-front\.webp/, name) : '');
  /* → { u: url, box: [x, y, w, h] of a sheet or null, key: its white still has to go, own: framed by the merchant } */
  function photoSrc(lens, view, small) {
    const L = PH.conf.lenses[lens] || {};
    const one = L[view];
    if (one && one[0]) return { u: (small && one[1]) || one[0], box: null, key: true, own: true };
    if (L.sheet) { const b = SHEET_PX[view] || SHEET_PX.front; return { u: L.sheet, box: [b[0] / 1448, b[1] / 1086, b[2] / 1448, b[3] / 1086], key: true, own: false }; }
    return { u: themePhoto('nocta-photo-' + lens + '-' + view + (small ? '-s' : '') + '.webp'), box: null };
  }
  /* a photo of your own (a white-background JPG, say) gets the same treatment in the browser: its
     white turns transparent, once, in idle time. If the browser may not read the image (no CORS),
     it is shown as it is and blended into light backgrounds instead. */
  const KEYED = new Map();
  function keyed(u) {
    if (KEYED.has(u)) return KEYED.get(u);
    const p = new Promise(res => {
      const go = () => {
        if (!window.fetch || !window.createImageBitmap) { res(null); return; }
        fetch(u, { mode: 'cors', credentials: 'omit' }).then(r => (r.ok ? r.blob() : Promise.reject(new Error('status ' + r.status))))
          .then(b => createImageBitmap(b))
          .then(bm => {
            const c = document.createElement('canvas'); c.width = bm.width; c.height = bm.height;
            const x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(bm, 0, 0);
            const d = x.getImageData(0, 0, c.width, c.height), px = d.data;
            for (let i = 0; i < px.length; i += 4) {
              if (px[i + 3] < 255) continue;                       // already transparent there: keep it
              const al = (255 - Math.min(px[i], px[i + 1], px[i + 2])) / 255;
              if (al < .004) { px[i + 3] = 0; continue; }
              const w = 255 * (1 - al);
              px[i] = (px[i] - w) / al; px[i + 1] = (px[i + 1] - w) / al; px[i + 2] = (px[i + 2] - w) / al; px[i + 3] = al * 255;
            }
            x.putImageData(d, 0, 0);
            c.toBlob(b2 => res(b2 ? URL.createObjectURL(b2) : null), 'image/webp', .9);
          }).catch(e => { report('photo key', e); res(null); });
      };
      if (window.requestIdleCallback) requestIdleCallback(go, { timeout: 1200 }); else setTimeout(go, 30);
    });
    KEYED.set(u, p);
    return p;
  }
  /* a shot: lens | view | mirrored | on a dark stage (over a pool of light) */
  const pk = (lens, view, m, dark) => lens + '|' + view + '|' + (m ? 1 : 0) + '|' + (dark ? 1 : 0);
  const turnKey = (lens, a, dark) => { const t = TURN[a] || TURN[0]; return pk(lens, t[0], t[1], dark); };
  const parsePk = k => { const p = String(k).split('|'); return { lens: LENS[p[0]] ? p[0] : 'dusk', view: VIEWS.indexOf(p[1]) >= 0 ? p[1] : 'front', m: p[2] === '1', dark: p[3] === '1' }; };
  const isDark = k => String(k).slice(-1) === '1';

  /* ---------- a stage: the photos of one slot ---------- */
  function photoStage(slot, opt) {
    opt = opt || {};
    const root = document.createElement('div'); root.className = 'nx-ph'; root.setAttribute('aria-hidden', 'true');
    // shots on a dark stage sit in their own layer, over a soft pool of light
    const gL = document.createElement('div'); gL.className = 'nx-ph-g';
    const gD = document.createElement('div'); gD.className = 'nx-ph-g nx-ph-g--dark';
    // the pool of light fills the slot and fades out before its edges (nothing sticks out of the slot)
    const pool = document.createElement('div'); pool.className = 'nx-ph-pool';
    root.appendChild(pool); root.appendChild(gL); root.appendChild(gD);
    const fb = $(':scope > .nx-fallback', slot) || $('.nx-fallback', slot);
    if (fb && fb.parentNode === slot) slot.insertBefore(root, fb.nextSibling); else slot.insertBefore(root, slot.firstChild);
    if (getComputedStyle(slot).position === 'static') slot.classList.add('nx-gl-host');
    const layers = new Map();
    let geo = '', fill = 0, ver = PH.ver, sm = null, drawn = false, poolO = -1;
    const S = { bw: 0, bh: 0, bx: 0, by: 0 };
    const isSmall = () => S.bw * Math.min(2, window.devicePixelRatio || 1) <= 1000;   // the 960 px copy is enough
    function load(L) {
      const s = photoSrc(L.p.lens, L.p.view, isSmall());
      if (!s.u || s.u === L.src) return;
      const want = s.u, img = L.img, b = s.box;
      // the page's own photo of the same shot is already loaded (whichever size the browser chose): start from it
      const u = !s.key && !L.src && fb && fb.getAttribute('data-nx-k') === L.k && fb.currentSrc ? fb.currentSrc : want;
      L.src = want; L.ready = false; L.own = !!s.own;
      img.style.cssText = b ? 'left:' + (-b[0] / b[2] * 100).toFixed(3) + '%;top:' + (-b[1] / b[3] * 100).toFixed(3) + '%;width:' + (100 / b[2]).toFixed(3) + '%;height:' + (100 / b[3]).toFixed(3) + '%' : '';
      L.el.classList.remove('nx-ph-l--mul');
      img.onload = () => { const done = () => { if (L.src === want) { L.ready = true; kick(); } }; if (img.decode) img.decode().then(done, done); else done(); };
      img.onerror = () => {
        if (L.src !== want) return;
        report('photo', new Error('could not load ' + img.src));
        const t = themePhoto('nocta-photo-' + L.p.lens + '-' + L.p.view + '.webp');
        if (t && img.src.indexOf(t) < 0) { img.style.cssText = ''; img.src = t; } else { L.ready = true; kick(); }   // nothing to show: do not hold the stage
      };
      if (s.key) keyed(u).then(k => { if (L.src !== want) return; if (k) img.src = k; else { L.el.classList.add('nx-ph-l--mul'); img.src = u; } });
      else img.src = u;
    }
    function make(k) {
      const p = parsePk(k), el = document.createElement('div'); el.className = 'nx-ph-l'; el.setAttribute('data-k', k);
      const img = new Image(); img.alt = ''; img.decoding = 'async'; img.draggable = false;
      el.appendChild(img);
      const L = { k, p, el, img, o: -1, z: -1, tf: '', ready: false, src: '' };
      (p.dark ? gD : gL).appendChild(el);
      layers.set(k, L);
      load(L);
      return L;
    }
    const get = k => layers.get(k) || make(k);
    /* the coating's glint: a band of violet light that travels across the two lenses of the front shot,
       masked to them. It sits beside the photo (same transform), so it lightens whatever is under it. */
    let sheen = null, sheenG = null, sheenO = -1, sheenT = '', sheenTf = '';
    function setSheen(L, o, x) {
      if (!sheen && o > 0) {
        sheen = document.createElement('div'); sheen.className = 'nx-ph-sheen';
        sheen.appendChild(document.createElement('i')); sheen.style.zIndex = '60';
        const mk = 'url("' + (PH.conf.mask || themePhoto('nocta-photo-lens-mask.png')) + '")';
        sheen.style.webkitMaskImage = mk; sheen.style.maskImage = mk;
      }
      if (!sheen) return;
      // the lens mask fits the front shot as it comes with the theme (or cut from a sheet laid out like
      // the supplied ones); a single photo of your own can be framed differently, so it shows no glint
      // rather than one in the wrong place
      if (!L || L.own || L.p.view !== 'front' || L.p.m) o = 0;
      if (o > 0) {
        const g = L.el.parentNode; if (sheenG !== g) { g.appendChild(sheen); sheenG = g; }
        if (L.tf !== sheenTf) { sheenTf = L.tf; sheen.style.transform = L.tf; }
      }
      if (Math.abs(o - sheenO) > .002) { sheenO = o; sheen.style.opacity = o.toFixed(3); }
      const t = 'translate3d(' + lerp(-110, 250, clamp(x, 0, 1)).toFixed(1) + '%,0,0) skewX(-14deg)';
      if (t !== sheenT) { sheenT = t; sheen.firstChild.style.transform = t; }
    }
    /* will-change comes off a layer 0.8 s after it last moved (the frame loop may already be asleep) */
    let settleT = 0;
    function settleSoon() {
      clearTimeout(settleT);
      settleT = setTimeout(() => {
        const t = performance.now(); let busy = false;
        layers.forEach(L => { if (!L.wc) return; if (t - (L.mv || 0) >= 700) { L.wc = ''; L.el.style.willChange = ''; } else busy = true; });
        if (busy) settleSoon();
      }, 800);
    }
    return {
      S, root,
      get bw() { return S.bw; },
      ready: k => get(k).ready,
      want(k) { get(k); },
      /* the 2:1 photo box inside the slot: contained (times the slot's --ph-fill), or covering it */
      size(w, h) {
        if (!fill) fill = parseFloat(getComputedStyle(slot).getPropertyValue('--ph-fill')) || 1;
        const g = Math.round(w) + 'x' + Math.round(h);
        if (g === geo) return; geo = g;
        if (opt.cover) { S.bw = Math.max(w, h * 2); } else { S.bw = Math.min(w, h * 2) * fill; }
        S.bh = S.bw / 2; S.bx = (w - S.bw) / 2; S.by = (h - S.bh) / 2;
        const css = 'left:' + S.bx.toFixed(1) + 'px;top:' + S.by.toFixed(1) + 'px;width:' + S.bw.toFixed(1) + 'px;height:' + S.bh.toFixed(1) + 'px';
        gL.style.cssText = css; gD.style.cssText = css;
        const s = isSmall(); if (sm !== null && s !== sm) layers.forEach(load);   // a bigger box gets the sharper file
        sm = s;
      },
      /* list: bottom → top, [{ k, o, x, y, s, sx }] (x, y in px, s a scale, sx a horizontal squeeze);
         sheen: { o, x } on the front shot */
      draw(list, sheenOpt) {
        if (ver !== PH.ver) { ver = PH.ver; layers.forEach(load); }      // photos changed in the editor
        const on = new Set(), now = performance.now();
        let darkO = 0, front = null, frontO = 0, any = false;
        list.forEach((it, z) => {
          const L = get(it.k), o = it.o <= .001 ? 0 : Math.min(1, it.o);
          on.add(L);
          if (L.z !== z) { L.z = z; L.el.style.zIndex = String(z + 1); }
          if (Math.abs(o - L.o) > .001) {
            if (L.o <= 0 && o > 0) L.el.style.visibility = 'visible';
            if (o <= 0) { L.el.style.visibility = ''; L.wc = ''; L.el.style.willChange = ''; }
            L.o = o; L.el.style.opacity = o.toFixed(3); L.mv = now;
          }
          const s = it.s || 1, sx = s * (it.sx || 1), tf = 'translate3d(' + (it.x || 0).toFixed(1) + 'px,' + (it.y || 0).toFixed(1) + 'px,0) scale(' + (L.p.m ? -sx : sx).toFixed(4) + ',' + s.toFixed(4) + ')';
          if (tf !== L.tf) { L.tf = tf; L.el.style.transform = tf; L.mv = now; }
          // promoted to its own layer only while it fades or moves; a photo at rest is plain again
          if (o > 0 && now - (L.mv || 0) < 700 && L.wc !== 'opacity,transform') { L.wc = 'opacity,transform'; L.el.style.willChange = L.wc; settleSoon(); }
          if (L.p.dark) darkO = Math.max(darkO, o);
          if (L.p.view === 'front' && !L.p.m && o > frontO) { front = L; frontO = o; }
          if (o > .5 && L.ready) any = true;
        });
        layers.forEach(L => { if (!on.has(L) && L.o > 0) { L.o = 0; L.el.style.opacity = '0'; L.el.style.visibility = ''; L.wc = ''; L.el.style.willChange = ''; } });
        if (Math.abs(darkO - poolO) > .002) { poolO = darkO; pool.style.opacity = darkO.toFixed(3); }
        setSheen(front, sheenOpt ? (sheenOpt.o || 0) * frontO : 0, sheenOpt ? sheenOpt.x : 0);
        // the photo shown by the page (before the script) fades once a layer is really there
        if (any && !drawn) { drawn = true; slot.classList.add('nx-drawn'); html.classList.add('nx-ph-ready'); }
      },
      /* a point of a shot (fractions of its box) → px in the slot, after the shot's transform */
      at(nx, ny, it) {
        const L = layers.get(it.k), m = L && L.p.m, s = it.s || 1;
        return [S.bx + S.bw / 2 + ((m ? 1 - nx : nx) - .5) * S.bw * s + (it.x || 0), S.by + S.bh / 2 + (ny - .5) * S.bh * s + (it.y || 0)];
      },
      destroy() { clearTimeout(settleT); root.remove(); slot.classList.remove('nx-drawn', 'nx-gl-host'); layers.clear(); }
    };
  }

  /* ---------- crossfades: the new shot fades in on top, the ones under it stay until it has
     landed (so the picture never thins out in between), then they go. A shot that is still fully
     there underneath just lets the ones above it fade away. ---------- */
  function fader() {
    let L = [];                                     // bottom → top: { k, o, out }
    const live = () => { for (let i = L.length - 1; i >= 0; i--) if (!L[i].out) return L[i]; return null; };
    return {
      get top() { const t = live(); return t ? t.k : ''; },
      get settled() { return L.length === 1 && L[0].o >= 1; },
      list() { return L.map(x => ({ k: x.k, o: smooth(clamp(x.o)) })); },
      set(list) { L = list.map(x => ({ k: x.k, o: x.o })); },
      to(k) {
        if (!k) return;
        const t = live(); if (t && t.k === k) return;
        const i = L.findIndex(x => x.k === k);
        if (i >= 0 && L[i].o >= .999) { L[i].out = false; for (let j = i + 1; j < L.length; j++) L[j].out = true; return; }
        if (i >= 0) { const e = L.splice(i, 1)[0]; e.out = false; L.push(e); return; }
        L.push({ k, o: L.length ? 0 : 0 });
      },
      /* → still moving. ready(k): its photo is decoded. cross(a, b): a and b live in different layers of the stage */
      step(dt, dur, ready, cross) {
        if (!L.length) return false;
        const t = live(); let busy = false;
        for (const x of L) if (x.out) { x.o -= dt / dur; busy = true; }
        L = L.filter(x => !x.out || x.o > 0);
        if (!t) return busy;
        if (t.o < 1) {
          if (!ready(t.k)) return true;                 // still loading: what is under it stays
          t.o = Math.min(1, t.o + dt / dur); busy = true;
        }
        if (cross) for (const x of L) if (x !== t && !x.out && cross(x.k, t.k)) x.o = Math.min(x.o, 1 - t.o);
        if (t.o >= 1 && !L.some(x => x.out)) L = [t];
        return busy || L.length > 1;
      }
    };
  }
  const crossDark = (a, b) => isDark(a) !== isDark(b);

  /* a change of angle reads as a turn: the angle that is leaving narrows a little while the next one
     fades in over it, widening as it arrives (a lens change, same angle, is a plain crossfade) */
  function squeeze(out) {
    if (out.length < 2 || reduce) return out;
    const top = out[out.length - 1], tp = parsePk(top.k);
    out.forEach(x => {
      if (x === top) return;
      const q = parsePk(x.k); if (q.view === tp.view && q.m === tp.m) return;
      x.sx = 1 - .07 * top.o; top.sx = .93 + .07 * top.o;
    });
    return out;
  }
  const turnLook = (list, y) => squeeze(list.map(x => ({ k: x.k, o: x.o, y })));

  /* ---------- a turn: drag it (mouse, pen, or a finger on a tablet), or let it turn on its own.
     A drag scrubs between neighbouring angles; on release it settles on the nearest one, carrying
     the flick's momentum to the next. ---------- */
  function turner(el, bag, opt) {
    opt = opt || {};
    const fd = fader();
    const S = { a: 0, pos: 0, lens: '', drag: null, release: null, hold: 0, si: 0, next: 0, step: 110 };
    const yOf = f => (typeof opt.y === 'function' ? opt.y(f) : opt.y || 0);
    const rubber = (p, lo, hi) => (p < lo ? lo - (lo - p) * .35 / (1 + (lo - p) * .35) : p > hi ? hi + (p - hi) * .35 / (1 + (p - hi) * .35) : p);
    if (el && opt.drag) {
      $$('img', el).forEach(i => { i.draggable = false; });
      bag.on(el, 'dragstart', e => e.preventDefault());
      bag.on(el, 'pointerdown', e => {
        if (e.button > 0 || (opt.enabled && !opt.enabled())) return;
        if (e.pointerType === 'mouse') e.preventDefault();
        S.drag = { x: e.clientX, a0: S.a, id: e.pointerId, v: 0, lx: e.clientX, lt: performance.now() };
        el.classList.add('is-dragging');
        try { el.setPointerCapture(e.pointerId); } catch (_) { /* old browsers */ }
      });
      bag.on(el, 'pointermove', e => {
        const d = S.drag; if (!d || e.pointerId !== d.id) return;
        e.preventDefault();
        const now = performance.now(), dx = e.clientX - d.lx;
        d.v += ((-dx / S.step) / Math.max(8, now - d.lt) * 1000 - d.v) * .5;     // angles per second
        d.lx = e.clientX; d.lt = now;
      });
      const end = e => {
        const d = S.drag; if (!d || e.pointerId !== d.id) return;
        S.drag = null; el.classList.remove('is-dragging');
        if (performance.now() - d.lt > 90) d.v = 0;          // held still before letting go: no flick
        S.release = d; kick();
      };
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(t => bag.on(el, t, end));
    }
    return {
      fd,
      get dragging() { return !!S.drag; },
      get a() { return S.a; },
      /* turn to an angle (hero: toward the mouse) */
      go(a) { a = clamp(Math.round(a), -2, 2); if (a === S.a && fd.top === turnKey(S.lens, a)) return; S.a = a; S.hold = performance.now() + 1500; if (S.lens) fd.to(turnKey(S.lens, a)); },
      /* → true while it moves; schedules its own next auto step */
      frame(f, st, lens) {
        const now = f.now, dur = reduce ? .12 : (opt.dur || .42);
        S.step = clamp(st.bw * .2, 70, 150);
        if (lens !== S.lens) {
          S.lens = lens;
          for (const a in TURN) st.want(turnKey(lens, +a));      // the five angles of this lens start loading
          if (!S.drag) fd.to(turnKey(lens, S.a));
        }
        if (S.drag) {
          const d = S.drag, pos = rubber(d.a0 - (d.lx - d.x) / S.step, -2, 2);
          const i = clamp(Math.floor(pos), -2, 2), t = clamp(pos - i);
          const list = [{ k: turnKey(lens, i), o: 1 }];
          if (t > .001 && i < 2) list.push({ k: turnKey(lens, i + 1), o: t });
          if (pos < -2) list[0].x = (pos + 2) * 40; else if (pos > 2) list[0].x = (pos - 2) * 40;   // past the last angle: it gives a little, then stops
          S.pos = pos; fd.set(list); S.hold = now + (opt.holdMs || 6000);
          const y0 = yOf(f); st.draw(list.map(x => Object.assign({ y: y0 }, x)), opt.sheen && opt.sheen(S, f));
          return true;
        }
        if (S.release) {
          const d = S.release; S.release = null;
          // where the flick was heading: a short flick turns one angle, a longer drag as far as it went
          const n = Math.max(1, Math.abs(Math.round(S.pos) - d.a0));
          S.a = clamp(clamp(Math.round(S.pos + d.v * .12), d.a0 - n, d.a0 + n), -2, 2);
          fd.to(turnKey(lens, S.a));
          const i = TURN_SEQ.indexOf(S.a); if (i >= 0) S.si = i;
        }
        // on its own: the next angle every few seconds while it is on screen (a timer wakes the loop; no frames in between)
        const auto = opt.auto && !reduce && (!opt.autoOn || opt.autoOn());
        if (auto) {
          const dwell = (typeof opt.dwell === 'function' ? opt.dwell() : opt.dwell) || 2600;
          if (now < S.hold) S.next = Math.max(S.next, S.hold);
          if (!S.next) S.next = now + dwell;
          if (now >= S.next && fd.settled) {
            const n = (S.si + 1) % TURN_SEQ.length, k = turnKey(lens, TURN_SEQ[n]);
            if (st.ready(k)) { S.si = n; S.a = TURN_SEQ[n]; fd.to(k); S.next = now + dwell + dur * 1000; }
            else S.next = now + 300;
          }
          wakeAt(S.next);
        } else S.next = 0;
        const busy = fd.step(f.dt, dur, k => st.ready(k), crossDark);
        st.draw(turnLook(fd.list(), yOf(f)), opt.sheen && opt.sheen(S, f));
        return busy;
      }
    };
  }

  /* ============================================================
     THE TURNING FRAME: the gallery's 3D slide. 28 real frames of the
     STILL TR90 turning from the front to the side (cut from the
     product film), drawn mirrored for the other side, so it turns
     through ±78°. 15.0: it turns on its own, always, at an even pace
     with gentle turns at the ends, and every moment shows exactly one
     real frame: no blending between frames, so never a double rim.
     Nothing to drag (every swipe moves the gallery or the page).
     In the frames the lenses are clear glass; the lens colour is
     multiplied back in through the same frame's lens mask, so Oslo,
     Lisbon and Havana look like real tinted lenses; a lens change is a
     soft colour change. Reduced motion: it rests facing you.
     ============================================================ */
  const SPIN = { n: 28, amp: 78, period: 12, fill: .9 };
  /* the lens colours, multiplied into the clear lenses: [r, g, b], strength */
  const SPIN_TINT = { day: [[214, 230, 250], .5], play: [[252, 206, 48], .92], dusk: [[255, 146, 50], .95] };
  function spinner(slot, bag, opt) {
    opt = opt || {};
    const base = slot.getAttribute('data-nx-spin') || '';
    if (!base) return null;
    const cv = document.createElement('canvas'); cv.className = 'nx-spin-cv'; cv.setAttribute('aria-hidden', 'true');
    slot.appendChild(cv); bag.add(() => cv.remove());
    const ctx = cv.getContext('2d');
    const tmp = document.createElement('canvas'), tx = tmp.getContext('2d');
    const name = (k, small) => base.replace(/nocta-turn-00(\.webp)/, k + (small ? '-s' : '') + '$1');
    const S = { a: 0, phase: .25, speed: 0, w: 0, h: 0, small: null, col: null, key: '', shown: false };
    const imgs = [], mask = { el: null, ok: false };
    let started = false, failed = 0;
    function load(small) {
      if (S.small === small && started) return;
      S.small = small; started = true;
      const order = [0]; for (let k = 1; k < SPIN.n; k++) order.push(k);       // the front first: it is what shows
      order.forEach((k, j) => {
        const im = new Image(); im.decoding = 'async';
        const it = imgs[k] = imgs[k] && imgs[k].small === small ? imgs[k] : { el: im, ok: false, small };
        if (it.el !== im) return;
        im.onload = () => { const done = () => { it.ok = true; kick(); }; if (im.decode) im.decode().then(done, done); else done(); };
        im.onerror = () => { failed++; };
        const go = () => { im.src = name('nocta-turn-' + pad(k), small); };
        if (j === 0) go(); else setTimeout(go, 60 + j * 25);                      // the rest a moment later, gently
      });
      const m = new Image(); m.decoding = 'async';
      m.onload = () => { mask.ok = true; kick(); }; m.src = name('nocta-turn-mask', small); mask.el = m;
    }
    // frame k on the mask sheet (4 × 7)
    const mbox = k => { const w = mask.el.naturalWidth / 4, h = mask.el.naturalHeight / 7; return [(k % 4) * w, Math.floor(k / 4) * h, w, h]; };
    /* the nearest frames that are ready, so a half-loaded turn still shows something sensible */
    function ready(k) { if (imgs[k] && imgs[k].ok) return k; for (let d = 1; d < SPIN.n; d++) { if (imgs[k - d] && imgs[k - d].ok) return k - d; if (imgs[k + d] && imgs[k + d].ok) return k + d; } return -1; }
    function draw() {
      const W = cv.width, H = cv.height; if (!W || !H) return false;
      // the nearest real frame, never a mix of two (a mix shows the rim twice)
      const want = Math.round(Math.min(1, Math.abs(S.a) / 90) * (SPIN.n - 1));
      const i0 = ready(want); if (i0 < 0) return false;
      const F = imgs[i0].el, fw = F.naturalWidth, fh = F.naturalHeight;
      const dw = Math.min(W * SPIN.fill, H * 1.9 * fw / fh * SPIN.fill / 1.9), dh = dw * fh / fw, dx = (W - dw) / 2, dy = (H - dh) / 2 + H * .03;
      ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = 1; ctx.clearRect(0, 0, W, H);
      const flip = S.a < 0;
      if (flip) ctx.setTransform(-1, 0, 0, 1, W, 0);
      ctx.drawImage(F, dx, dy, dw, dh);
      if (mask.ok && S.col) {
        if (tmp.width !== W || tmp.height !== H) { tmp.width = W; tmp.height = H; }
        tx.setTransform(1, 0, 0, 1, 0, 0); tx.globalCompositeOperation = 'source-over'; tx.globalAlpha = 1; tx.clearRect(0, 0, W, H);
        if (flip) tx.setTransform(-1, 0, 0, 1, W, 0);
        const b0 = mbox(i0); tx.drawImage(mask.el, b0[0], b0[1], b0[2], b0[3], dx, dy, dw, dh);
        tx.setTransform(1, 0, 0, 1, 0, 0);
        // a real tinted lens is not one flat colour: a touch deeper at the top, lighter toward the bottom
        const c = S.col, mix = (k, m) => 'rgb(' + [0, 1, 2].map(i => Math.round(m ? c[i] + (255 - c[i]) * k : c[i] * k)).join(',') + ')';
        const gr = tx.createLinearGradient(0, dy, 0, dy + dh);
        gr.addColorStop(0, mix(.9)); gr.addColorStop(.5, mix(0, 1)); gr.addColorStop(1, mix(.22, 1));
        tx.globalCompositeOperation = 'source-in'; tx.globalAlpha = c[3]; tx.fillStyle = gr; tx.fillRect(0, 0, W, H);
        ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalCompositeOperation = 'multiply'; ctx.globalAlpha = 1; ctx.drawImage(tmp, 0, 0);
        // and the glass catches the light: a soft diagonal sheen, only on the lenses (the mask again)
        tx.globalCompositeOperation = 'copy'; tx.globalAlpha = 1;
        if (flip) tx.setTransform(-1, 0, 0, 1, W, 0);
        tx.drawImage(mask.el, b0[0], b0[1], b0[2], b0[3], dx, dy, dw, dh);
        tx.setTransform(1, 0, 0, 1, 0, 0);
        const sh = tx.createLinearGradient(dx, dy, dx + dw * .55, dy + dh);
        sh.addColorStop(0, 'rgba(255,255,255,0)'); sh.addColorStop(.32, 'rgba(255,255,255,.34)'); sh.addColorStop(.42, 'rgba(255,255,255,.06)'); sh.addColorStop(.6, 'rgba(255,255,255,0)');
        tx.globalCompositeOperation = 'source-in'; tx.fillStyle = sh; tx.fillRect(0, 0, W, H);
        ctx.globalCompositeOperation = 'screen'; ctx.globalAlpha = c[3] * .9; ctx.drawImage(tmp, 0, 0);
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = 1;
      if (!S.shown) { S.shown = true; cv.classList.add('is-on'); slot.classList.add('nx-spin-on'); }
      return true;
    }
    bag.on(slot, 'dragstart', e => e.preventDefault());
    return {
      get dragging() { return false; },
      /* the gallery is near but this slide is not showing yet: fetch the front frame and the mask */
      prep(w) { const dpr = Math.min(2, window.devicePixelRatio || 1); load(w * dpr * SPIN.fill <= 700); },
      /* w, h: the slide's box; lenses: the lens keys to show (one, a pair or the set: they take turns) */
      frame(f, w, h, lenses) {
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const W = Math.round(w * dpr), H = Math.round(h * dpr);
        let busy = false;
        if (W !== cv.width || H !== cv.height) { cv.width = W; cv.height = H; S.key = ''; }
        load(W * SPIN.fill <= 700);
        // the lens colour: a pair or the set changes lens every 2.6 s, always blended
        const L = lenses && lenses.length ? lenses : ['dusk'];
        const lk = L.length > 1 ? L[Math.floor(f.now / 2600) % L.length] : L[0];
        const t = SPIN_TINT[lk] || SPIN_TINT.dusk, want = [t[0][0], t[0][1], t[0][2], t[1]];
        if (L.length > 1 && !reduce) wakeAt((Math.floor(f.now / 2600) + 1) * 2600 + 5);
        if (!S.col || reduce) S.col = want.slice();
        else {
          let d = 0; const k = 1 - Math.pow(.0008, f.dt);              // about half a second to settle
          for (let i = 0; i < 4; i++) { const v = S.col[i] + (want[i] - S.col[i]) * k; d += Math.abs(want[i] - v) * (i === 3 ? 255 : 1); S.col[i] = v; }
          if (d > .6) busy = true; else S.col = want.slice();
        }
        // the sway: an even pace across the middle and gentle turns at the ends (a triangle wave with
        // rounded corners), so the frames step by at a steady rhythm; it starts softly
        const auto = !reduce && opt.auto !== false;
        if (auto) {
          S.speed = Math.min(1, S.speed + f.dt / 1.2);
          S.phase = (S.phase + f.dt * S.speed * S.speed * (3 - 2 * S.speed) / SPIN.period) % 1;
          const tri = 1 - 4 * Math.abs(S.phase - .5);                     // -1 … 1 … -1 over one period
          const a = SPIN.amp * Math.sin(tri * Math.PI / 2) * .35 + SPIN.amp * tri * .65;
          if (Math.abs(a - S.a) > .01) S.a = a;
        } else { S.speed = 0; S.a = 0; }
        const key = S.a.toFixed(2) + '|' + (S.col ? S.col.map(v => v.toFixed(1)).join(',') : '') + '|' + imgs.filter(i => i && i.ok).length + '|' + mask.ok;
        if (key !== S.key) { if (draw()) S.key = key; }
        return busy ? true : auto ? 'spin' : '';
      }
    };
  }
  /* ============================================================
     CORE: section registry, one frame loop, editor events
     ============================================================ */
  const MODULES = {};
  const insts = new Set();
  const ctas = new Set();                  // buy buttons on screen hide the sticky bar
  const P = { x: 0, y: 0, tx: 0, ty: 0, active: false };   // mouse position, -1…1
  let uid = 0, running = false, last = 0, topInset = 0;
  /* viewport size, read once per resize instead of every frame (cw: without the scrollbar) */
  const VP = { w: innerWidth, h: innerHeight, cw: html.clientWidth || innerWidth };
  /* the loop runs flat out only while something moves. After the last scroll, touch, key or
     mouse move, gentle idle motion (the frame's float, the lens peeks) runs at 30 fps for a
     while, then everything stops until the visitor does something again. Photo turns that run
     on their own wake the loop with a timer for each step and let it sleep in between. */
  const HOT_MS = 1200, AMBIENT_MS = finePtr ? 20000 : 8000;
  let lastInput = performance.now(), clock = 0, lastPaint = 0, lastWant = '', lastScroll = -1e9;
  function wake() { lastInput = performance.now(); kick(); }
  /* phone layout (768 px and narrower): the frame turns on its own instead of following a finger */
  const liteQ = mq('(max-width: 768px)');

  /* a timer that wakes the frame loop at a given time (an auto-turn's next angle): the loop
     sleeps in between instead of running frames that change nothing */
  let wakeT = 0, wakeTimer = 0;
  function wakeAt(t) {
    if (!t || (wakeTimer && wakeT <= t)) return;
    clearTimeout(wakeTimer); wakeT = t;
    wakeTimer = setTimeout(() => { wakeTimer = 0; wakeT = 0; kick(); }, Math.max(16, t - performance.now()));
  }

  /* listeners, timers and observers a section owns; all released on unmount */
  function makeBag() {
    const offs = [];
    return {
      on(el, type, fn, opt) { if (!el) return fn; el.addEventListener(type, fn, opt); offs.push(() => el.removeEventListener(type, fn, opt)); return fn; },
      later(fn, ms) {
        const off = () => clearTimeout(id);
        const id = setTimeout(() => { const i = offs.indexOf(off); if (i >= 0) offs.splice(i, 1); fn(); }, ms);
        offs.push(off); return id;
      },
      every(fn, ms) { const id = setInterval(fn, ms); offs.push(() => clearInterval(id)); return id; },
      watch(obs) { offs.push(() => obs.disconnect()); return obs; },
      add(fn) { offs.push(fn); },
      clear() { while (offs.length) { try { offs.pop()(); } catch (e) { report('cleanup', e); } } }
    };
  }
  /* fixed overlays (sticky bar, sheet, pop-ups) move to <body>, out of any transformed theme wrapper */
  function portal(bag, owner, nodes) {
    const p = document.createElement('div');
    p.className = 'nx nx-portal'; p.setAttribute('data-nx-portal', owner);
    nodes.forEach(n => { if (n) p.appendChild(n); });
    document.body.appendChild(p);
    bag.add(() => p.remove());
    return p;
  }
  function readConfig(root) {
    const s = $('script[data-nx-config]', root);
    if (!s) return {};
    try { return JSON.parse(s.textContent) || {}; } catch (e) { report('settings of ' + root.getAttribute('data-nocta'), e); return {}; }
  }

  function mount(root) {
    if (!root || root.__nx) return;
    const name = root.getAttribute('data-nocta'), mod = MODULES[name];
    if (!mod) return;
    const inst = { name, root, bag: makeBag(), cfg: readConfig(root), id: (root.id || name) + '#' + (++uid), par: [] };
    root.__nx = inst;
    try { Object.assign(inst, mod(root, inst.cfg, inst.bag, inst) || {}); } catch (e) { report(name + ' mount', e); }
    try { common(root, inst); } catch (e) { report(name + ' effects', e); }
    inst.near = true;                           // until the observer reports otherwise
    if (nearIO) nearIO.observe(root);
    insts.add(inst);
    kick();
  }
  function unmount(root) {
    const inst = root && root.__nx;
    if (!inst) return;
    root.classList.remove('nx-live');
    try { if (inst.destroy) inst.destroy(); } catch (e) { report(inst.name + ' unmount', e); }
    inst.bag.clear();
    if (nearIO) nearIO.unobserve(root);
    insts.delete(inst);
    root.__nx = null;
  }
  /* only sections within about half a screen of view take part in the frame loop */
  const nearIO = 'IntersectionObserver' in window ? new IntersectionObserver(es => {
    es.forEach(e => { const i = e.target.__nx; if (i) { i.near = e.isIntersecting; e.target.classList.toggle('nx-live', i.near); if (i.near) kick(); } });
  }, { rootMargin: '60% 0px 60% 0px' }) : null;
  const within = (scope, fn) => {
    if (!scope || !scope.querySelectorAll) return;
    if (scope.matches && scope.matches('[data-nocta]')) fn(scope);
    $$('[data-nocta]', scope).forEach(fn);
  };
  function mountAll(scope) { within(scope || document, mount); }
  function ownerOf(el) { const r = el && el.closest && el.closest('[data-nocta]'); return r && r.__nx; }

  document.addEventListener('shopify:section:load', e => mountAll(e.target));
  document.addEventListener('shopify:section:unload', e => within(e.target, unmount));
  document.addEventListener('shopify:block:select', e => { const i = ownerOf(e.target); if (i && i.select) try { i.select(e.target, e); } catch (err) { report('block select', err); } });
  document.addEventListener('shopify:block:deselect', e => { const i = ownerOf(e.target); if (i && i.deselect) try { i.deselect(e.target, e); } catch (err) { report('block deselect', err); } });
  document.addEventListener('shopify:section:select', e => within(e.target, r => { const i = r.__nx; if (i && i.sectionSelect) i.sectionSelect(); }));
  document.addEventListener('shopify:section:deselect', e => within(e.target, r => { const i = r.__nx; if (i && i.sectionDeselect) i.sectionDeselect(); }));
  window.addEventListener('pointermove', e => {
    wake();
    if (e.pointerType !== 'mouse' || reduce) return;
    P.tx = (e.clientX / VP.w - .5) * 2; P.ty = (e.clientY / VP.h - .5) * 2; P.active = true;
  }, { passive: true });
  document.addEventListener('mouseleave', () => { P.tx = P.ty = 0; P.active = false; });
  // capture phase: a control that stops its event (the tour's arrow keys) still wakes the loop.
  // Listeners only note the time and ask for a frame; all reading and writing happens once per frame.
  ['wheel', 'touchstart', 'touchmove', 'pointerdown', 'keydown'].forEach(t => window.addEventListener(t, wake, { passive: true, capture: true }));
  window.addEventListener('scroll', () => { lastScroll = performance.now(); wake(); }, { passive: true, capture: true });
  ['shopify:block:select', 'shopify:block:deselect', 'shopify:section:select', 'shopify:section:load'].forEach(t => document.addEventListener(t, wake));
  // reduced motion switched on while the page is open: the mouse tilt lets go at once
  try { reduceQ.addEventListener('change', () => { if (reduce) { P.tx = P.ty = P.x = P.y = 0; P.active = false; } wake(); }); } catch (e) { /* old Safari */ }
  window.addEventListener('resize', () => { VP.w = innerWidth; VP.h = innerHeight; VP.cw = html.clientWidth || innerWidth; safeAt = -1; wake(); }, { passive: true });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) wake(); });

  /* what covers the top of the screen: the theme's sticky header (wherever it sticks: below a
     notch, an app bar or an announcement bar), or at least the safe area. The tour, the amber
     wash and in-page jumps leave room for it. */
  const HDR = '[data-nx-header],.shopify-section-header-sticky,.section-header,sticky-header,#shopify-section-header,.site-header,header';
  let hdr = null, hdrAt = -1e9, safeTop = 0, safeAt = -1;
  function headerInset(now) {
    if (safeAt < 0) {                               // env(safe-area-inset-top), read once per resize
      safeAt = now;
      const p = document.createElement('div'); p.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;padding-top:env(safe-area-inset-top,0px)';
      document.body.appendChild(p); safeTop = parseFloat(getComputedStyle(p).paddingTop) || 0; p.remove();
    }
    if (now - hdrAt > 1500) {
      hdrAt = now; hdr = null;
      const list = $$(HDR).slice(0, 14);
      for (const el of list) {
        if (el.closest('.nx')) continue;
        const pos = getComputedStyle(el).position;
        if (pos === 'sticky' || pos === 'fixed') { hdr = el; break; }
      }
    }
    let t = safeTop;
    if (hdr) {
      const r = hdr.getBoundingClientRect();
      if (r.height > 0 && r.bottom > 0 && r.top < VP.h * .25) t = Math.max(t, r.bottom);   // a header stuck near the top
    }
    return Math.round(clamp(t, 0, VP.h * .3));
  }
  let topSeen = 0, topStable = 0;

  function kick() { if (!running && insts.size && !document.hidden) { running = true; last = performance.now(); requestAnimationFrame(loop); } }
  function loop(now) {
    running = false;
    if (!insts.size || document.hidden) return;
    const since = now - lastInput;
    // idle motion runs at 30 fps (every other display frame); a transition still in progress keeps full rate,
    // and so does a glide (15.0: the tech sweep and the drag demo's own glide move far, so they stay smooth)
    if (since > HOT_MS && lastWant !== 'hot' && lastWant !== 'glide' && now - lastPaint < 30) { running = true; requestAnimationFrame(loop); return; }
    let want = '';
    try { want = frame(now); } catch (e) { report('frame', e); want = 'hot'; }
    // safety net: nothing the visitor started lasts this long, so the loop always ends up asleep
    if (want === 'hot' && since > AMBIENT_MS + 5000) want = '';
    lastPaint = now; lastWant = want;
    if (want === 'hot' || want === 'glide' || want === 'spin' || since < HOT_MS || (want === 'ambient' && since < AMBIENT_MS)) { running = true; requestAnimationFrame(loop); }
  }
  const act = [];
  function frame(now) {
    const dt = Math.min(.05, (now - last) / 1000); last = now;
    // idle motion follows this clock, so it resumes where it stopped. On touch screens it holds
    // still while the page is scrolling.
    const held = !finePtr && now - lastScroll < 160;
    if (!held) clock += dt;
    let hot = false, amb = false, spin = false, glide = false;
    if (Math.abs(P.tx - P.x) + Math.abs(P.ty - P.y) > .002) { P.x += (P.tx - P.x) * .06; P.y += (P.ty - P.y) * .06; hot = true; }
    const top = headerInset(now);
    // lite: phone layout, where nothing follows the scroll position and the frame turns on its own
    const f = { now, dt, adt: held || reduce ? 0 : dt, time: clock, vh: VP.h, vw: VP.w, cw: VP.cw, top, lite: liteQ.matches, held };
    act.length = 0; insts.forEach(i => { if (i.near || i.always) act.push(i); });
    // 1. read: every section measures what it needs before anything on the page is written
    for (const i of act) {
      if (i.measure) { try { i.measure(f); } catch (e) { report(i.name + ' measure', e); } }
      if (i.par.length) parMeasure(i, f);
    }
    // 2. write
    if (top !== topSeen) { topSeen = top; topStable = 0; } else if (topStable < 3) topStable++;
    if (topStable === 2 && top !== topInset) { topInset = top; html.style.setProperty('--nx-top', top + 'px'); }   // only once the header has settled
    for (const i of act) {
      if (i.frame) { try { const r = i.frame(f); if (r === true) hot = true; else if (r === 'ambient') amb = true; else if (r === 'spin') spin = true; else if (r === 'glide') glide = true; } catch (e) { report(i.name + ' frame', e); } }
      if (i.par.length) parallax(i, f);
    }
    return hot ? 'hot' : glide && !reduce ? 'glide' : spin && !reduce ? 'spin' : amb && !reduce ? 'ambient' : '';
  }

  /* ============================================================
     shared effects: reveals, word-by-word headings, magnets,
     parallax, count-ups, FAQ accordions
     ============================================================ */
  function common(root, inst) {
    const bag = inst.bag, edit = designMode();
    $$('[data-stagger]', root).forEach(g => Array.prototype.forEach.call(g.children, (c, i) => { c.classList.add('nx-rv'); c.style.setProperty('--i', Math.min(i, 8)); }));
    // headings rise word by word
    $$('.nx-h2.nx-rv', root).forEach(h => {
      if (h.__split) return; h.__split = true;
      let i = 0;
      const walk = n => Array.prototype.slice.call(n.childNodes).forEach(c => {
        if (c.nodeType === 3) {
          const frag = document.createDocumentFragment();
          c.textContent.split(/(\s+)/).forEach(tk => {
            if (!tk) return;
            if (/^\s+$/.test(tk)) frag.appendChild(document.createTextNode(tk));
            else { const s = document.createElement('span'); s.className = 'nx-w'; s.style.setProperty('--wi', i++); s.textContent = tk; frag.appendChild(s); }
          });
          c.parentNode.replaceChild(frag, c);
        } else if (c.nodeType === 1) walk(c);
      });
      walk(h);
    });
    // numbers read in the shop's locale (1,284 or 1 284)
    const nf0 = numberFormat();
    $$('[data-count]', root).forEach(el => { el.textContent = nf0(+el.getAttribute('data-count') || 0); });
    $$('[data-num]', root).forEach(el => { el.textContent = nf0(+el.getAttribute('data-num') || 0); });   // same format, no count-up
    // reveals: content is visible at rest; below the fold it starts faint and rises in
    if (!edit && !reduce && 'IntersectionObserver' in window) {
      const io = bag.watch(new IntersectionObserver(es => es.forEach(en => {
        if (!en.isIntersecting) return;
        const el = en.target; io.unobserve(el);
        el.classList.add('is-rising'); el.classList.remove('is-pre');     // the staggered fade wins over a card's own transition
        bag.later(() => el.classList.remove('is-rising'), 1700);
      }), { rootMargin: '0px 0px -8% 0px' }));
      // (cards waiting off to the side of a swipe row simply show: they would stay faint until swiped to)
      requestAnimationFrame(() => $$('.nx-rv', root).forEach(el => { const r = el.getBoundingClientRect(); if (r.top > innerHeight * .92 && r.left < innerWidth && r.right > 0) { el.classList.add('is-pre'); io.observe(el); } }));
      // numbers count up once they come into view
      const cio = bag.watch(new IntersectionObserver(es => es.forEach(en => {
        if (!en.isIntersecting) return; cio.unobserve(en.target);
        const el = en.target, to = +el.getAttribute('data-count'), t0 = performance.now(), nf = numberFormat();
        // 11.0: the number keeps the width of its final value while it counts, so the words after
        // it never re-wrap (no layout shift, and nothing for ScrollTrigger to re-measure mid-scroll)
        const w = el.getBoundingClientRect().width;
        Object.assign(el.style, { display: 'inline-block', minWidth: Math.ceil(w) + 'px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' });
        const step = now => { const t = clamp((now - t0) / 1400); el.textContent = nf(Math.round(easeOut(t) * to)); if (t < 1 && el.isConnected) requestAnimationFrame(step); };
        requestAnimationFrame(step);
      }), { threshold: .6 }));
      $$('[data-count]', root).forEach(el => cio.observe(el));
    }
    // magnetic buttons (mouse only)
    if (finePtr && !reduce) {
      $$('.nx-magnet', root).forEach(b => {
        const pull = +(b.getAttribute('data-pull') || .16);
        let r = null;
        bag.on(b, 'pointerenter', () => { b.style.transform = ''; r = b.getBoundingClientRect(); b.style.willChange = 'transform'; });
        bag.on(b, 'pointermove', e => { if (!r) r = b.getBoundingClientRect(); b.style.transform = 'translate(' + ((e.clientX - r.left - r.width / 2) * pull * (r.width > 300 ? .35 : 1)).toFixed(1) + 'px,' + ((e.clientY - r.top - r.height / 2) * pull * 1.6).toFixed(1) + 'px) scale(1.015)'; });
        bag.on(b, 'pointerleave', () => { b.style.transform = ''; b.style.willChange = ''; r = null; });
        bag.on(b, 'pointerdown', () => { b.style.transform = ''; });           // the pressed state (scale .97) shows at once
      });
    }
    // parallax layers
    inst.par = $$('[data-speed]', root).map(el => ({ el, speed: +el.getAttribute('data-speed') || 0, y0: 0, h: 0, ty: 0, H: 0 }));
    // accordions
    $$('.nx-fq-q', root).forEach(q => { toggleFaq(q, q.getAttribute('aria-expanded') === 'true'); bag.on(q, 'click', () => toggleFaq(q)); });
  }
  function numberFormat() {
    let f = null; try { f = new Intl.NumberFormat(locale()); } catch (e) { f = null; }
    return n => (f ? f.format(n) : String(n));
  }
  function toggleFaq(q, open) {
    const on = open == null ? q.getAttribute('aria-expanded') !== 'true' : open;
    q.setAttribute('aria-expanded', on ? 'true' : 'false');
    const a = q.nextElementSibling; if (a) { a.setAttribute('aria-hidden', on ? 'false' : 'true'); a.inert = !on; }
  }
  function parMeasure(inst, f) {
    const par = inst.par;
    if (reduce || f.vw <= 860) return;
    const docH = pageH(), sy = pageY();
    if (par[0].H !== docH) par.forEach(o => { const r = o.el.getBoundingClientRect(); o.y0 = r.top + sy - o.ty; o.h = r.height; o.H = docH; });
    inst.sy = sy;
  }
  function parallax(inst, f) {
    const par = inst.par;
    if (reduce || f.vw <= 860) { par.forEach(o => { if (o.ty) { o.ty = 0; o.el.style.transform = ''; } }); return; }
    const sy = inst.sy;
    for (const o of par) {
      const top = o.y0 - sy; if (top > f.vh + 200 || top + o.h < -200) continue;
      const ty = -((top + o.h / 2) - f.vh / 2) * o.speed;
      if (Math.abs(ty - o.ty) < .1) continue;
      o.ty = ty; o.el.style.transform = 'translate3d(0,' + ty.toFixed(1) + 'px,0)';
    }
  }
  const onScreen = (el, top) => { if (!el || !el.isConnected) return false; const r = el.getBoundingClientRect(); return r.width > 0 && r.bottom > (top || 0) && r.top < innerHeight; };

  /* a scroll listener that does its work at most once per frame */
  function rafOnce(fn) { let q = false; return () => { if (q) return; q = true; requestAnimationFrame(() => { q = false; fn(); }); }; }
  /* dispatch cut-off: counts down to a real time in the shop's time zone,
     then names the next dispatch day; optionally adds an arrival window */
  const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  function readCut(el) { try { return JSON.parse(el.getAttribute('data-nx-cutoff') || '{}'); } catch (e) { report('cut-off settings', e); return {}; } }
  function shipPlanner(c) {
    const days = (Array.isArray(c.days) && c.days.length ? c.days : [1, 2, 3, 4, 5]).map(Number);
    const cut = (+c.hour || 0) * 3600 + (+c.minute || 0) * 60;
    const off = (() => { const m = /^([+-])(\d\d):?(\d\d)$/.exec(c.offset || ''); return m ? (m[1] === '-' ? -1 : 1) * (+m[2] * 60 + +m[3]) : null; })();
    let fmt = null;
    try { if (c.tz) fmt = new Intl.DateTimeFormat('en-GB', { timeZone: c.tz, hour12: false, year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }); } catch (e) { fmt = null; }
    /* the date and time at the warehouse, whatever the visitor's own time zone */
    const parts = d => {
      if (fmt) { const p = {}; fmt.formatToParts(d).forEach(x => { p[x.type] = x.value; }); return { wd: WD.indexOf(p.weekday), h: +p.hour % 24, m: +p.minute, s: +p.second, y: +p.year, mo: +p.month, d: +p.day }; }
      if (off == null) return null;
      const x = new Date(d.getTime() + off * 6e4);
      return { wd: x.getUTCDay(), h: x.getUTCHours(), m: x.getUTCMinutes(), s: x.getUTCSeconds(), y: x.getUTCFullYear(), mo: x.getUTCMonth() + 1, d: x.getUTCDate() };
    };
    // calendar maths on the warehouse's own date (noon UTC stands in for that day; shown with timeZone UTC)
    // 18.0: public holidays and bridge days ("Days without dispatch", YYYY-MM-DD): no dispatch, and they
    // do not count as a delivery day either
    const off18 = new Set(String(c.holidays || '').split(/[\s,;]+/).filter(x => /^\d{4}-\d\d-\d\d$/.test(x)));
    const key = x => x.toISOString().slice(0, 10), isOff = x => off18.has(key(x));
    const addBiz = (d, n) => { const x = new Date(d); while (n > 0) { x.setUTCDate(x.getUTCDate() + 1); const w = x.getUTCDay(); if (w !== 0 && w !== 6 && !isOff(x)) n--; } return x; };
    /* → { p, before, left (seconds), k (days until it ships), from, to (arrival window) } */
    return function plan(ms) {
      const p = parts(new Date(ms)); if (!p || p.wd < 0) return null;
      const secs = p.h * 3600 + p.m * 60 + p.s, dayAt = n => new Date(Date.UTC(p.y, p.mo - 1, p.d + n, 12));
      const ships = n => days.indexOf((p.wd + n) % 7) >= 0 && !isOff(dayAt(n));
      const before = ships(0) && secs < cut;
      let k = 0; if (!before) { k = 1; while (k < 15 && !ships(k)) k++; }
      const ship = dayAt(k);
      const tmin = Math.max(0, +c.transitMin || 0), tmax = Math.max(tmin, +c.transitMax || 0);
      return { p, before, left: cut - secs, k, wd: (p.wd + k) % 7, from: addBiz(ship, tmin), to: addBiz(ship, tmax) };
    };
  }
  function makeCutoff(el) {
    const c = readCut(el), plan = shipPlanner(c);
    const out = $('[data-nx-cutoff-t]', el) || el, etaEl = $('[data-nx-eta]', el);
    const hu = isHu(c.dateLocale || c.locale || locale());
    // the day it ships, as the sentence needs it: Hungarian "hétfőn", otherwise the weekday's name
    const dayName = wd => { if (hu) return HU_ON[wd]; try { return new Intl.DateTimeFormat(c.locale || locale(), { weekday: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2023, 0, 1 + wd))); } catch (e) { return WD[wd]; } };
    let dfmt = null; try { dfmt = new Intl.DateTimeFormat(c.dateLocale || 'en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }); } catch (e) { dfmt = null; }
    // "Wed 30 Sept": the locale's own order and words, without the comma after the weekday;
    // Hungarian: "okt. 12. (hétfő)"
    const fmtDate = d => (hu ? huDate(d, { short: true }) : dfmt ? dfmt.formatToParts(d).map(x => (x.type === 'literal' ? x.value.replace(/,\s*/g, ' ') : x.value)).join('').replace(/\s+/g, ' ').trim() : d.toUTCString().slice(0, 11));
    const left = sec => {
      const h = Math.floor(sec / 3600), m = Math.floor(sec % 3600 / 60), s = sec % 60;
      if (c.format === 'hm') { const mm = Math.ceil(sec / 60), hh = Math.floor(mm / 60); return (hh ? hh + ' h ' : '') + (mm % 60) + ' min'; }
      return pad(h) + ':' + pad(m) + ':' + pad(s);
    };
    let key = '', timeEl = null, lastS = -1;
    /* the countdown keeps one width, so its line never re-wraps as the digits change (on a phone that
       made the page jump a line every second). 17.0: each digit sits in a cell as wide as the font's
       widest digit (tabular figures, which Poppins does not have), so the width stays put and no gap
       opens after the time ("13:53:41   alatt" in 16.0). Measured once, and again when the font arrives. */
    function setTime(t, str) {
      if (c.format === 'hm') { t.textContent = str; return; }
      if (!t.__cells || t.__cells.length !== str.length) {
        t.textContent = ''; t.__cells = [];
        for (const ch of str) { const sp = document.createElement('span'); sp.className = /\d/.test(ch) ? 'nx-td' : 'nx-tc'; sp.textContent = ch; t.appendChild(sp); t.__cells.push(sp); }
        return;
      }
      for (let i = 0; i < str.length; i++) if (t.__cells[i].textContent !== str[i]) t.__cells[i].textContent = str[i];
    }
    function fixWidth(t) {
      if (c.format === 'hm' || !t.isConnected) return;
      const keep = t.textContent; let w = 0;
      t.style.display = 'inline-block';
      for (let d = 0; d < 10; d++) { t.textContent = '' + d; w = Math.max(w, t.getBoundingClientRect().width); }
      if (w) t.style.setProperty('--nx-dw', Math.ceil(w * 10) / 10 + 'px');
      t.style.display = ''; t.__cells = null; setTime(t, keep);
    }
    try { document.fonts.ready.then(() => { if (timeEl) fixWidth(timeEl); }); } catch (e) { /* no font loading API */ }
    function tick(ms) {
      const sNow = Math.floor(ms / 1000); if (sNow === lastS) return; lastS = sNow;
      const r = plan(ms);
      if (!r) { el.hidden = true; return; }
      tick.plan = r;
      if (r.before) {
        if (key !== 'before') { key = 'before'; timeEl = document.createElement('span'); timeEl.className = 'nx-time'; fillTpl(out, c.before, { time: timeEl, promise: c.promise || '' }); eta(r); fixWidth(timeEl); }
        setTime(timeEl, left(r.left));
      } else {
        const d = r.k === 1 ? (c.tomorrow || 'holnap') : dayName(r.wd);
        if (key !== 'after' + d) { key = 'after' + d; fillTpl(out, c.after, { day: strongEl(d), promise: c.promise || '' }); eta(r); }
      }
    }
    function eta(r) {
      if (!etaEl) return;
      etaEl.hidden = !c.eta;
      if (c.eta) fillTpl(etaEl, ' ' + c.eta, { from: strongEl(fmtDate(r.from)), to: strongEl(fmtDate(r.to)), window: strongEl(fmtDate(r.from) + ' – ' + fmtDate(r.to)) });
    }
    tick.fmtDate = fmtDate;
    return tick;
  }
  function lastSundayOct(y) { const d = new Date(Date.UTC(y, 9, 31, 1, 0, 0)); while (d.getUTCDay() !== 0) d.setUTCDate(d.getUTCDate() - 1); return d; }  // EU clocks go back at 01:00 UTC


  /* ============================================================
     COMMERCE: one shared state per product, so every buy box on
     the page (top, simulator, final offer, sticky bar) always shows
     the same lens, and one "added to your bag" confirmation
     ============================================================ */
  const Stores = {};
  function storeFor(cfg) {
    const pid = String(cfg.productId || 'product');
    let s = Stores[pid];
    const mkV = list => (list || []).map(v => Object.assign({}, v, { key: 'key' in v ? v.key : lensKey(v.title) }));
    if (!s) {
      const V = mkV(cfg.variants);
      s = Stores[pid] = { pid, V, B: null, P: [], pair: null, v: null, bundle: false, subs: new Set(), refs: 0, title: cfg.title || '', money: cfg.moneyFormat, lens: cfg.lens || 'dusk' };
      s.v = V.find(v => v.id === cfg.current) || V.find(v => v.available) || V[0] || null;
    }
    if (!s.V.length && cfg.variants) { s.V = mkV(cfg.variants); s.v = s.V.find(v => v.id === cfg.current) || s.V[0] || null; }
    if (!s.B && cfg.bundle && cfg.bundle.id) s.B = Object.assign({ key: 'set' }, cfg.bundle);
    // 13.0: two lenses together ("Oslo + Lisbon" …): a product of its own, one variant per pair
    if (!s.P.length && cfg.pairs && cfg.pairs.length) s.P = cfg.pairs.map(p => Object.assign({}, p, { pair: true, key: 'pair', lenses: pairLenses(p.title) }));
    if (!s.Ppid && cfg.pairPid) s.Ppid = String(cfg.pairPid);           // 16.0: the two-lens product, for the Releasit form
    if (!s.notes && cfg.notes) s.notes = cfg.notes;                    // "Clear / Yellow / Amber" for the bag confirmation, from any buy box
    if (!s.brand && cfg.brand) s.brand = cfg.brand;                    // 18.0: "STILL" and "TR90 keret" for order names, from the product's buy box
    if (!s.frameLine && cfg.frameLine) s.frameLine = cfg.frameLine;
    if (!s.v && s.B) s.bundle = true;
    s.refs++;
    return s;
  }
  function releaseStore(s) { if (s && --s.refs <= 0) delete Stores[s.pid]; }
  const curOf = s => (s.bundle && s.B ? s.B : s.pair || s.v);
  const keyIn = (s, x) => (x && x === s.B ? 'set' : x && x.pair ? 'pair' : (x && x.key) || s.lens || 'dusk');
  /* the lenses a choice shows: one, both of a pair, or all three of the set */
  const lensesOf = (s, x) => { const k = keyIn(s, x); return k === 'set' ? KEYS : k === 'pair' ? (x.lenses.length ? x.lenses : ['dusk']) : [LENS[k] ? k : 'dusk']; };
  const dotOf = (s, x) => { const L = lensesOf(s, x); return L.length === 2 ? 'linear-gradient(90deg,' + LENS[L[0]].dot + ' 50%,' + LENS[L[1]].dot + ' 50%)' : lensDot(keyIn(s, x)); };
  function selectIn(s, patch, from) {
    Object.assign(s, patch);
    if (!s.v && !s.pair && s.B) s.bundle = true;
    if (!s.bundle && !s.v && !s.pair) return;
    Still.lens = keyIn(s, curOf(s));
    s.subs.forEach(fn => { try { fn(from); } catch (e) { report('buy box', e); } });
  }
  const variantByKey = (s, k) => s.V.find(x => x.key === k && x.available) || s.V.find(x => x.key === k);
  function stockLevel(x, lowAt) {
    if (!x || !x.available) return 'out';
    if (x.mgmt === 'shopify') {
      if (x.qty <= 0) return x.policy === 'continue' ? 'pre' : 'out';
      if (lowAt > 0 && x.qty <= lowAt) return 'low';
    }
    return 'in';
  }
  function rollText(el, txt, bag) {
    if (!el) return;
    const old = $$('span', el).filter(s => !s.classList.contains('is-out')).pop();
    if (old && old.textContent === txt) return;
    const n = document.createElement('span'); n.textContent = txt;
    if (!old || reduce) { el.textContent = ''; el.appendChild(n); return; }
    n.style.transform = 'translateY(100%)'; n.style.opacity = '0';
    old.classList.add('is-out'); el.appendChild(n);
    void n.offsetWidth;
    old.style.transform = 'translateY(-100%)'; old.style.opacity = '0';
    n.style.transform = ''; n.style.opacity = '';
    bag.later(() => old.remove(), 650);
  }

  /* ---------- the "added to your bag" confirmation (one per page) ---------- */
  const Sheet = (() => {
    let wrap = null, el = null, scrim = null, back = null, open = false;
    const phone = mq('(max-width: 560px)');
    function build() {
      if (wrap && wrap.isConnected) return;
      wrap = document.createElement('div'); wrap.className = 'nx nx-portal'; wrap.setAttribute('data-nx-portal', 'sheet');
      wrap.innerHTML = '<div class="nx-scrim"></div><div class="nx-sheet" role="dialog" aria-modal="false" aria-labelledby="NxSheetT" aria-hidden="true">' +
        '<button type="button" class="nx-sheet-x" data-nx-close>×</button><p class="nx-sheet-h" id="NxSheetT"><i aria-hidden="true"></i><span></span></p>' +
        '<div class="nx-sheet-item"><span class="nx-sw-dot" aria-hidden="true"></span><p><span data-n></span><small data-s></small></p><b data-p></b></div>' +
        '<div class="nx-sheet-a"><a class="nx-pill nx-pill--ghost" data-view href="/cart"></a><form method="post" action="/cart"><button type="submit" name="checkout" class="nx-pill"></button></form></div></div>';
      document.body.appendChild(wrap);
      el = $('.nx-sheet', wrap); scrim = $('.nx-scrim', wrap);
      $('[data-nx-close]', el).addEventListener('click', close);
      scrim.addEventListener('click', close);
    }
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
    document.addEventListener('pointerdown', e => { if (open && el && !el.contains(e.target) && !(e.target.closest && e.target.closest('[data-nx-add]'))) close(); });
    function show(item, L, from) {
      build(); L = L || {};
      $('.nx-sw-dot', el).style.background = item.dot;
      fillTpl($('.nx-sheet-h span', el), L.heading || 'Bekerült a kosaradba');
      fillTpl($('[data-n]', el), item.name); fillTpl($('[data-s]', el), item.sub || ''); fillTpl($('[data-p]', el), item.price);
      const v = $('[data-view]', el); v.textContent = L.view || 'Kosár megtekintése'; v.setAttribute('href', L.cartUrl || '/cart');
      $('form', el).setAttribute('action', L.cartUrl || '/cart'); $('form button', el).textContent = L.checkout || 'Tovább a pénztárhoz';
      $('[data-nx-close]', el).setAttribute('aria-label', L.close || 'Bezárás');
      back = from || document.activeElement; open = true; Still.sheetOpen = true;
      el.classList.add('is-on'); el.setAttribute('aria-hidden', 'false');
      if (phone.matches) scrim.classList.add('is-on');
      setTimeout(() => { const c = $('[data-nx-close]', el); if (c && open) c.focus({ preventScroll: true }); }, 60);
    }
    function close() {
      if (!open) return;
      open = false; Still.sheetOpen = false;
      el.classList.remove('is-on'); el.setAttribute('aria-hidden', 'true'); scrim.classList.remove('is-on');
      if (back && back.isConnected && back.focus) back.focus({ preventScroll: true });
    }
    return { show, close, get open() { return open; } };
  })();

  /* ---------- add to cart (Shopify AJAX API; simulated in the preview) ---------- */
  function themeCart(cfg) {
    if (cfg.afterAdd !== 'theme') return null;
    const d = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    return d && typeof d.renderContents === 'function' && typeof d.getSectionsToRender === 'function' ? d : null;
  }
  function updateBubble(sections) {
    const h = sections && sections['cart-icon-bubble'], el = document.getElementById('cart-icon-bubble');
    if (!h || !el) return;
    const src = new DOMParser().parseFromString(h, 'text/html').querySelector('.shopify-section');
    if (src) el.innerHTML = src.innerHTML;
  }
  async function addToCart(x, cfg) {
    let data;
    const tc = themeCart(cfg);
    if (window.STILL_PREVIEW) { await new Promise(r => setTimeout(r, 650)); data = { items: [{ id: x.id, key: String(x.id), quantity: 1 }], sections: {} }; }
    else {
      const sections = tc ? tc.getSectionsToRender().map(s => s.id) : ['cart-icon-bubble'];
      const res = await fetch((cfg.cartAdd || '/cart/add') + '.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: x.id, quantity: 1 }], sections: sections.join(','), sections_url: location.pathname })
      });
      data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) throw new Error(data.description || data.message || cfg.addError || 'Sorry, this could not be added to your bag.');
    }
    updateBubble(data.sections);
    try { if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'nocta', productVariantId: x.id, cartData: data }); } catch (e) { /* theme without pub/sub */ }
    document.dispatchEvent(new CustomEvent('nocta:cart:added', { bubbles: true, detail: { variantId: x.id, response: data } }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    vib(12);
    return data;
  }
  /* after a successful add: the theme's drawer, the cart page, or the STILL confirmation */
  function afterAdd(s, x, cfg, data, from) {
    if (cfg.afterAdd === 'cart' && !window.STILL_PREVIEW) { location.href = cfg.cartUrl || '/cart'; return; }
    const tc = themeCart(cfg), item = (data.items && data.items[0]) || {};
    if (tc && !window.STILL_PREVIEW) { tc.renderContents(Object.assign({ id: item.id, key: item.key }, item, { sections: data.sections })); return; }
    const nm = orderName(s, x, cfg);                          // 18.0: "STILL Havana" · "TR90 keret · Este · meleg fény"
    Sheet.show({ dot: dotOf(s, x), name: nm.name, sub: nm.sub, price: formatMoney(x.price, s.money) }, Object.assign({ cartUrl: cfg.cartUrl }, cfg.sheet || {}), from);
  }
  /* a button that shows idle → spinner → check → idle */
  function cartButton(btn, cfg, labelOf) {
    if (!btn) return null;
    return {
      el: btn,
      set(state, x, busyText) {
        btn.setAttribute('data-state', state);
        btn.disabled = state === 'busy' || !(x && x.available);
        btn.setAttribute('aria-busy', state === 'busy' ? 'true' : 'false');
        if (state === 'busy') {
          btn.style.minWidth = btn.offsetWidth + 'px';
          btn.innerHTML = '<span class="nx-spin" aria-hidden="true"></span><span class="nx-sr"></span>';
          btn.lastChild.textContent = busyText || cfg.adding || 'Adding…';
        } else if (state === 'done') {
          btn.innerHTML = '<svg class="nx-check" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.2 4.2L19 7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg><span></span>';
          btn.lastChild.textContent = cfg.added || 'Added';
        } else {
          btn.style.minWidth = '';
          const s = document.createElement('span');
          fillTpl(s, x && x.available ? labelOf(x) : (cfg.soldOut || 'Elfogyott'), { price: x ? formatMoney(x.price, cfg.moneyFormat) : '' });
          btn.textContent = ''; btn.appendChild(s);
        }
      }
    };
  }
  /* runs one add: busy state, the API call, errors next to the button.
     16.0: while the shop sells with the Releasit cash-on-delivery form (STILL · Utánvét section),
     the same buttons open that form instead (26-cod.js) */
  function runAdd(s, x, cfg, btn, others, errEl, bag) {
    if (codOn()) return codOrder(s, x, cfg, btn, others, errEl, bag);
    return runAddCart(s, x, cfg, btn, others, errEl, bag);
  }
  async function runAddCart(s, x, cfg, btn, others, errEl, bag) {
    if (!x || !x.available || s.busy) return;
    s.busy = true;
    if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
    btn.set('busy', x); others.forEach(o => { if (o && o !== btn) o.el.disabled = true; });
    const idle = () => { s.busy = false; s.subs.forEach(fn => { try { fn('idle'); } catch (e) { /* ignore */ } }); };
    let data;
    try {
      data = await addToCart(x, cfg);
    } catch (e) {
      idle();
      // offline or a dropped connection: the merchant's own message, not "Failed to fetch"
      const msg = !e || e instanceof TypeError || !e.message ? (cfg.addError || 'Sorry, this could not be added to your bag. Please try again.') : e.message;
      if (errEl) { errEl.hidden = false; errEl.textContent = msg; }
      return;
    }
    // the item is in the bag now. If the theme's drawer fails to refresh, the visitor still
    // gets a confirmation and never a false "could not add" (which leads to a second add)
    btn.set('done', x);
    try { afterAdd(s, x, cfg, data, btn.el); } catch (e) { report('after add', e); try { cfg = Object.assign({}, cfg, { afterAdd: 'sheet' }); afterAdd(s, x, cfg, data, btn.el); } catch (e2) { /* nothing more to do */ } }
    bag.later(idle, 1600);
  }

  /* ---------- a buy box: swatches, set, price, stock, button, cut-off ----------
     Any section can hold one. All of them share the product's state. */
  /* 17.0: what the button orders, by name. One lens: the brand (its own span) and the lens ("STILL" "Havana"),
     the frame and the lens note under it. A pair: its product's name and the pair ("STILL Duo", "Oslo + Havana").
     The set: its name and its line ("STILL Full Day", "Reggeltől estig …"). */
  function nameOf(s, x, cfg) {
    const notes = cfg.notes || s.notes || {}, frame = cfg.frameLine || '';
    if (x === s.B) return { brand: false, name: s.B.title || '', sub: s.B.sub || '' };
    if (x.pair) return { brand: false, name: x.ptitle || '', sub: x.title || '' };
    const note = (x.key && notes[x.key]) || '';
    return { brand: !!cfg.brand, name: x.title !== 'Default Title' ? x.title : (s.title || ''), sub: [frame, note].filter(Boolean).join(' · ') };
  }
  /* 18.0: the name of an order line wherever the page names one (the order form, the bag confirmation):
     one lens is "STILL Havana" over "TR90 keret · Este · meleg fény"; a pair keeps its name with the
     lenses under it ("STILL Duo" · "Oslo + Havana"), the set too ("STILL Full Day" · its line) */
  function orderName(s, x, cfg) {
    cfg = cfg || {};
    if (x === s.B) return { name: s.B.title || '', sub: s.B.sub || '' };
    if (x.pair) return { name: x.ptitle || s.title || '', sub: x.title || '' };
    const notes = cfg.notes || s.notes || {}, note = (x.key && notes[x.key]) || '';
    const frame = cfg.frameLine || s.frameLine || '', sub = [frame, note].filter(Boolean).join(' · ');
    if (!x.title || x.title === 'Default Title') return { name: s.title || '', sub };
    if (!LENS[x.key]) return { name: s.title || x.title, sub: s.title ? x.title : '' };      // not a lens (sizes, colours): the product and its variant
    const brand = cfg.brand || s.brand || String(s.title || '').split(' ')[0];
    return { name: brand ? brand + ' ' + x.title : x.title, sub };
  }
  function buyBox(box, s, cfg, bag, opts) {
    opts = opts || {};
    const q = sel => $(sel, box);
    const E = {
      sws: $$('.nx-sw[data-id]', box), pairs: $$('.nx-pair[data-pair]', box), pairWrap: q('[data-nx-pairs]'), bundle: q('[data-nx-bundle]'), form: q('form.nx-form'), id: q('form.nx-form input[name="id"]'),
      add: q('[data-nx-add]'), price: q('[data-nx-price]'), compare: q('[data-nx-compare]'), per: q('[data-nx-per]'),
      pname: q('[data-nx-pname-n]'), psub: q('[data-nx-pname-s]'), pbrand: q('[data-nx-pname] .nx-brandw'),
      stock: q('[data-nx-stock]'), cutoff: q('[data-nx-cutoff]'), err: q('[data-nx-err]'), contents: q('[data-nx-contents]')
    };
    const money = c => formatMoney(c, cfg.moneyFormat);
    const main = cartButton(E.add, cfg, () => cfg.addLabel || 'Kosárba');
    const extra = (opts.extraButtons || []).filter(Boolean);
    const all = [main].concat(extra).filter(Boolean);
    const tick = E.cutoff ? makeCutoff(E.cutoff) : null;
    function render(from) {
      const x = curOf(s); if (!x) return;
      E.sws.forEach(b => b.setAttribute('aria-checked', !s.bundle && !s.pair && s.v && +b.getAttribute('data-id') === s.v.id ? 'true' : 'false'));
      E.pairs.forEach(b => { const on = !s.bundle && !!s.pair && +b.getAttribute('data-pair') === s.pair.id; b.setAttribute('aria-checked', on ? 'true' : 'false'); });
      if (E.pairs.length) { const pon = E.pairs.find(b => b.getAttribute('aria-checked') === 'true') || E.pairs[0]; E.pairs.forEach(b => { b.tabIndex = b === pon ? 0 : -1; }); if (E.pairWrap) E.pairWrap.classList.toggle('is-on', !s.bundle && !!s.pair); }
      const on = E.sws.find(b => b.getAttribute('aria-checked') === 'true') || E.sws[0];
      E.sws.forEach(b => { b.tabIndex = b === on ? 0 : -1; });
      if (E.bundle) E.bundle.setAttribute('aria-pressed', s.bundle ? 'true' : 'false');
      if (E.id) E.id.value = x.id;
      if (E.pname) {   // 17.0: "STILL Havana" · "TR90 keret · Este · meleg fény"; a pair or the set shows its own name
        const nm = nameOf(s, x, cfg);
        E.pname.textContent = nm.name; if (E.pbrand) E.pbrand.hidden = !nm.brand; if (E.psub) E.psub.textContent = nm.sub;
      }
      rollText(E.price, money(x.price), bag);
      if (E.compare) { const show = !!cfg.showCompare && x.compare > x.price; E.compare.hidden = !show; if (show) E.compare.textContent = money(x.compare); }
      if (E.per) {
        const show = !!cfg.perText && x.price > 0;
        E.per.hidden = !show;
        if (show) fillTpl(E.per, cfg.perText, { per: strongEl(formatSmall(perNight(x.price), cfg.moneyFormat)) });
      }
      if (E.stock) {
        const lv = stockLevel(x, cfg.lowAt), txt = (cfg.stock || {})[lv] || '';
        E.stock.setAttribute('data-level', lv === 'pre' ? 'in' : lv);
        E.stock.hidden = !txt;
        fillTpl($('span', E.stock), txt, { count: String(x.qty) });
      }
      if (E.contents && cfg.contents) {   // 18.0 · "Mit kapok?": one lens, a pair or the set, as the line under the price
        const C = cfg.contents, kind = x === s.B ? 'set' : x.pair ? 'pair' : 'single', named = x.title && x.title !== 'Default Title';
        let t = C[kind] || '';
        t = named ? t.replace('[lens]', x.title).replace('[lenses]', x.title) : t.replace(' [lens] lencsével', '').replace('[lens]', '').replace('[lenses]', '');
        E.contents.hidden = !t; if (t && E.contents.textContent !== t) E.contents.textContent = t;
      }
      if (E.cutoff) E.cutoff.hidden = !x.available;
      if (!s.busy) all.forEach(b => b.set('idle', x));
      if (opts.onRender) opts.onRender(x, from);
    }
    s.subs.add(render); bag.add(() => s.subs.delete(render));
    E.pairs.forEach(b => bag.on(b, 'click', () => { const p = s.P.find(x => x.id === +b.getAttribute('data-pair')); if (p) selectIn(s, { pair: p, bundle: false }, box); vib(8); }));
    if (E.pairs.length) bag.on(E.pairs[0].parentElement, 'keydown', e => {
      const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key]; if (!dir) return;
      e.preventDefault();
      const i = Math.max(0, E.pairs.indexOf(document.activeElement)), n = E.pairs[(i + dir + E.pairs.length) % E.pairs.length];
      n.focus(); n.click();
    });
    E.sws.forEach(b => bag.on(b, 'click', () => { const v = s.V.find(v => v.id === +b.getAttribute('data-id')); if (v) { selectIn(s, { v, bundle: false, pair: null }, box); if (opts.onPick) opts.onPick(v); } vib(8); }));
    const swWrap = E.sws[0] && E.sws[0].parentElement;
    bag.on(swWrap, 'keydown', e => {
      const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key]; if (!dir) return;
      e.preventDefault();
      const i = Math.max(0, E.sws.indexOf(document.activeElement)), n = E.sws[(i + dir + E.sws.length) % E.sws.length];
      n.focus(); n.click();
    });
    bag.on(E.bundle, 'click', () => { if (!s.B) return; selectIn(s, { bundle: !s.bundle, pair: null }, box); vib(8); });
    const add = btn => runAdd(s, curOf(s), cfg, btn, all, E.err, bag);
    bag.on(E.form, 'submit', e => { e.preventDefault(); if (main) add(main); });
    if (!E.form && main) bag.on(E.add, 'click', () => add(main));
    extra.forEach(b => bag.on(b.el, 'click', () => add(b)));
    if (E.add) { ctas.add(E.add); bag.add(() => ctas.delete(E.add)); }
    render();
    return { render, tick: ms => { if (tick) tick(ms); }, cutoffTick: tick, add: E.add };
  }

  /* ============================================================
     16.0 · CASH ON DELIVERY THROUGH THE RELEASIT FORM
     While the shop sells only with cash on delivery, every STILL buy
     button (product page, sticky bar, final offer, drag demo) opens
     the Releasit order form (Releasit COD Form & Upsells) with what
     the visitor picked: one lens, a "STILL Duo" pair or the set.
     Releasit has no public JavaScript API. For each product the page
     sells, the theme renders the spot Releasit documents for custom
     pages (the product's JSON and Releasit's button hook inside a
     product form: snippets/nocta-v8-cod-targets.liquid). The script
     writes the picked variant into that form and clicks the button
     Releasit drew there (or the hidden button that carries Releasit's
     page-builder classes, if the section says so). Releasit loads a
     moment after the page: a click before that waits a few seconds,
     then shows a short message or falls back to the cart (a setting).
     Every opening is announced as "nocta:cod:open" (the preview,
     which has no Releasit, opens a mock of the form on it).
     ============================================================ */
  let CODCFG;
  function codCfg() {
    if (CODCFG && CODCFG.el.isConnected) return CODCFG.c;
    const host = document.querySelector('[data-nocta="cod"]');
    CODCFG = host ? { el: host, c: readConfig(host) } : null;
    return CODCFG ? CODCFG.c : null;
  }
  const codOn = () => { const c = codCfg(); return !!c && c.mode !== 'cart'; };
  const codSpot = pid => (pid ? document.querySelector('.nx-cod-t[data-nx-cod-pid="' + pid + '"]') : null);
  /* the element to click: the proxy with Releasit's classes, or whatever Releasit drew into its hook */
  function codButton(spot, c) {
    if (!spot) return null;
    if (c.trigger === 'class') return $('[data-nx-rsi-proxy]', spot);
    const hook = $('._rsi-cod-form-gempages-button-hook', spot);
    if (!hook || !hook.firstElementChild) return null;
    return $('button, a, [role="button"], input[type="submit"], input[type="button"]', hook) || hook.firstElementChild;
  }
  /* a click that reaches a handler on the element or any of its parents (works for any element) */
  function clickEl(el) {
    let leaf = el; while (leaf.firstElementChild && !/^(BUTTON|A|INPUT)$/.test(leaf.tagName)) leaf = leaf.firstElementChild;
    if (typeof leaf.click === 'function') leaf.click();
    else leaf.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  }
  async function codOrder(s, x, cfg, btn, others, errEl, bag) {
    const c = codCfg() || {};
    if (!x || !x.available || s.busy) return;
    const pid = x === s.B ? String(s.B.pid || '') : x.pair ? String(s.Ppid || '') : String(s.pid);
    const spot = codSpot(pid);
    const idIn = spot && $('input[name="id"]', spot);
    if (idIn) idIn.value = x.id;
    if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
    const { name, sub } = orderName(s, x, cfg);               // 18.0: "STILL Havana" · "TR90 keret · Este · meleg fény"; pairs and the set keep their names
    Still.codOpened = true;                                   // the scratch card stays away from now on
    document.dispatchEvent(new CustomEvent('nocta:cod:open', { bubbles: true, detail: {
      productId: pid, variantId: x.id, name, variant: x.title, sub, price: x.price, money: s.money,
      kind: x === s.B ? 'set' : x.pair ? 'pair' : 'single', lenses: lensesOf(s, x), dot: dotOf(s, x), from: btn && btn.el
    } }));
    if (window.STILL_PREVIEW) return;                         // the preview opens its mock of the form
    let b = codButton(spot, c);
    if (!b && spot) {
      // Releasit is still loading: wait a little (the button says so), then go on or give up
      s.busy = true; btn.set('busy', x, c.opening); others.forEach(o => { if (o && o !== btn) o.el.disabled = true; });
      const t0 = performance.now(), limit = Math.max(1, +c.wait || 4) * 1000;
      while (!b && performance.now() - t0 < limit) { await new Promise(r => setTimeout(r, 150)); b = codButton(spot, c); }
      s.busy = false; s.subs.forEach(fn => { try { fn('idle'); } catch (e) { /* ignore */ } });
    }
    if (b) { try { clickEl(b); } catch (e) { report('releasit', e); } return; }
    report('releasit', new Error(spot ? 'the Releasit form did not load (is the app embed on?)' : 'no Releasit spot for product ' + pid));
    if (c.fallback === 'cart') return runAddCart(s, x, Object.assign({}, cfg, { afterAdd: 'cart' }), btn, others, errEl, bag);
    if (errEl) { errEl.hidden = false; errEl.textContent = c.notReady || 'A rendelési űrlap most nem töltött be. Frissítsd az oldalt, és próbáld újra.'; }
  }
  /* the section itself only holds the settings */
  MODULES.cod = function () { CODCFG = undefined; return {}; };
  /* ============================================================
     PRODUCT: gallery with the turning photos, lens picker, honest FOMO,
     add to cart, "added" sheet, sticky bar
     ============================================================ */
  MODULES.product = function (root, cfg, bag, inst) {
    const edit = designMode();
    const q = sel => $(sel, root);
    const els = { sbar: q('[data-nx-sbar]'), gal: q('[data-nx-gal]'), s3d: q('[data-nx-3d]'), seal: q('[data-nx-seal]'), box: q('.nx-pdp-buy') || root, side: q('[data-nx-side]') };
    const wideQ = mq('(min-width: 901px)');
    let sideTop = null;
    const s = storeFor(cfg);
    bag.add(() => releaseStore(s));
    if (!curOf(s)) return {};

    // the sticky bar lives on <body>, out of any transformed theme wrapper
    portal(bag, inst.id, [els.sbar]);
    const sbBtn = els.sbar && cartButton($('[data-nx-add]', els.sbar), cfg, () => cfg.sbarLabel || cfg.addLabel || 'Kosárba');

    /* ---------- gallery ---------- */
    let gi = -1;
    let slides = els.gal ? $$('.nx-slide:not([hidden])', els.gal) : [], thumbs = $$('.nx-thumbs button:not([hidden])', root);
    const arrows = $$('.nx-arrow', root), gdots = $$('.nx-gdots i', root), thumbBox = q('.nx-thumbs'), gnum = q('[data-nx-gnum]'), gtot = q('[data-nx-gtot]');
    const galGo = (i, now) => { if (!els.gal) return; i = clamp(i, 0, slides.length - 1); els.gal.scrollTo({ left: i * els.gal.clientWidth, behavior: reduce || now ? 'auto' : 'smooth' }); };
    function galSync() {
      if (!els.gal) return;
      const i = clamp(Math.round(els.gal.scrollLeft / Math.max(1, els.gal.clientWidth)), 0, slides.length - 1);
      if (i === gi) return; gi = i;
      thumbs.forEach((t, k) => { if (k === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
      gdots.forEach((d, k) => d.classList.toggle('is-on', k === i));
      const gw = els.gal.closest('.nx-pdp-gal'); if (gw) gw.classList.toggle('nx-on-info', !!(slides[i] && slides[i].classList.contains('nx-slide--info')));   // 17.0: the seal steps aside on phones
      if (gnum) gnum.textContent = i + 1;
      arrows.forEach(a => { a.disabled = (+a.getAttribute('data-dir') < 0 && i === 0) || (+a.getAttribute('data-dir') > 0 && i === slides.length - 1); });
      // a thumbnail out of its rail comes back into view (the rail only, never the page): sideways on
      // tablets, up and down in the rail beside the image on wide screens
      const t = thumbs[i];
      if (t && thumbBox) {
        const r = t.getBoundingClientRect(), b = thumbBox.getBoundingClientRect();
        if (thumbBox.scrollHeight > thumbBox.clientHeight + 2) {
          if (r.top < b.top || r.bottom > b.bottom) thumbBox.scrollTo({ top: thumbBox.scrollTop + (r.top - b.top) - (b.height - r.height) / 2, behavior: reduce ? 'auto' : 'smooth' });
        } else if (thumbBox.scrollWidth > thumbBox.clientWidth + 2) {
          if (r.left < b.left || r.right > b.right) thumbBox.scrollTo({ left: thumbBox.scrollLeft + (r.left - b.left) - (b.width - r.width) / 2, behavior: reduce ? 'auto' : 'smooth' });
        }
      }
    }
    if (els.gal) {
      bag.on(els.gal, 'scroll', rafOnce(galSync), { passive: true });
      if (thumbBox) bag.on(thumbBox, 'click', e => { const t = e.target.closest && e.target.closest('button[data-i]'); if (t && !t.hidden) { galGo(+t.getAttribute('data-i')); vib(6); } });
      arrows.forEach(a => bag.on(a, 'click', () => galGo(gi + +a.getAttribute('data-dir'))));
      bag.on(els.gal, 'keydown', e => { if (e.key === 'ArrowRight') { galGo(gi + 1); e.preventDefault(); } if (e.key === 'ArrowLeft') { galGo(gi - 1); e.preventDefault(); } });
      bag.on(window, 'resize', () => { if (gi > 0) els.gal.scrollTo({ left: gi * els.gal.clientWidth }); });
      galSync();
    }
    /* 15.0 · the picked lens's photos open the gallery: its product and person photos, its detail and
       its lifestyle photo (data-g: day, play, dusk), then the turning frame and the "3 lencse" graphic
       (data-fix), then the photos of no lens (lifestyle), the set's photo last (data-g="set"). The
       other lenses' photos wait hidden (or follow the lifestyle photos, as the theme setting says).
       Each slide knows its place in the product (data-o); the thumbnails move with their slides. */
    const pairs = (els.gal ? $$('.nx-slide', els.gal) : []).map(sl => ({ sl, th: null }));
    { const all = $$('.nx-thumbs button', root); pairs.forEach((x, k) => { x.th = all[k] || null; }); }
    const LENSES = ['day', 'play', 'dusk'];
    const lensKey = () => { const c = curOf(s), k = keyIn(s, c); return k === 'pair' ? (lensesOf(s, c)[0] || '') : k; };
    let galKey = lensKey(), shownLens = LENSES.indexOf(galKey) >= 0 ? galKey : (cfg.lens || 'dusk');
    function galOrder(lens) {
      const o = x => +x.sl.getAttribute('data-o') || 0, g = x => x.sl.getAttribute('data-g') || '', by = (a, b) => o(a) - o(b);
      const fx = t => pairs.find(x => x.sl.getAttribute('data-fix') === t);
      const photos = pairs.filter(x => !x.sl.hasAttribute('data-fix'));
      const mine = photos.filter(x => g(x) === lens).sort(by);
      const rest = photos.filter(x => !g(x)).sort(by);
      const others = photos.filter(x => LENSES.indexOf(g(x)) >= 0 && g(x) !== lens).sort(by);
      const set = photos.filter(x => g(x) === 'set');
      const fixed = [fx('3d'), fx('info')].filter(Boolean);
      let seq;
      if (mine.length) seq = mine.concat(fixed, rest);
      else { seq = rest.slice(); fixed.forEach(x => seq.splice(clamp(+x.sl.getAttribute('data-pos') - 1, 0, seq.length), 0, x)); }
      const showOthers = cfg.otherLenses === 'end';
      if (showOthers) seq = seq.concat(others);
      seq = seq.concat(set);
      return { seq, hide: showOthers ? [] : others, first: mine.length ? 0 : -1, set: set[0] || null };
    }
    function galLens(key) {
      if (!els.gal || !key || key === galKey) return;
      galKey = key;
      const lens = LENSES.indexOf(key) >= 0 ? key : shownLens;
      const { seq, hide, first, set } = galOrder(lens);
      const target = key === 'set' ? set : (first >= 0 ? seq[first] : null);
      if (!target && lens === shownLens) return;                       // nothing of that lens: nothing moves
      if (lens !== shownLens) {
        shownLens = lens;
        const showing = slides[Math.max(0, gi)];
        seq.forEach((x, k) => {
          x.sl.hidden = false; els.gal.appendChild(x.sl);
          const lab = x.sl.getAttribute('aria-label') || '';
          x.sl.setAttribute('aria-label', (k + 1) + ' / ' + seq.length + lab.replace(/^\d+ \/ \d+/, '').replace(/^(?!\s·)(.+)$/, ' · $1'));
          if (x.th && thumbBox) { x.th.hidden = false; thumbBox.appendChild(x.th); x.th.setAttribute('data-i', k); }
        });
        hide.forEach(x => {
          x.sl.hidden = true; els.gal.appendChild(x.sl);
          if (x.th && thumbBox) { x.th.hidden = true; thumbBox.appendChild(x.th); x.th.setAttribute('data-i', -1); }
        });
        slides = seq.map(x => x.sl); thumbs = seq.map(x => x.th).filter(Boolean);
        if (gtot) gtot.textContent = slides.length;
        // stay on the slide that was showing if it is still there, then slide to the lens's first photo
        gi = -1; const at = slides.indexOf(showing); galGo(at >= 0 ? at : 0, true); galSync();
      }
      galGo(target ? slides.indexOf(target.sl) : 0);
    }
    if (els.gal) {
      const sub = from => { if (from === 'idle') return; galLens(lensKey()); };
      s.subs.add(sub); bag.add(() => s.subs.delete(sub));
    }
    /* 17.0: the light behind the product takes the light of the lens's city: Oslo's cool morning,
       Lisbon's gold afternoon, Havana's warm evening (a slow cross-fade in CSS; the set stays neutral) */
    const lightOf = () => { const x = curOf(s), k = keyIn(s, x); return LENS[k] ? k : k === 'pair' ? ((x && x.lenses && x.lenses[0]) || 'dusk') : 'set'; };
    const light = () => root.setAttribute('data-nx-light', lightOf());
    light(); s.subs.add(light); bag.add(() => { s.subs.delete(light); root.removeAttribute('data-nx-light'); });
    /* the turning frame: real frames of the STILL TR90, lens colour from the lens picked anywhere on
       the page. 15.0: it turns on its own on every screen, frame by frame; nothing to drag, so every
       swipe moves the gallery or the page. */
    const spin = els.s3d ? spinner(els.s3d, bag) : null;

    /* ---------- the buy box, plus the sticky bar that mirrors it ---------- */
    // the note can name the real arrival day: "arrives [eta]" (from the cut-off block's transit days)
    let bb = null, noteNow = '';
    function sbarNote() {
      const n = cfg.sbarNote || ''; if (n.indexOf('[eta]') < 0) return n;
      const t = bb && bb.cutoffTick, pl = t && t.plan;
      return pl && t.fmtDate ? n.replace('[eta]', t.fmtDate(pl.from)) : '';
    }
    /* 18.0: the main line is the order's name, as in the order form: "STILL Havana", "STILL Duo",
       "STILL Full Day"; a pair's lenses lead the second line ("Lisbon + Havana · 27 990 Ft · …").
       The second line keeps the price and the real arrival day in the longest form that fits the
       bar: "érkezik: okt. 20., kedd", then "okt. 20., kedd", then "okt. 20.", and only then the
       price alone (17.0 cut the day off on phones up to 400 px). */
    let sbarOpts = [];
    function sbarFit() {
      const sp = els.sbar && $('.nx-sbar-t span', els.sbar); if (!sp || !sbarOpts.length) return;
      if (!sp.clientWidth) { sp.textContent = sbarOpts[0]; return; }          // not laid out yet: fitted when the bar shows
      for (let i = 0; i < sbarOpts.length; i++) { sp.textContent = sbarOpts[i]; if (sp.scrollWidth <= sp.clientWidth) return; }
    }
    function sbarRender(x) {
      if (!els.sbar) return;
      const dot = $('.nx-sbar-dot', els.sbar); if (dot) dot.style.background = dotOf(s, x);
      fillTpl($('.nx-sbar-t strong', els.sbar), orderName(s, x, cfg).name);
      noteNow = sbarNote();
      const price = formatMoney(x.price, cfg.moneyFormat), lead = x.pair ? x.title : '';
      const t = bb && bb.cutoffTick, pl = t && t.plan, day = pl && t.fmtDate ? t.fmtDate(pl.from) : '';
      const line = a => a.filter(Boolean).join(' · ');
      sbarOpts = [line([lead, price, noteNow])];
      if (day && noteNow) { sbarOpts.push(line([lead, price, day])); if (day.indexOf(',') > 0) sbarOpts.push(line([lead, price, day.split(',')[0]])); }
      sbarOpts.push(line([lead, price]));
      if (lead) sbarOpts.push(lead);                                        // a pair on the narrowest phones: its lenses (the price is on the buy box and in the form)
      sbarOpts = sbarOpts.filter((o, i, a) => a.indexOf(o) === i);
      sbarFit();
    }
    function toUrl(v) {
      if (!cfg.updateUrl || edit || !v) return;
      try { const u = new URL(location.href); u.searchParams.set('variant', v.id); u.hash = ''; history.replaceState(history.state, '', u.toString()); } catch (e) { /* sandboxed preview */ }
    }
    bb = buyBox(els.box, s, cfg, bag, { extraButtons: [sbBtn], onRender: sbarRender, onPick: toUrl });
    bag.on(window, 'resize', sbarFit, { passive: true });                // 18.0: refit the bar's second line
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(sbarFit).catch(() => {});
    // links from other pages can pick the lens: #oslo, #lisbon, #havana (or #day, #play, #dusk), #duo or #pair, #set or #fullday
    const HASH = { oslo: 'day', lisbon: 'play', havana: 'dusk', duo: 'pair', fullday: 'set', 'full-day': 'set' };
    function fromHash() {
      let h = (location.hash || '').slice(1).toLowerCase(); if (!h) return;
      h = HASH[h] || h;
      if (h === 'set' && s.B) { selectIn(s, { bundle: true, pair: null }, root); return; }
      if (h === 'pair' && s.P.length) { selectIn(s, { pair: s.P.find(x => x.available) || s.P[0], bundle: false }, root); return; }
      const v = variantByKey(s, h);
      if (v) selectIn(s, { v, bundle: false, pair: null }, root);
    }
    fromHash(); bag.on(window, 'hashchange', fromHash);

    /* ---------- sticky bar ---------- */
    let barOn = false;
    function setBar(on) {
      if (!els.sbar || on === barOn) return; barOn = on;
      els.sbar.classList.toggle('is-on', on); els.sbar.setAttribute('aria-hidden', on ? 'false' : 'true');
      if (on) sbarFit();
      html.classList.toggle('nx-sbar-on', on);
      if (sbBtn) sbBtn.el.tabIndex = on ? 0 : -1;
    }
    // the countdown and the arrival day in the sticky bar tick once a second, loop or no loop
    const tickNow = () => { if (document.hidden) return; bb.tick(Date.now()); if (els.sbar && sbarNote() !== noteNow) sbarRender(curOf(s)); };
    tickNow(); bag.every(tickNow, 1000);
    let lastBar = 0, sealY = -1, want = null, m = { y: 0, r: null, g: null };
    const sealSvg = els.seal && $('svg', els.seal);
    bag.add(() => html.classList.remove('nx-sbar-on'));
    return {
      always: true,                                  // the sticky bar is needed exactly when this section is off screen
      measure(f) {
        m.y = Math.round(pageY());
        /* 15.0 · the head and buy box column beside the gallery stays in view: at the top when it fits
           the screen, otherwise it scrolls with the page until its end shows and stays there */
        if (els.side) {
          let t = null;
          if (wideQ.matches) { const h = els.side.offsetHeight, top = f.top + 24; t = Math.round(h + top + 24 <= f.vh ? top : f.vh - h - 24); }
          if (t !== sideTop) { sideTop = t; if (t == null) els.side.style.removeProperty('--nx-side-top'); else els.side.style.setProperty('--nx-side-top', t + 'px'); }
        }
        if (els.sbar && f.now - lastBar > 120) {
          lastBar = f.now;
          const past = bb.add && bb.add.getBoundingClientRect().bottom < f.top + 4;
          let any = false; ctas.forEach(b => { if (!any && onScreen(b, f.top)) any = true; });
          want = !!past && !any && !Sheet.open && !Still.locked && !edit && !overlayOpen();
        }
        m.r = m.g = null;
        if (inst.near && spin && els.gal) { m.r = els.s3d.getBoundingClientRect(); m.g = els.gal.getBoundingClientRect(); }
      },
      frame(f) {
        // the 30-night seal turns as the page scrolls
        if (sealSvg && !reduce && inst.near && m.y !== sealY) { sealY = m.y; sealSvg.style.transform = 'rotate(' + (m.y * .14 % 360).toFixed(1) + 'deg)'; }
        if (want != null) { setBar(want); want = null; }
        if (m.r) {
          const r = m.r, g = m.g;
          // off screen, or swiped away in the gallery: nothing turns and nothing loads
          if (g.bottom < f.top - f.vh || g.top > f.vh * 2) return;
          if (r.right <= g.left + 2 || r.left >= g.right - 2 || g.bottom < f.top || g.top > f.vh) { spin.prep(r.width); return; }
          return spin.frame(f, r.width, r.height, lensesOf(s, curOf(s)));
        }
      },
      select(el) {
        if (el.classList.contains('nx-fq')) { const qb = $('.nx-fq-q', el); if (qb) toggleFaq(qb, true); }
      }
    };
  };

  /* ============================================================
     AMBER WASH: a bright late-night screen, then the lenses come
     up to your eyes and the whole screen turns warm
     ============================================================ */
  MODULES.amber = function (root, cfg, bag) {
    const stick = $('.nx-amber-stick', root), base = $('.nx-amber-scene .nx-scn', root), lens = $('.nx-amber-lens', root), tint = $('.nx-amber-tint', root);
    const svg = $('.nx-amber-frame', root), paths = $$('.nx-amber-frame path', root), lines = $$('[data-nx-line]', root);
    const glare = $('.nx-amber-glare', root), veil = $('.nx-amber-veil', root), intro = $('.nx-amber-top', root);
    let box = '';
    if (!stick || !lens || paths.length < 3) return {};
    const k = LENS[cfg.lens] ? cfg.lens : 'dusk';
    if (base && !$('.nx-scn', lens)) { const c = cloneScene(base); c.style.filter = LENS[k].sim.f; lens.insertBefore(c, tint); }
    let key = '';
    const TH = [.56, .63, .7], m = {};
    return {
      measure() {
        const r = root.getBoundingClientRect();
        m.top = r.top; m.bottom = r.bottom; m.w = stick.clientWidth; m.h = stick.clientHeight; m.H = root.offsetHeight;
      },
      frame(f) {
        if (m.bottom < 0 || m.top > f.vh) return;
        const w = m.w, h = m.h;
        const total = Math.max(1, m.H - h), p = clamp(-m.top / total);
        const nk = p.toFixed(4) + ',' + w + ',' + h; if (nk === key) return; key = nk;
        const a = reduce ? (p > .3 ? 1 : 0) : smooth(clamp((p - .05) / .5));
        const sc0 = Math.min(w * .36 / 2.62, h * .3), sc1 = 1.35 * Math.max(w * 1.02, h * 1.16);
        const sc = sc0 * Math.pow(sc1 / sc0, a);                       // exponential zoom reads as a steady approach
        const u = smooth(clamp((a - .2) / .7));
        const x = w / 2 - .64 * sc * u, y = lerp(h * .6, h * .5, a);
        if (box !== w + 'x' + h) { box = w + 'x' + h; svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h); }
        // the glasses grow toward the eyes: only the outline and the lens mask are redrawn; the
        // scene seen through the lens is its own layer and is not repainted
        const clip = drawGlasses(paths, x, y, sc);
        lens.style.clipPath = clip; lens.style.webkitClipPath = clip;
        // everything else fades: opacity only
        if (glare) glare.style.opacity = (1 - .72 * a).toFixed(3);
        if (veil) veil.style.opacity = smooth(clamp((p - .5) / .2)).toFixed(3);
        if (intro) intro.style.opacity = (1 - clamp((p - .02) / .12)).toFixed(3);
        lines.forEach((l, i) => {
          const t = TH[Math.min(i, 2)];
          if (p >= t) l.classList.add('is-in'); else if (p < t - .04) l.classList.remove('is-in');
        });
      }
    };
  };

  /* ============================================================
     INSIDE THE FRAME (13.0): the teardown film on its own, looping.
     The film is a ping-pong (apart, then back together), so its last
     frame is its first: the loop has no visible restart. Muted, inline,
     it plays while near the screen and rests off screen or in a
     background tab. With reduced motion the poster stays and the film
     is never loaded.
     ============================================================ */
  function overlayOpen(t) {
    return Still.sheetOpen || /\boverflow-hidden/.test(document.body.className) || /\b(js-drawer-open|drawer-open|menu-open|search-open)\b/.test(html.className + ' ' + document.body.className) ||
      !!(t && t.closest && t.closest('cart-drawer, .drawer, dialog, [role="dialog"], .menu-drawer, [aria-modal="true"]'));
  }

  MODULES.story = function (root, cfg, bag) {
    const v = $('video.nx-tear-v', root);
    if (!v || reduce || cfg.autoplay === false) return {};
    const RATE = clamp(+cfg.rate || 1, .5, 4);
    v.muted = true; v.defaultMuted = true; v.loop = true; v.playsInline = true; v.autoplay = true;
    v.defaultPlaybackRate = RATE; v.playbackRate = RATE;
    let near = false;
    const play = () => {
      if (!near || document.hidden) return;
      if (v.preload !== 'auto') v.preload = 'auto';
      if (v.playbackRate !== RATE) v.playbackRate = RATE;
      const p = v.play(); if (p && p.catch) p.catch(() => { /* autoplay refused: the poster stays */ });
    };
    bag.on(v, 'loadedmetadata', () => { v.playbackRate = RATE; });
    bag.on(v, 'playing', () => v.classList.add('is-on'));
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(es => es.forEach(e => { near = e.isIntersecting; if (near) play(); else v.pause(); }), { rootMargin: '50% 0px' });
      io.observe(root); bag.add(() => io.disconnect());
    } else { near = true; play(); }
    bag.on(document, 'visibilitychange', () => { if (document.hidden) v.pause(); else play(); });
    return { destroy() { v.pause(); } };
  };

  /* ============================================================
     FINAL OFFER: the turning photos, seasonal line (a real date), cut-off
     ============================================================ */
  MODULES.offer = function (root, cfg, bag, inst) {
    const slot = $('[data-nx-slot]', root), cta = $('[data-nx-cta]', root), boxEl = $('[data-nx-buy]', root);
    const ph = slot ? photoStage(slot) : null;
    if (ph) bag.add(() => ph.destroy());
    const turn = ph ? turner(null, bag, { auto: true, dwell: 3400, y: f => (reduce ? 0 : Math.sin(f.time * 1.25) * 3) }) : null;
    if (cta) { ctas.add(cta); bag.add(() => ctas.delete(cta)); }
    // a full buy box, in step with every other one on the page
    let s = null, bb = null;
    if (boxEl && cfg.variants && cfg.variants.length) {
      s = storeFor(cfg); bag.add(() => releaseStore(s));
      bb = buyBox(boxEl, s, cfg, bag);
    }
    // in the 60 days before the EU clocks go back, the seasonal copy takes over (a real date)
    const sea = $$('[data-nx-season]', root), def = $$('[data-nx-default]', root);
    if (cfg.season && sea.length) {
      const now = new Date(), t = lastSundayOct(now.getUTCFullYear()), diff = t - now;
      if (diff > 0 && diff < 60 * 864e5) {
        const dayOf = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const days = Math.round((dayOf(t) - dayOf(now)) / 864e5);
        let date = t.toDateString();
        try { date = isHu(cfg.dateLocale) ? huDate(new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate(), 12)), { on: true }) : new Intl.DateTimeFormat(cfg.dateLocale || 'en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(t); } catch (e) { /* default */ }
        const when = days <= 0 ? (cfg.whenToday || 'Mától') : days === 1 ? (cfg.whenTomorrow || 'Holnaptól') : String(cfg.whenDays || '[days] nap múlva').replace('[days]', days);
        sea.forEach(el => { fillTokens(el, { date, days: String(days), when }); el.hidden = false; });
        def.forEach(el => { el.hidden = true; });
        // "arrives before the switch" only when the delivery window really ends before it
        const cutEl = $('[data-nx-cutoff]', root), before = $('[data-nx-season-before]', root);
        if (before) {
          const plan = cutEl ? shipPlanner(readCut(cutEl))(Date.now()) : null;
          before.hidden = !(plan && plan.to < t && readCut(cutEl).transitMax > 0);
        }
      } else {
        // 18.0: the HTML may already show the seasonal copy (Liquid decides it at render time): after the
        // switch, a cached page goes back to the default copy
        sea.forEach(el => { el.hidden = true; }); def.forEach(el => { el.hidden = false; });
      }
    }
    const tick = !bb && $('[data-nx-cutoff]', root) ? makeCutoff($('[data-nx-cutoff]', root)) : null;
    const tickNow = () => { if (document.hidden || inst.near === false) return; if (bb) bb.tick(Date.now()); else if (tick) tick(Date.now()); };
    tickNow(); bag.every(tickNow, 1000);
    let r = null;
    return {
      measure() { r = ph ? slot.getBoundingClientRect() : null; },
      frame(f) {
        if (!r || r.bottom < f.top || r.top > f.vh) return;
        let lk = cfg.lens === 'auto' || !cfg.lens ? (s ? keyIn(s, curOf(s)) : Still.lens) || 'dusk' : cfg.lens;
        if (lk === 'set' || lk === 'pair') { const L = s ? lensesOf(s, curOf(s)) : KEYS, n = Math.floor(f.now / 2600); lk = L[n % L.length]; if (!reduce) wakeAt((n + 1) * 2600 + 5); }   // the set or a pair: its lenses take turns
        ph.size(r.width, r.height);
        return turn.frame(f, ph, LENS[lk] ? lk : 'dusk') ? true : reduce ? '' : 'ambient';
      }
    };
  };

  /* ============================================================
     HOME HERO: the frame turns toward the mouse, the glint follows
     the cursor, the glow drifts, and the lens matches the visitor's hour
     ============================================================ */
  MODULES.hero = function (root, cfg, bag, inst) {
    const copy = $('[data-nx-copy]', root), slot = $('[data-nx-slot]', root), g1 = $('.nx-glow .g1', root), g2 = $('.nx-glow .g2', root);
    const timeEl = $('[data-nx-time]', root), btns = $$('.nx-hero-lenses button', root);
    const names = cfg.names || { day: 'Oslo', play: 'Lisbon', dusk: 'Havana' };
    const hourNow = () => { const d = new Date(); return d.getHours() + d.getMinutes() / 60; };
    let lens = LENS[cfg.lens] ? cfg.lens : lensAtHour(hourNow());
    let setLens = function (k) {
      lens = k;
      btns.forEach(b => { const on = b.getAttribute('data-v') === k; b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1; });
      root.style.setProperty('--glow', LENS[k].glow);
    };
    btns.forEach(b => bag.on(b, 'click', () => { setLens(b.getAttribute('data-v')); vib(8); }));
    bag.on(btns[0] && btns[0].parentElement, 'keydown', e => {
      const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key]; if (!dir) return;
      e.preventDefault(); const i = Math.max(0, btns.indexOf(document.activeElement)), n = btns[(i + dir + btns.length) % btns.length]; n.focus(); n.click();
    });
    setLens(lens);
    let lastMin = -1;
    function clock() {
      if (!timeEl) return;
      const d = new Date(), m = d.getHours() * 60 + d.getMinutes(); if (m === lastMin) return; lastMin = m;
      let t = pad(d.getHours()) + ':' + pad(d.getMinutes());
      try { t = new Intl.DateTimeFormat(locale(), { hour: '2-digit', minute: '2-digit' }).format(d); } catch (e) { /* 24 h */ }
      const k = lensAtHour(d.getHours() + d.getMinutes() / 60);
      fillTpl(timeEl, cfg.chip || '[time] nálad · [phase]', { time: t, lens: strongEl(names[k] || k), phase: strongEl((cfg.phases || {})[k] || names[k] || k) });
      const dot = $('.nx-chip i', root); if (dot) dot.style.background = LENS[k].glow;
    }
    clock();
    bag.every(() => { if (!document.hidden && inst.near) clock(); }, 1000);
    /* 14.0: the three studio photos lie on top of each other, lined up to the pixel, on transparent
       backgrounds. Changing lens is a true cross-fade: the new photo rises to full while the others go to
       none, added together (plus-lighter), so the frame stays solid and still all the way through and a
       fade interrupted halfway simply turns toward the new lens from where it is. A photo that has not
       loaded yet is waited for first. */
    const hv = $('[data-nx-hv]', root), imgs = hv ? $$('.nx-hv-img', hv) : [];
    let want = null, dead = false;
    bag.add(() => { dead = true; });
    function showLens(k) {
      const next = imgs.find(im => im.getAttribute('data-v') === k);
      want = next;
      if (!next) return;
      const go = () => {
        if (want !== next || dead) return;
        imgs.forEach(im => { im.style.opacity = im === next ? '1' : '0'; });
      };
      if (next.complete && next.naturalWidth) go();
      else if (next.decode) next.decode().then(go, go);
      else bag.on(next, 'load', go);
    }
    if (hv) {
      const lensName = k => names[k] || k;
      const base = hv.getAttribute('aria-label') || '';
      const label = k => { hv.setAttribute('aria-label', base + (base ? ', ' : '') + lensName(k) + ' ' + (cfg.lensWord || 'lencsével')); };
      const orig = setLens;
      setLens = k => { orig(k); showLens(k); label(k); };
      setLens(lens);
      bag.add(() => imgs.forEach(im => { im.style.opacity = ''; }));
    }
    const m = {}; let styled = false;
    const unstyle = () => { [copy, slot, g1, g2, hv].forEach(el => { if (el) { el.style.transform = ''; el.style.opacity = ''; } }); styled = false; };
    return {
      measure() { const r0 = root.getBoundingClientRect(); m.top = r0.top; m.bottom = r0.bottom; m.hh = root.offsetHeight; },
      frame(f) {
        const hh = m.hh, sy = -m.top;
        if (m.bottom < -60 || m.top > f.vh) return;
        if (reduce && styled) unstyle();                // reduced motion switched on: everything back in place
        if (reduce || !(sy > -f.vh && sy < hh + 60)) return;
        styled = true;
        if (copy) { copy.style.transform = 'translate3d(' + (-P.x * 6).toFixed(1) + 'px,' + (-Math.max(0, sy) * .16 - P.y * 4).toFixed(1) + 'px,0)'; copy.style.opacity = (1 - clamp(sy / (f.vh * .6)) * .85).toFixed(3); }
        if (slot) slot.style.transform = 'translate3d(0,' + (Math.max(0, sy) * .2).toFixed(1) + 'px,0)';
        // the frame: a few pixels toward the mouse, and a slow float
        if (hv) hv.style.transform = 'translate3d(' + (P.x * 8).toFixed(2) + 'px,' + (P.y * 5 + Math.sin(f.time * 1.25) * 3).toFixed(2) + 'px,0)';
        if (!f.lite) {                                  // phones: the glow stays put
          if (g1) g1.style.transform = 'translate3d(' + (P.x * 46).toFixed(1) + 'px,' + (P.y * 30 + Math.max(0, sy) * .28).toFixed(1) + 'px,0)';
          if (g2) g2.style.transform = 'translate3d(' + (-P.x * 70).toFixed(1) + 'px,' + (-P.y * 40 + Math.max(0, sy) * .1).toFixed(1) + 'px,0)';
        }
        return 'ambient';
      }
    };
  };


  /* ============================================================
     THE THREE LENSES: look through each lens at a late screen
     ============================================================ */
  MODULES.lenses = function (root, cfg, bag) {
    const peeks = $$('.nx-lt', root).map((el, i) => {
      const k = LENS[el.getAttribute('data-v')] ? el.getAttribute('data-v') : 'dusk', view = $('.nx-lt-view', el), lensEl = $('.nx-lt-lens', el), tint = $('.nx-lt-tint', el);
      if (!view || !lensEl) return null;
      const base = $('.nx-scn', view);
      if (base && !$('.nx-scn', lensEl)) { const c = cloneScene(base); c.style.filter = LENS[k].sim.f; lensEl.insertBefore(c, tint); }
      bag.on(view, 'dragstart', e => e.preventDefault());
      if (tint) tint.style.backgroundColor = LENS[k].sim.bg;
      const img = $('.nx-lt-photo', el);                // 15.0: the real frame (a photo cut-out)
      if (!img) return null;
      const t = { view, rig: photoRig(img, lensEl), i, x: 0, y: 0, tx: 0, ty: 0, over: false, last: -1e9 };
      pointTo(view, bag, (what, p, mouse) => {          // a mouse looks wherever it points; a finger taps or drags sideways
        if (what === 'move' || what === 'down') { t.tx = p[0]; t.ty = p[1]; t.over = true; }
        else if (what === 'leave' || !mouse) t.over = false;
        t.last = performance.now();
      });
      return t;
    }).filter(Boolean);
    return {
      measure(f) { for (const t of peeks) t.r = t.view.getBoundingClientRect(); },
      frame(f) {
        let hot = false, amb = false;
        for (const t of peeks) {
          const r = t.r; if (!r || r.bottom < 0 || r.top > f.vh || r.right < 0 || r.left > f.vw) continue;
          const w = r.width, h = r.height, fw = w * .64, sc = fw / 2.7;
          if (!t.x) { t.x = t.tx = w / 2; t.y = t.ty = h * .5; }
          const idle = !t.over && f.now - t.last > 900;
          if (idle) { const s = f.time; t.tx = w / 2 + (reduce ? 0 : Math.sin(s * .45 + t.i * 1.7) * w * .15); t.ty = h * .5 + (reduce ? 0 : Math.sin(s * .8 + t.i) * h * .08); if (!reduce) amb = true; }
          t.tx = clamp(t.tx, sc * .6, w - sc * .6); t.ty = clamp(t.ty, sc * .3, h - sc * .3);
          const k = t.over ? .24 : .06; t.x += (t.tx - t.x) * k; t.y += (t.ty - t.y) * k;
          if (!idle && Math.abs(t.tx - t.x) + Math.abs(t.ty - t.y) > .5) hot = true;   // idle drift is ambient, not hot
          t.rig.set(t.x, t.y, fw, w, h);                // transforms only
        }
        return hot ? true : amb ? 'ambient' : '';
      }
    };
  };

  /* ============================================================
     TECHNOLOGY: dark stage, the clear lens under a pool of light.
     15.0: the coating's violet light sweeps across the lenses on its own (touch screens, and on a
     desktop whenever the cursor is not over the section): left to right, eased, about 4.6 s a pass,
     a short rest off the lens, then again. A mouse over the section takes the light along instead.
     The soft glow behind the frame lives in the stage and stays on the lenses (14.0's followed the
     cursor across the whole full-bleed section and could be left far off to the side).
     Reduced motion: one still highlight, nothing moves.
     ============================================================ */
  MODULES.tech = function (root, cfg, bag, inst) {
    const slot = $('[data-nx-slot]', root), glow = $('[data-nx-glow]', root);
    // the light band's centre, as a share of the photo box, is -.252 + 1.512·x (see setSheen);
    // the lenses span about .13–.87 of the box, their middle sits at .49 of its height
    const SW = { pass: 4.6, rest: 1.3, a: .1, b: .9, lo: .253, hi: .742, still: .42 };
    const band = x => -.252 + 1.512 * x;
    const ph = slot ? photoStage(slot) : null, fd = fader(), K = pk('day', 'front', 0, 1);
    if (ph) bag.add(() => ph.destroy());
    // over: a mouse is over the section; t: the sweep's own clock; k: 0…1 hand-over back to the sweep
    const tp = { x: 0, y: 0, tx: 0, ty: 0, over: false, cx: SW.still, sx: reduce ? SW.still : SW.a, t: 0, k: 1, gx: .5, go: 0 };
    let r = null, gsz = '';
    bag.on(root, 'pointermove', e => {
      if (e.pointerType !== 'mouse' || reduce || !r || !ph || !ph.S.bw) return;
      const fx = (e.clientX - r.left - ph.S.bx) / ph.S.bw, fy = (e.clientY - r.top - ph.S.by) / ph.S.bh;
      tp.tx = clamp((fx - .5) * 2, -1, 1); tp.ty = clamp((fy - .5) * 2, -1, 1);
      tp.cx = clamp((fx + .252) / 1.512, SW.lo, SW.hi);      // the band sits under the cursor, on the lenses
      tp.over = true;
    });
    bag.on(root, 'pointerleave', () => {
      if (!tp.over) return;
      tp.over = false; tp.tx = tp.ty = 0;
      // the sweep picks up from where the light is, heading right, and eases in over 0.8 s
      const e = clamp((tp.sx - SW.a) / (SW.b - SW.a));
      tp.t = Math.acos(1 - 2 * e) / Math.PI * SW.pass; tp.k = 0;
    });
    const sine = t => (1 - Math.cos(Math.PI * t)) / 2;
    // where the sweep is at time t: the band's x, and the glow's place and strength
    function auto(t) {
      const P = SW.pass + SW.rest, u = t % P;
      if (u < SW.pass) { const x = lerp(SW.a, SW.b, sine(u / SW.pass)); return { x, rest: -1 }; }
      return { x: SW.b, rest: (u - SW.pass) / SW.rest };
    }
    return {
      measure() { r = ph ? slot.getBoundingClientRect() : null; },
      frame(f) {
        if (!r || r.bottom < f.top || r.top > f.vh) return;
        ph.size(r.width, r.height);
        fd.to(K);
        const busy = fd.step(f.dt, .7, k => ph.ready(k));
        const dt = f.dt;
        let gx, go, rest = -1;
        if (reduce) { tp.over = false; tp.sx = SW.still; tp.x = tp.y = 0; }
        else if (tp.over) {
          tp.sx += (tp.cx - tp.sx) * (1 - Math.exp(-dt / .12));
        } else {
          tp.t += dt;
          const A = auto(tp.t); rest = A.rest;
          if (tp.k < 1) { tp.k = Math.min(1, tp.k + dt / .8); tp.sx += (A.x - tp.sx) * (1 - Math.exp(-dt / Math.max(.001, .3 * (1 - tp.k)))); }
          else tp.sx = A.x;
        }
        const moving = Math.abs(tp.tx - tp.x) + Math.abs(tp.ty - tp.y) > .002 || (tp.over && Math.abs(tp.cx - tp.sx) > .001);
        tp.x += (tp.tx - tp.x) * .08; tp.y += (tp.ty - tp.y) * .08;
        // the glow behind the frame: under the light while it is on the lenses, dimmer and drifting back
        // to the left lens while the light rests off to the right
        const c = band(tp.sx), on = clamp(1 - Math.abs(c - .5) / .5);
        if (rest >= 0) { gx = lerp(.72, .28, sine(rest)); go = .32; }
        else { gx = clamp(c, .28, .72); go = .32 + .68 * smooth(on); }
        if (reduce) { gx = clamp(c, .28, .72); go = 1; }
        tp.gx += (gx - tp.gx) * (reduce ? 1 : 1 - Math.exp(-dt / .15)); tp.go += (go - tp.go) * (reduce ? 1 : 1 - Math.exp(-dt / .15));
        if (glow && ph.S.bw) {
          const d = ph.S.bw * .64, g = d.toFixed(0);
          if (g !== gsz) { gsz = g; glow.style.width = glow.style.height = g + 'px'; }
          glow.style.transform = 'translate3d(' + (ph.S.bx + tp.gx * ph.S.bw - d / 2).toFixed(1) + 'px,' + (ph.S.by + .49 * ph.S.bh - d / 2).toFixed(1) + 'px,0)';
          glow.style.opacity = tp.go.toFixed(3);
        }
        // the frame leans a little toward the cursor; the light crosses the lens with it, or on its own
        ph.draw(fd.list().map(e => ({ k: e.k, o: e.o, x: tp.x * 10, y: tp.y * 6 })), { o: .9, x: tp.sx });
        if (reduce) return moving || busy;
        return moving || busy ? true : tp.over ? '' : rest >= 0 ? 'spin' : 'glide';
      }
    };
  };

  /* comparison and FAQ need only the shared effects; the editor can open an answer */
  MODULES.compare = function () { return {}; };
  MODULES.faq = function () {
    return { select(el) { const q = el.classList.contains('nx-fq') ? $('.nx-fq-q', el) : null; if (q) toggleFaq(q, true); } };
  };


  /* ============================================================
     IN THE BOX: the four pieces fan out as the section scrolls in
     ============================================================ */
  MODULES.box = function (root, cfg, bag) {
    const stage = $('[data-nx-box]', root);
    if (!stage) return {};
    const cards = $$('.nx-ib', stage);
    let lastF = -1, r = null, at = '', D = [];
    // each card's spread comes from the stylesheet (--x, --y, --r per screen size), read once per size
    const num = (v, unit) => { v = String(v || '').trim(); const n = parseFloat(v) || 0; return /vw$/.test(v) ? n * VP.w / 100 : n; };
    bag.add(() => cards.forEach(c => { c.style.transform = ''; }));
    return {
      measure() {
        r = stage.getBoundingClientRect();
        if (at !== VP.w + 'x' + VP.h) { at = VP.w + 'x' + VP.h; lastF = -1; D = cards.map(c => { const cs = getComputedStyle(c); return [num(cs.getPropertyValue('--x')), num(cs.getPropertyValue('--y')), parseFloat(cs.getPropertyValue('--r')) || 0]; }); }
      },
      frame(f) {
        if (r.bottom < 0 || r.top > f.vh) return;
        const k = reduce ? 1 : smooth(clamp((f.vh * .95 - r.top) / (f.vh * .6)));   // phones lay the four out as a 2 × 2 grid (CSS)
        if (Math.abs(k - lastF) < .002) return; lastF = k;
        cards.forEach((c, i) => { const d = D[i] || [0, 0, 0]; c.style.transform = 'translate(-50%,-50%) translate3d(' + (d[0] * k).toFixed(1) + 'px,' + (d[1] * k).toFixed(1) + 'px,0) rotate(' + (d[2] * k).toFixed(2) + 'deg)'; });
      }
    };
  };

  /* ============================================================
     SAME SCREEN, SOFTER LIGHT: the glasses over a late screen;
     switch lenses (the choice follows the buy box).
     15.0: the glasses are the real frame (a cut-out of the studio
     front shot). A mouse drags them, as before. On touch screens
     they glide across the screen by themselves, slowly, left and
     right (eased), and the page scrolls over the demo as over any
     picture: the visitor only switches Oslo / Lisbon / Havana.
     Reduced motion: they rest in the middle.
     ============================================================ */
  MODULES.try = function (root, cfg, bag) {
    const el = $('[data-nx-sim]', root), lensEl = $('.nx-sim-lens', root), tint = $('.nx-sim-tint', root), img = $('.nx-sim-photo', root);
    const segBtns = $$('.nx-seg button', root), thumb = $('.nx-seg-thumb', root);
    const perEl = $('[data-nx-sim-per]', root), errEl = $('[data-nx-err]', root);
    if (!el || !lensEl || !img) return {};
    const base = $('.nx-sim-scene .nx-scn', el);
    if (base && !$('.nx-scn', lensEl)) lensEl.insertBefore(cloneScene(base), tint);
    const clone = $('.nx-scn', lensEl);
    const rig = photoRig(img, lensEl);
    const glide = !finePtr;                                  // touch screens: no dragging, the glasses move on their own
    el.classList.toggle('is-auto', glide);
    bag.add(() => el.classList.remove('is-auto'));
    const s = cfg.variants && cfg.variants.length ? storeFor(cfg) : null;
    if (s) bag.add(() => releaseStore(s));
    const names = cfg.names || { day: 'Oslo', play: 'Lisbon', dusk: 'Havana' }, desc = cfg.lensDesc || {};
    const btn = cartButton($('[data-nx-add]', root), cfg, () => cfg.addLabel || 'Kosárba — [price]');
    const sim = { k: s && !s.bundle && !s.pair && s.v && LENS[s.v.key] ? s.v.key : (LENS[cfg.lens] ? cfg.lens : 'dusk'), x: 0, y: 0, tx: 0, ty: 0, drag: false, last: -1e9, key: '' };
    // a product whose variants aren't Oslo / Lisbon / Havana (one variant, or sizes): the switch only
    // previews the tint, and the button adds the variant that is selected
    const forLens = k => (s ? variantByKey(s, k) || (s.V.some(v => LENS[v.key]) ? null : curOf(s)) : null);
    function paint() {
      const k = sim.k, L = LENS[k];
      segBtns.forEach((b, i) => { const on = b.getAttribute('data-v') === k; b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1; if (on && thumb) thumb.style.transform = 'translateX(' + (i * 100) + '%)'; });
      // 13.0, the glare scene: through the lens the glare is gone and the light is calm and warm
      const gl = clone && clone.classList.contains('nx-scn--glare');
      if (tint) tint.style.backgroundColor = gl ? L.sim.gbg : L.sim.bg;
      if (clone) clone.style.filter = gl ? L.sim.g : L.sim.f;
      const v = forLens(k);
      if (perEl) {
        perEl.hidden = !v || !cfg.perText;
        if (v && cfg.perText) fillTpl(perEl, cfg.perText, { lens: strongEl(names[k] || k), desc: desc[k] || '', per: formatSmall(perNight(v.price), cfg.moneyFormat) });
      }
      if (btn && s && !s.busy) btn.set('idle', v);
      if (btn && !s) btn.el.hidden = true;
    }
    function choose(k, fromStore) {
      if (!LENS[k]) return;
      sim.k = k; paint();
      if (!fromStore && s && !s.bundle && !s.pair) { const v = variantByKey(s, k); if (v && v !== s.v) selectIn(s, { v, bundle: false, pair: null }, root); }   // the page remembers the lens you tried (when it is a variant)
    }
    if (s) {
      const sub = from => { if (from === 'idle') { paint(); return; } if (from !== root && !s.bundle && !s.pair && s.v && s.v.key && s.v.key !== sim.k) choose(s.v.key, true); };
      s.subs.add(sub); bag.add(() => s.subs.delete(sub));
    }
    segBtns.forEach(b => bag.on(b, 'click', () => { choose(b.getAttribute('data-v')); vib(8); sim.last = performance.now(); }));
    bag.on(segBtns[0] && segBtns[0].parentElement, 'keydown', e => {
      const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key]; if (!dir) return;
      e.preventDefault();
      const i = Math.max(0, segBtns.indexOf(document.activeElement)), n = segBtns[(i + dir + segBtns.length) % segBtns.length];
      n.focus(); n.click();
    });
    if (btn && s) {
      bag.on(btn.el, 'click', () => runAdd(s, forLens(sim.k), cfg, btn, [btn], errEl, bag));
      ctas.add(btn.el); bag.add(() => ctas.delete(btn.el));        // the sticky bar steps aside while this button is on screen
    }
    // move the glasses: a mouse drags them (and a pen or finger on a computer with a mouse). On touch
    // screens nothing listens to the finger here, so a swipe over the demo simply scrolls the page.
    bag.on(el, 'dragstart', e => e.preventDefault());
    if (!glide) pointTo(el, bag, (what, p, mouse, pressed) => {
      if (what === 'down' || (what === 'move' && (!mouse || pressed))) { sim.drag = true; el.classList.add('is-used'); sim.tx = p[0]; sim.ty = p[1]; sim.last = performance.now(); }
      else if (what === 'end' || what === 'leave') { if (sim.drag) sim.last = performance.now(); sim.drag = false; }   // a mouse resting over it does not stop the idle drift
    });
    paint();
    let r = null, gt = 0;
    const GLIDE = 11;                                        // seconds for a full left-right-left loop
    return {
      measure() { r = el.getBoundingClientRect(); },
      frame(f) {
        if (r.bottom < 0 || r.top > f.vh) return;
        // the frame's width: phones leave room on both sides to glide; wide screens as before
        const fw = r.width < 640 ? r.width * (glide ? .74 : .9) : Math.min(r.width * .66, 820) * 1.04;
        const hx = fw / 2, hy = fw * FCUT.aspect / 2;
        if (!sim.x) { sim.x = sim.tx = r.width / 2; sim.y = sim.ty = r.height * .5; }
        if (glide) {
          // left and right across the screen, eased at each end (a cosine), a breath of vertical drift;
          // its own clock, so it never jumps when it comes back on screen
          if (!reduce) gt += f.dt;
          const ph = gt / GLIDE * Math.PI * 2, span = Math.max(0, r.width / 2 - hx * .9);
          sim.x = sim.tx = r.width / 2 - Math.cos(ph) * span;
          sim.y = sim.ty = r.height * .5 + Math.sin(ph * 2) * r.height * .025;
          rig.set(sim.x, sim.y, fw, r.width, r.height);
          return reduce ? '' : 'glide';
        }
        let amb = false;
        if (!sim.drag && f.now - sim.last > 4500 && !reduce) {
          const t = f.time; amb = true;
          sim.tx = r.width / 2 + Math.sin(t * .42) * r.width * (r.width < 640 ? .08 : .17);
          sim.ty = r.height * .5 + Math.sin(t * .84) * r.height * .1;
        }
        sim.tx = clamp(sim.tx, hx * .4, r.width - hx * .4); sim.ty = clamp(sim.ty, hy * .6, r.height - hy * .6);
        const k = sim.drag ? .35 : .08;
        sim.x += (sim.tx - sim.x) * k; sim.y += (sim.ty - sim.y) * k;
        const hot = sim.drag || (!amb && Math.abs(sim.tx - sim.x) + Math.abs(sim.ty - sim.y) > .5);   // idle drift is ambient
        rig.set(sim.x, sim.y, fw, r.width, r.height);   // transforms only
        return hot ? true : amb ? 'ambient' : '';
      }
    };
  };

  /* ============================================================
     HOW THE LENS FILTERS: a schematic of what reaches your eyes
     (illustrative shapes, labelled as such)
     ============================================================ */
  MODULES.how = function (root, cfg, bag) {
    const sv = $('[data-nx-sv]', root), segBtns = $$('.nx-seg button', root), thumb = $('.nx-seg-thumb', root);
    if (!sv) return {};
    const LAM = []; for (let l = 380; l <= 700; l += 2) LAM.push(l);
    const SX = l => 64 + (l - 380) / 320 * 904, SY = v => 262 - v * 206;
    const g2 = (l, m, w) => Math.exp(-Math.pow((l - m) / w, 2));
    const SPEC = LAM.map(l => Math.min(1, .96 * g2(l, 452, 12) + .6 * g2(l, 565, 60) + .1 * g2(l, 640, 40)));
    const TF = {
      none: LAM.map(() => 1),
      day: LAM.map(l => 1 - .3 * g2(l, 438, 22)),
      play: LAM.map(l => 1 - .7 / (1 + Math.exp((l - 478) / 9)) - .08 * g2(l, 438, 22)),
      dusk: LAM.map(l => 1 - .93 / (1 + Math.exp((l - 528) / 10)))
    };
    const st = { cur: TF.dusk.slice(), to: TF[cfg.lens] ? cfg.lens : 'dusk' };
    st.cur = TF[st.to].slice();
    const L = cfg.labels || {};
    function wl(l) {
      const stops = [[380, [120, 60, 200]], [420, [90, 70, 255]], [450, [40, 110, 255]], [490, [0, 200, 220]], [520, [40, 210, 90]], [570, [230, 225, 40]], [600, [255, 150, 30]], [650, [240, 60, 30]], [700, [180, 20, 20]]];
      for (let i = 0; i < stops.length - 1; i++) { const a = stops[i], b = stops[i + 1]; if (l <= b[0]) { const t = (l - a[0]) / (b[0] - a[0]); return 'rgb(' + a[1].map((v, j) => Math.round(lerp(v, b[1][j], t))).join(',') + ')'; } }
      return 'rgb(180,20,20)';
    }
    const esc = t => String(t || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    let stops = ''; for (let l = 380; l <= 700; l += 20) stops += '<stop offset="' + ((l - 380) / 320).toFixed(3) + '" stop-color="' + wl(l) + '"/>';
    let ticks = ''; for (let l = 400; l <= 700; l += 50) ticks += '<line class="ax" x1="' + SX(l) + '" x2="' + SX(l) + '" y1="262" y2="268"/><text x="' + SX(l) + '" y="304" text-anchor="middle">' + l + ' nm</text>';
    const uid = 'sv' + Math.random().toString(36).slice(2, 7);
    sv.innerHTML = '<defs><linearGradient id="' + uid + 'r" x1="0" x2="1">' + stops + '</linearGradient><pattern id="' + uid + 'h" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="7" height="7" fill="rgba(242,101,28,.10)"/><line x1="0" y1="0" x2="0" y2="7" stroke="#F2651C" stroke-width="2.2"/></pattern></defs>' +
      '<rect x="' + SX(400) + '" y="40" width="' + (SX(450) - SX(400)) + '" height="222" fill="rgba(110,90,255,.07)" rx="6"/>' +
      '<text x="' + ((SX(400) + SX(450)) / 2) + '" y="30" text-anchor="middle" class="sv-band">' + esc(L.band || 'Blue-violet band') + '</text>' +
      '<path data-cut fill="url(#' + uid + 'h)"/><path data-pass fill="#1D1D1F"/>' +
      '<path data-line fill="none" stroke="#1D1D1F" stroke-width="1.5" stroke-dasharray="4 4" opacity=".45"/>' +
      '<line class="ax" x1="64" x2="968" y1="262" y2="262"/>' + ticks +
      '<rect x="64" y="272" width="904" height="10" rx="5" fill="url(#' + uid + 'r)"/>' +
      '<g><line x1="' + SX(452) + '" x2="' + SX(452) + '" y1="' + (SY(.96) - 6) + '" y2="' + (SY(.96) - 26) + '" stroke="#1D1D1F" stroke-width="1"/><text x="' + (SX(452) + 8) + '" y="' + (SY(.96) - 18) + '">' + esc(L.peak || "Screen's blue peak") + '</text></g>';
    const cutP = $('[data-cut]', sv), passP = $('[data-pass]', sv), lineP = $('[data-line]', sv);
    const pathOf = vals => 'M' + LAM.map((l, i) => SX(l).toFixed(1) + ' ' + SY(vals[i]).toFixed(1)).join('L');
    function draw() {
      const pass = SPEC.map((v, i) => v * st.cur[i]);
      const top = pathOf(SPEC), bot = pathOf(pass);
      const back = LAM.slice().reverse().map((l, k) => { const i = LAM.length - 1 - k; return SX(l).toFixed(1) + ' ' + SY(pass[i]).toFixed(1); }).join('L');
      cutP.setAttribute('d', top + 'L' + back + 'Z');
      passP.setAttribute('d', bot + 'L' + SX(700) + ' ' + SY(0) + 'L' + SX(380) + ' ' + SY(0) + 'Z');
      lineP.setAttribute('d', top);
    }
    function set(k) {
      st.to = k;
      segBtns.forEach((b, i) => { const on = b.getAttribute('data-v') === k; b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1; if (on && thumb) thumb.style.transform = 'translateX(' + (i * 100) + '%)'; });
    }
    segBtns.forEach(b => bag.on(b, 'click', () => { set(b.getAttribute('data-v')); vib(6); }));
    bag.on(segBtns[0] && segBtns[0].parentElement, 'keydown', e => {
      const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key]; if (!dir) return;
      e.preventDefault(); const i = Math.max(0, segBtns.indexOf(document.activeElement)), n = segBtns[(i + dir + segBtns.length) % segBtns.length]; n.focus(); n.click();
    });
    set(st.to); draw();
    let r = null;
    return {
      measure() { r = sv.getBoundingClientRect(); },
      frame(f) {
        if (r.bottom < 0 || r.top > f.vh) return;
        const target = TF[st.to]; let moving = false;
        for (let i = 0; i < target.length; i++) { const d = target[i] - st.cur[i]; if (Math.abs(d) > .002) { st.cur[i] += d * (reduce ? 1 : .14); moving = true; } }
        if (moving) draw();
        return moving;
      }
    };
  };

  /* ============================================================
     CLOSE-UPS (product "lens material", home "small details").
     14.0, studio look: real photographs cut close from the studio
     shots, tied to the scroll the Apple way. As the section comes up
     the cards rise into place one after another, the photo in each
     eases in (slow zoom) and drifts a little against its card
     (parallax), and a soft band of light crosses the coated lens.
     Everything follows the scroll position (smoothed a touch), so
     scrolling back plays it backwards. Transform and opacity only.
     Positions come from the layout boxes (offsetTop), never from the
     moving cards, so nothing feeds back. Reduced motion and the theme
     editor: the photos stand still, in place.
     Photo look (12.0): a real photo of the frame, through a window
     that comes a little closer as the card scrolls up; the cards rise
     in as they come into view.
     ============================================================ */
  /* Photo look: each tile shows a window of one shot: [x, y] of the point to centre, the share of the
     photo's width that spans the tile, and where in the tile that point sits ([x, y], default the
     middle); the photo always covers the tile */
  const TILE = {
    hinge: { k: pk('day', 'hinge', 0, 0), win: [.64, .37, .56] },
    lens: { k: pk('day', 'front', 0, 1), win: [.735, .5, .6, .6, .38], sheen: true, glow: true },
    tex: { k: pk('play', 'side', 0, 0), win: [.3, .5, .75, .5, .42] }
  };
  function studioTiles(root, bag) {
    const box = $('.nx-dets', root);
    const figs = $$('.nx-det', root).filter(el => $('[data-nx-studio]', el));
    if (!box || !figs.length) return null;
    const still = reduce || designMode();
    const T = figs.map((fig, i) => ({
      fig, i, img: $('.nx-det-move', fig), sweep: $('.nx-det-sweep', fig),
      y: 0, h: 0, e: still ? 1 : 0, p: .5, te: 1, tp: .5, last: ''
    }));
    if (still) return { measure() {}, frame() { return false; } };
    T.forEach(t => { t.fig.classList.add('nx-det--live'); });
    bag.add(() => T.forEach(t => {
      t.fig.classList.remove('nx-det--live');
      t.fig.style.transform = t.fig.style.opacity = '';
      if (t.img) t.img.style.transform = '';
      if (t.sweep) t.sweep.style.removeProperty('--u'), t.sweep.style.opacity = '';
    }));
    /* where each card's own box starts, in the box of the grid (transforms do not change offsetTop) */
    const offs = el => { let y = 0, n = el; while (n && n !== box) { y += n.offsetTop; n = n.offsetParent; } return n === box ? y : el.offsetTop - box.offsetTop; };
    let first = true;
    return {
      measure(f) {
        const br = box.getBoundingClientRect();
        for (const t of T) { t.y = br.top + offs(t.fig); t.h = t.fig.offsetHeight; }
        const stag = Math.min(90, f.vh * .07);
        for (const t of T) {
          // rise-in: from the moment the card's place reaches the bottom of the screen, a little later
          // for each card beside an earlier one in the same row (the stagger; cards stacked on a phone
          // are staggered by the page itself), done within about a third of a screen
          const k = T.filter(u => u.i < t.i && Math.abs(u.y - t.y) < t.h * .6).length;
          t.te = smooth(clamp((f.vh * .98 - t.y - k * stag) / (f.vh * .3)));
          // the photo's travel: 0 as the card enters at the bottom, 1 as it leaves at the top
          t.tp = clamp((f.vh - t.y) / (f.vh + t.h));
        }
        if (first) { first = false; for (const t of T) { t.e = t.te; t.p = t.tp; } }
      },
      frame(f) {
        let hot = false;
        const k = 1 - Math.pow(.0009, f.dt);            // ≈ .11 a frame at 60 fps: smooth, never laggy
        for (const t of T) {
          if (t.y > f.vh * 1.6 || t.y + t.h < -f.vh * .6) continue;
          const de = t.te - t.e, dp = t.tp - t.p;
          if (Math.abs(de) > .0015) { t.e += de * k; hot = true; } else t.e = t.te;
          if (Math.abs(dp) > .0008) { t.p += dp * k; hot = true; } else t.p = t.tp;
          const e = t.e, p = t.p;
          const key = e.toFixed(3) + '|' + p.toFixed(4);
          if (key === t.last) continue;
          t.last = key;
          // the card: rises 56 px and turns from 0 to full; a faint scale-up makes it arrive, not slide
          t.fig.style.transform = e >= 1 ? '' : 'translate3d(0,' + ((1 - e) * 56).toFixed(2) + 'px,0) scale(' + (.965 + .035 * e).toFixed(4) + ')';
          t.fig.style.opacity = e >= 1 ? '' : (.0 + e).toFixed(3);
          // the photo: zooms in slowly and drifts against the card, slower than the page
          // (the drift never exceeds the margin the zoom gives, so no edge of the photo ever shows)
          if (t.img) t.img.style.transform = 'translate3d(0,' + ((.5 - p) * 20).toFixed(2) + 'px,0) scale(' + (1.07 + .08 * p).toFixed(4) + ')';
          // the light crossing the coated lens (masked to the lens opening): left to right as the card travels
          if (t.sweep) {
            const u = clamp((p - .2) / .5);
            t.sweep.style.setProperty('--u', u.toFixed(4));
            t.sweep.style.opacity = (Math.sin(u * Math.PI) * .9).toFixed(3);
          }
        }
        return hot;
      }
    };
  }
  MODULES.details = function (root, cfg, bag) {
    const studio = studioTiles(root, bag);
    const tiles = $$('[data-nx-tile]', root).map(el => {
      if (el.hasAttribute('data-nx-photo') || $('[data-nx-studio]', el)) return null;
      const slot = $('.nx-det-slot', el) || el;
      const sh = TILE[el.getAttribute('data-nx-tile')] || TILE.hinge;
      return { slot, sh, ph: photoStage(slot, { cover: true }), fd: fader(), r: null };
    }).filter(Boolean);
    bag.add(() => tiles.forEach(t => { if (t.ph) t.ph.destroy(); }));

    /* the reveal (photo look): cards that start below the screen rise in as they reach it, one after another */
    const cards = $$('.nx-det', root).filter(c => !$('[data-nx-studio]', c));
    if (cards.length && !reduce && !designMode() && 'IntersectionObserver' in window) {
      const vh = innerHeight, later = cards.filter(c => c.getBoundingClientRect().top > vh * .9);
      later.forEach(c => c.classList.add('nx-pre'));
      const io = new IntersectionObserver(es => {
        es.filter(e => e.isIntersecting).forEach((e, i) => {
          const c = e.target; io.unobserve(c);
          c.style.transitionDelay = (i * .15).toFixed(2) + 's';
          c.classList.remove('nx-pre');
          bag.later(() => { c.style.transitionDelay = ''; }, 1300 + i * 150);
        });
      }, { rootMargin: '0px 0px -8% 0px' });
      later.forEach(c => io.observe(c));
      bag.add(() => { io.disconnect(); cards.forEach(c => c.classList.remove('nx-pre')); });
    }
    return {
      measure(f) { if (studio) studio.measure(f); for (const t of tiles) t.r = t.slot.getBoundingClientRect(); },
      frame(f) {
        let any = false, hot = studio ? studio.frame(f) : false;
        for (const t of tiles) {
          const r = t.r; if (!r || r.bottom < -40 || r.top > f.vh + 40) continue;
          any = true;
          const lp = reduce || f.lite ? .5 : clamp((f.vh - r.top) / (f.vh + r.height));   // phones: no drift on scroll
          t.ph.size(r.width, r.height);
          t.fd.to(t.sh.k);
          if (t.fd.step(f.dt, .6, k => t.ph.ready(k))) hot = true;
          // the window, a little closer as the tile scrolls up; never so far that an edge of the photo shows
          const S = t.ph.S, w = r.width, h = r.height, wn = t.sh.win, tx = wn[3] == null ? .5 : wn[3], ty = wn[4] == null ? .5 : wn[4];
          const z = Math.max(w / (wn[2] * S.bw), h / S.bh) * (1 + (lp - .5) * .05);
          const mx = Math.max(0, (S.bw * z - w) / 2), my = Math.max(0, (S.bh * z - h) / 2);
          const x = clamp(-(wn[0] - .5) * S.bw * z + (tx - .5) * w + (.5 - lp) * 12, -mx, mx), y = clamp(-(wn[1] - .5) * S.bh * z + (ty - .5) * h, -my, my);
          t.ph.draw(t.fd.list().map(e => ({ k: e.k, o: e.o, x, y, s: z })), t.sh.sheen ? { o: .9, x: reduce ? .45 : .5 + .5 * Math.sin(f.time * .55) } : null);
          if (t.sh.glow) {
            const gx = Math.round((w / 2 + x + (wn[0] - .5) * S.bw * z) / w * 100), gy = Math.round((h / 2 + y + (wn[1] - .5) * S.bh * z) / h * 100);
            if (gx !== t.gx || gy !== t.gy) { t.gx = gx; t.gy = gy; t.slot.style.setProperty('--gx', gx + '%'); t.slot.style.setProperty('--gy', gy + '%'); }
          }
        }
        return hot ? true : any && !reduce && tiles.some(t => t.ph) ? 'ambient' : '';
      }
    };
  };

  /* ============================================================
     WHAT TIME IS IT WHERE YOU ARE? The card reads the visitor's
     clock; drag through the day and the sky, the lens and the
     photo follow (after dark, the frame on the night sky)
     ============================================================ */
  MODULES.hour = function (root, cfg, bag, inst) {
    const card = $('[data-nx-hour]', root), range = $('input[type="range"]', root), slot = $('[data-nx-slot]', root);
    if (!card || !range) return {};
    const q = sel => $(sel, root);
    const E = { clock: q('[data-nx-clock]'), now: q('[data-nx-now]'), name: q('[data-nx-name]'), why: q('[data-nx-why]'), cta: q('[data-nx-cta]'), reset: q('[data-nx-reset]') };
    const lenses = cfg.lenses || {};
    const DAY0 = 360;                                           // the scrubber runs 06:00 → 05:55 next morning
    const lensAt = m => lensAtHour((m / 60) % 24);   // 17.0: Oslo reggel, Lisbon délután, Havana este; 18.0: the shared rule, so 05:00–06:00 is Oslo here too, as in the hero
    const nowMin = () => { const d = new Date(); let m = d.getHours() * 60 + d.getMinutes(); if (m < DAY0) m += 1440; return Math.min(m, 1795); };
    const fmt = m => { const x = Math.round(m) % 1440; return pad(Math.floor(x / 60)) + ':' + pad(x % 60); };
    const relLum = hex => { const c = [1, 3, 5].map(i => { const v = parseInt(hex.slice(i, i + 2), 16) / 255; return v <= .03928 ? v / 12.92 : Math.pow((v + .055) / 1.055, 2.4); }); return .2126 * c[0] + .7152 * c[1] + .0722 * c[2]; };
    /* sky stops: [minute, top, bottom, darkness]. Light and dark switch within 15 minutes,
       so the text colour can always be one that reads well on the whole card */
    const SKY = [[360, '#dfe7f5', '#fbfbff', 0], [540, '#e7eefc', '#fbfbff', 0], [780, '#eef3ff', '#fbfbff', 0], [1020, '#ffe8c4', '#fff6ea', 0], [1140, '#ffc08a', '#ffe2c2', .05],
      [1200, '#eea38a', '#f8cba6', .12], [1215, '#5e4476', '#8a5b7a', .6], [1260, '#3b3160', '#5c4270', .85], [1320, '#1b1d3a', '#2e2548', .95], [1560, '#0b0d1c', '#141633', 1],
      [1680, '#1b2140', '#2b2f55', .9], [1770, '#3a3f6a', '#5d5f86', .65], [1785, '#e3d6dc', '#f4ebe8', .1], [1800, '#dfe7f5', '#fbfbff', 0]];
    function skyAt(m) {
      for (let i = 0; i < SKY.length - 1; i++) {
        const a = SKY[i], b = SKY[i + 1]; if (m > b[0]) continue;
        const u = clamp((m - a[0]) / (b[0] - a[0]));
        const t = (a[3] < .5) !== (b[3] < .5) ? (u < .5 ? 0 : 1) : smooth(u);
        return [hexMix(a[1], b[1], t), hexMix(a[2], b[2], t), lerp(a[3], b[3], t)];
      }
      return SKY[0].slice(1);
    }
    const H = { m: nowMin(), user: false, lens: null, dark: 0 };
    function setHour(m) {
      H.m = clamp(m, 360, 1795); range.value = H.m;
      const k = lensAt(H.m), L = lenses[k] || {};
      if (E.clock) E.clock.textContent = fmt(H.m);
      if (E.now) E.now.textContent = H.user ? (cfg.picked || 'Választott időpont') : (cfg.yourTime || 'Jelenlegi idő');
      if (E.name) E.name.textContent = L.phase || ((L.name || k) + ' ' + (cfg.hourWord || 'idő'));   // 11.0: "Nappali idő"
      card.style.setProperty('--hc', LENS[k].dot);
      if (H.lens !== k) {
        H.lens = k;
        if (E.why) E.why.textContent = L.why || '';
        if (E.cta) {
          let tpl = cfg.ctaText || 'Shop [lens] — [price]';
          if (!L.price) tpl = tpl.replace(/\s*[—–-]\s*\[price\]/, '').replace('[price]', '');   // no price known: no dangling dash
          fillTpl(E.cta, tpl, { lens: L.name || k, price: L.price || '' }); if (L.url) E.cta.setAttribute('href', L.url);
        }
      }
      const sky = skyAt(H.m);
      card.style.setProperty('--sky1', sky[0]); card.style.setProperty('--sky2', sky[1]);
      // text colour follows the real brightness of the sky behind it (WCAG luminance)
      const cr = (x, y) => (Math.max(x, y) + .05) / (Math.min(x, y) + .05), Lt = relLum(sky[0]), Lb = relLum(sky[1]);
      card.classList.toggle('is-light-text', Math.min(cr(.913, Lt), cr(.913, Lb)) > Math.min(cr(.0122, Lt), cr(.0122, Lb)));
      H.dark = sky[2];
      range.setAttribute('aria-valuetext', fmt(H.m) + ', ' + (L.phase || ((L.name || k) + ' ' + (cfg.hourWord || 'idő'))) + ' · ' + (L.name || k));
      if (E.reset) E.reset.hidden = !H.user;
    }
    bag.on(range, 'input', () => { H.user = true; setHour(+range.value); });
    bag.on(E.reset, 'click', () => { H.user = false; setHour(nowMin()); });
    setHour(H.m);
    const ph = slot ? photoStage(slot) : null, fd = fader();
    if (ph) bag.add(() => ph.destroy());
    let warm = false;
    let lastMin = -1, r = null;
    bag.every(() => { if (document.hidden || !inst.near) return; const m = nowMin(); if (m !== lastMin) { lastMin = m; if (!H.user) setHour(m); } }, 1000);
    return {
      measure() { r = ph ? slot.getBoundingClientRect() : null; },
      frame(f) {
        if (!r || r.bottom < f.top || r.top > f.vh) return;
        if (!warm) { warm = true; KEYS.forEach(k => { ph.want(pk(k, 'front', 0, 0)); }); }   // the slider can reach any lens
        ph.size(r.width, r.height);
        fd.to(pk(lensAt(H.m), 'front', 0, H.dark > .5 ? 1 : 0));
        const busy = fd.step(f.dt, reduce ? .12 : .45, k => ph.ready(k), crossDark);
        const y = reduce ? 0 : Math.sin(f.time * 1.25) * 3;
        ph.draw(fd.list().map(e => ({ k: e.k, o: e.o, y })));
        return busy ? true : reduce ? '' : 'ambient';
      }
    };
  };

  /* ============================================================
     THIRTY NIGHTS: the timeline fills in as it scrolls past
     ============================================================ */
  MODULES.nights = function (root) {
    const steps = $('[data-nx-steps]', root), items = steps ? $$('li', steps) : [];
    // the progress line is a real element scaled with a transform (the stylesheet's ::after is the no-script fallback)
    let bar = steps && $(':scope > .nx-steps-bar', steps);
    if (steps && !bar) { bar = document.createElement('span'); bar.className = 'nx-steps-bar'; bar.setAttribute('aria-hidden', 'true'); steps.appendChild(bar); }
    if (steps) { steps.classList.add('has-bar'); }
    let lastP = -1, r = null;
    return {
      measure() { r = steps ? steps.getBoundingClientRect() : null; },
      frame(f) {
        if (!r || r.bottom < 0 || r.top > f.vh) return;
        const p = reduce ? 1 : clamp((f.vh * .8 - r.top) / (r.height + f.vh * .25));
        if (Math.abs(p - lastP) < .002) return; lastP = p;
        bar.style.transform = (VP.w <= 760 ? 'scaleY(' : 'scaleX(') + p.toFixed(3) + ')';
        items.forEach((li, i) => li.classList.toggle('is-on', p > 0 && p >= i / Math.max(1, items.length - 1) * .98));
      }
    };
  };

  /* ============================================================
     WHERE WOULD YOU LIKE TO BEGIN? a soft light follows the mouse
     ============================================================ */
  MODULES.begin = function (root, cfg, bag) {
    if (finePtr && !reduce) $$('.nx-gcard', root).forEach(c => bag.on(c, 'pointermove', e => { const r = c.getBoundingClientRect(); c.style.setProperty('--mx', (e.clientX - r.left) + 'px'); c.style.setProperty('--my', (e.clientY - r.top) + 'px'); }));
    return {};
  };
  MODULES.worries = function () { return {}; };

  /* ============================================================
     SOCIAL PROOF: filter the reviews by the worry they answer, and
     "show more". Without the script every card simply shows.
     ============================================================ */
  MODULES.proof = function (root, cfg, bag) {
    const grid = $('.nx-pcards', root);
    if (!grid) return {};
    root.classList.add('is-js');
    bag.add(() => root.classList.remove('is-js'));
    const cards = $$('.nx-pcard', grid), chips = $$('.nx-pf [role="radio"]', root);
    const more = $('[data-nx-more]', root), live = $('[data-nx-proof-live]', root);
    const show = Math.max(1, +cfg.show || 6), names = cfg.topics || {};
    let topic = 'all', open = false;
    // the card row is a keyboard stop only while it actually scrolls (the phone carousel)
    function fit() {
      const scrolls = grid.scrollWidth > grid.clientWidth + 2;
      if (scrolls) grid.setAttribute('tabindex', '0'); else grid.removeAttribute('tabindex');
    }
    function apply() {
      let n = 0, cut = 0;
      cards.forEach(c => {
        const on = topic === 'all' || c.getAttribute('data-topic') === topic;
        c.hidden = !on;
        if (!on) return;
        n++;
        const extra = !open && n > show;
        c.classList.toggle('is-extra', extra);
        if (extra) cut++;
      });
      if (more) { more.hidden = !cut; more.setAttribute('aria-expanded', String(open)); }
      return n;
    }
    // new cards fade up on the compositor (Web Animations), no forced layout per card
    function rise(list) {
      if (reduce || !Element.prototype.animate) return;
      list.slice(0, 9).forEach((c, i) => { try { c.animate([{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'none' }], { duration: 520, delay: i * 45, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'backwards' }); } catch (e) { /* old browser */ } });
    }
    function pick(ch, focus) {
      chips.forEach(c => { const on = c === ch; c.setAttribute('aria-checked', String(on)); c.tabIndex = on ? 0 : -1; });
      if (focus) ch.focus();
      const was = topic; topic = ch.getAttribute('data-topic') || 'all';
      if (was === topic) return;
      const n = apply();
      grid.scrollLeft = 0;
      fit();
      rise(cards.filter(c => !c.hidden && !c.classList.contains('is-extra')));
      if (live) fillTpl(live, cfg.countText || '[count]', { count: n, topic: names[topic] || '' });
    }
    chips.forEach((ch, i) => {
      bag.on(ch, 'click', () => pick(ch));
      bag.on(ch, 'keydown', e => {
        const k = e.key, L = chips.length;
        const j = k === 'ArrowRight' || k === 'ArrowDown' ? (i + 1) % L : k === 'ArrowLeft' || k === 'ArrowUp' ? (i - 1 + L) % L : k === 'Home' ? 0 : k === 'End' ? L - 1 : -1;
        if (j < 0) return;
        e.preventDefault(); e.stopPropagation(); pick(chips[j], true);
      });
    });
    if (more) bag.on(more, 'click', () => {
      const fresh = cards.filter(c => !c.hidden && c.classList.contains('is-extra'));
      open = true; apply(); fit();
      rise(fresh);
      if (fresh[0]) fresh[0].focus();          // focus moves to the first new review, not back to the top
    });
    apply();
    if ('ResizeObserver' in window) bag.watch(new ResizeObserver(fit)).observe(grid); else fit();
    return {
      select(el) {
        if (!el.classList || !el.classList.contains('nx-pcard')) return;
        if (el.hidden) { if (chips[0]) pick(chips[0]); else { topic = 'all'; apply(); } }
        if (el.classList.contains('is-extra')) { open = true; apply(); }
        el.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduce ? 'auto' : 'smooth' });
      }
    };
  };

  /* ============================================================
     CUSTOMER PHOTOS (11.0): the row scrolls inside its own box.
     Arrows (mouse only) step by whole tiles, a thin bar shows how
     far along you are, and a photo's lens chip picks that lens in
     the buy box on this page (18.0: "Full Day" picks the set).
     Without the script the row still swipes and the chips open the
     product with the lens picked.
     ============================================================ */
  MODULES.ugc = function (root, cfg, bag) {
    const strip = $('.nx-ugc-strip', root);
    if (!strip) return {};
    const arrows = $('.nx-ugc-arrows', root), prev = $('[data-nx-ugc="prev"]', root), next = $('[data-nx-ugc="next"]', root);
    const thumb = $('.nx-ugc-bar i', root);
    let raf = 0, over = false;
    // reads first, then writes, once per frame at most
    function update() {
      raf = 0;
      const max = strip.scrollWidth - strip.clientWidth, x = strip.scrollLeft;
      const scrolls = max > 4;
      if (scrolls !== over) {
        over = scrolls;
        root.classList.toggle('is-scroll', scrolls);
        if (scrolls) strip.setAttribute('tabindex', '0'); else strip.removeAttribute('tabindex');   // a keyboard stop only when there is something to scroll
        if (arrows) arrows.hidden = !(scrolls && finePtr);
      }
      if (prev) prev.disabled = x <= 4;
      if (next) next.disabled = x >= max - 4;
      if (thumb && scrolls) {
        const k = Math.max(.08, Math.min(1, strip.clientWidth / strip.scrollWidth)), t = max ? x / max : 0;
        thumb.style.transform = 'translateX(' + (t * (1 - k) * 100).toFixed(2) + '%) scaleX(' + k.toFixed(4) + ')';
      }
    }
    const soon = () => { if (!raf) raf = requestAnimationFrame(update); };
    bag.add(() => { if (raf) cancelAnimationFrame(raf); });
    bag.on(strip, 'scroll', soon, { passive: true });
    if ('ResizeObserver' in window) bag.watch(new ResizeObserver(soon)).observe(strip); else bag.on(window, 'resize', soon);
    // one press moves by as many whole tiles as fit on screen
    function step(dir) {
      const tile = $('.nx-ugc-tile', strip); if (!tile) return;
      const gap = parseFloat(getComputedStyle(strip).columnGap) || 16, w = tile.getBoundingClientRect().width + gap;
      const n = Math.max(1, Math.floor((strip.clientWidth - gap) / w));
      strip.scrollBy({ left: dir * n * w, behavior: reduce ? 'auto' : 'smooth' });
    }
    if (prev) bag.on(prev, 'click', () => step(-1));
    if (next) bag.on(next, 'click', () => step(1));
    // "Ezt választom": pick the lens in a buy box on this page, then glide to it
    $$('[data-nx-lens]', root).forEach(a => bag.on(a, 'click', e => {
      const k = a.getAttribute('data-nx-lens');
      // 18.0: "Full Day" (a photo with all three lenses) picks the set
      const s = Object.values(Stores).find(x => (k === 'set' ? !!x.B : variantByKey(x, k)));
      if (!s) return;                                           // no buy box here: the link opens the product page
      e.preventDefault();
      if (k === 'set') selectIn(s, { bundle: true, pair: null }, root);
      else { const v = variantByKey(s, k); if (v) selectIn(s, { v, bundle: false, pair: null }, root); }
      vib(8);
      // the nearest buy box: the one at the top of the product page or the final offer
      const here = pageY(), boxes = $$('.nx-pdp-buy, [data-nocta="offer"] .nx-offer-card').filter(b => b.offsetParent !== null);
      const box = boxes.sort((a, b) => Math.abs(pageTop(a) - here) - Math.abs(pageTop(b) - here))[0];
      if (box) glideTo(pageTop(box) - topInset - 16);
    }));
    update();
    return {
      // theme editor: clicking a photo block brings its tile into view
      select(el) {
        if (!el.classList || !el.classList.contains('nx-ugc-tile')) return;
        strip.scrollTo({ left: el.offsetLeft - strip.offsetLeft - parseFloat(getComputedStyle(strip).paddingLeft || 0), behavior: reduce ? 'auto' : 'smooth' });
      }
    };
  };

  /* ============================================================
     SIGNATURE (14.0; 17.0: several pen strokes): the brand name, written
     by hand, at the end of the page. Each SVG path has pathLength="1": a dash of 1 whose
     offset goes from 1 to 0 draws it from the first point to the last
     (CSS does the drawing; the easing and the time are in the CSS and
     the section's settings). It starts when the signature is well in
     view, once. Reduced motion and the theme editor: drawn from the
     start.
     ============================================================ */
  MODULES.sign = function (root, cfg, bag) {
    const svg = $('[data-nx-sign]', root);
    if (!svg || reduce || designMode() || !('IntersectionObserver' in window)) return {};
    /* 17.0: one <path> per pen stroke (18.0: the word in one stroke, then the t's bar and the i's dot). Each gets its share of the writing time by its length, and
       starts when the one before it has finished, after a short lift of the pen. */
    const paths = $$('path', svg), dur = parseFloat(getComputedStyle(svg).getPropertyValue('--nx-sign-dur')) || 2.8;
    if (paths.length > 1) {
      const len = paths.map(p => { try { return p.getTotalLength(); } catch (e) { return 1; } });
      const lift = .14, total = len.reduce((a, b) => a + b, 0) || 1, ink = Math.max(.6, dur - lift * (paths.length - 1));
      let at = 0;
      paths.forEach((p, i) => {
        const t = Math.max(i ? .18 : .6, ink * len[i] / total);   // 18.0: the t's bar and the i's dot are short, but still visibly written
        p.style.setProperty('--nx-sign-t', t.toFixed(3) + 's'); p.style.setProperty('--nx-sign-dl', at.toFixed(3) + 's');
        at += t + lift;
      });
    }
    root.classList.add('nx-sign--ready');
    const io = new IntersectionObserver(es => {
      if (!es.some(e => e.isIntersecting)) return;
      io.disconnect();
      svg.getBoundingClientRect();                    // the hidden state is on screen first, then the stroke runs
      requestAnimationFrame(() => root.classList.add('nx-sign--draw'));
    }, { threshold: .55 });
    io.observe(svg);
    bag.add(() => { io.disconnect(); root.classList.remove('nx-sign--ready', 'nx-sign--draw'); });
    return {};
  };

  /* ============================================================
     SCRATCH CARD: 5% off, once per visitor (14.0; 15.0 rework).
     It appears when the page has been scrolled to the set share (about
     a quarter), never in the first seconds, never while the cart sheet
     or any other pop-up is open, never in the theme editor by itself.
     "Once" is remembered in the browser (localStorage); where that is
     blocked, once per page load.
     The foil is a canvas: matte silver with a fine grain and the hint.
     Mouse, finger and pen scratch it (pointer events; the page does not
     scroll while scratching: touch-action none) with a 28 px brush. The
     code is only under the foil, and "Kód másolása" stays locked until
     three quarters of it are off; then the rest fades, the code settles
     with a small spring and the button wakes up. Copying shows
     "Kimásolva ✓" for 2 s.
     Keyboard and screen readers: the foil is in the Tab order; holding
     Space, Enter or an arrow key scratches it along rows, bit by bit,
     and the share already off is read out every tenth. Nothing extra
     shows for mouse and touch. Esc, × and a tap on the blurred page
     close it; Tab stays inside; the focus goes back where it was. On
     phones it is a sheet from the bottom that can also be swiped down.
     ============================================================ */
  MODULES.scratch = function (root, cfg, bag) {
    const wrap = $('[data-nx-sc]', root), card = wrap && $('[data-nx-sc-card]', wrap);
    if (!wrap || !card) return {};
    const edit = designMode();
    const KEY = String(cfg.key || 'nx-scratch-1');
    const UNLOCK = clamp((+cfg.unlock || 75) / 100, .4, .9);
    const seen = () => { try { return localStorage.getItem(KEY) === '1'; } catch (e) { return false; } };
    const mark = () => { try { localStorage.setItem(KEY, '1'); } catch (e) { /* blocked: once per page load */ } };
    const q = sel => $(sel, card);
    const foil = q('[data-nx-sc-foil]'), field = q('[data-nx-sc-field]'), prize = q('[data-nx-sc-prize]');
    const live = q('[data-nx-sc-live]'), copyBtn = q('[data-nx-sc-copy]'), copyT = q('[data-nx-sc-copy-t]') || copyBtn;
    const ctx = foil && foil.getContext ? foil.getContext('2d') : null;
    const sheetQ = mq('(max-width: 600px)');
    const t0 = performance.now();
    let shown = false, open = false, revealed = false, back = null, touched = false;
    const progTxt = p => String(cfg.progress || '[percent]% lekaparva').replace('[percent]', p);
    const say = t => { if (live) { live.textContent = ''; requestAnimationFrame(() => { live.textContent = t; }); } };

    /* ---------- the foil ---------- */
    let W = 0, H = 0;
    const probe = document.createElement('canvas'), pc = probe.getContext('2d', { willReadFrequently: true });
    function paintFoil() {
      if (!ctx || revealed) return;
      const r = field.getBoundingClientRect(), dpr = Math.min(2, window.devicePixelRatio || 1);
      const w = Math.round(r.width * dpr), h = Math.round(r.height * dpr);
      if (!w || !h || (touched && foil.width)) return;     // once scratched, it keeps its marks
      if (w === W && h === H && foil.width === w) return;
      W = foil.width = w; H = foil.height = h;
      ctx.globalCompositeOperation = 'source-over';
      const g = ctx.createLinearGradient(0, 0, W, H);
      g.addColorStop(0, '#c3c6cb'); g.addColorStop(.42, '#dcdee2'); g.addColorStop(.5, '#e4e6e9'); g.addColorStop(.58, '#d4d7db'); g.addColorStop(1, '#b6b9bf');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      // the grain: a small tile of random greys, one device pixel each
      const n = document.createElement('canvas'); n.width = n.height = 128;
      const nc = n.getContext('2d'), im = nc.createImageData(128, 128);
      for (let i = 0; i < im.data.length; i += 4) { const v = 120 + Math.random() * 110; im.data[i] = im.data[i + 1] = im.data[i + 2] = v; im.data[i + 3] = 30; }
      nc.putImageData(im, 0, 0);
      ctx.fillStyle = ctx.createPattern(n, 'repeat'); ctx.fillRect(0, 0, W, H);
      // the hint, pressed into the foil
      const fam = getComputedStyle(card).fontFamily || 'sans-serif', fs = Math.round(Math.min(18, Math.max(15, r.width / 20)) * dpr);
      ctx.font = '600 ' + fs + 'px ' + fam; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      try { ctx.letterSpacing = (.04 * fs).toFixed(1) + 'px'; } catch (e) { /* older browsers */ }
      const hint = cfg.hint == null ? 'Kapard le' : String(cfg.hint);
      if (hint) {
        ctx.fillStyle = 'rgba(255,255,255,.55)'; ctx.fillText(hint, W / 2, H / 2 + dpr);
        ctx.fillStyle = '#676a71'; ctx.fillText(hint, W / 2, H / 2);
      }
      probe.width = 48; probe.height = Math.max(8, Math.round(48 * H / W));
    }
    function cleared() {
      const pw = probe.width, ph = probe.height;
      pc.clearRect(0, 0, pw, ph); pc.drawImage(foil, 0, 0, pw, ph);
      const d = pc.getImageData(0, 0, pw, ph).data; let c = 0;
      for (let i = 3; i < d.length; i += 4) if (d[i] < 140) c++;
      return c / (pw * ph);
    }
    let said = 0;
    function progress() {
      const c = cleared(), pct = Math.min(100, Math.round(c * 100));
      foil.setAttribute('aria-valuenow', pct); foil.setAttribute('aria-valuetext', progTxt(pct));
      const step = Math.floor(pct / 10) * 10;
      if (step > said && document.activeElement === foil) { said = step; say(progTxt(step)); }
      if (c >= UNLOCK) reveal();
    }
    let drawing = null, lastCheck = 0, kbKey = () => false;
    const at = e => { const r = foil.getBoundingClientRect(); return [(e.clientX - r.left) * (foil.width / r.width), (e.clientY - r.top) * (foil.height / r.height)]; };
    const scale = () => foil.width / Math.max(1, foil.getBoundingClientRect().width);
    function stroke(a, b) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = 28 * scale(); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0] + .01, b[1]); ctx.stroke();
    }
    if (ctx) {
      bag.on(foil, 'pointerdown', e => {
        if (revealed || (e.pointerType === 'mouse' && e.button !== 0)) return;
        e.preventDefault();
        drawing = { id: e.pointerId, p: at(e) }; touched = true;
        try { foil.setPointerCapture(e.pointerId); } catch (err) { /* fine without */ }
        stroke(drawing.p, drawing.p);
      });
      bag.on(foil, 'pointermove', e => {
        if (!drawing || e.pointerId !== drawing.id) return;
        e.preventDefault();
        const list = (e.getCoalescedEvents && e.getCoalescedEvents()) || [];
        for (const ev of (list.length ? list : [e])) { const p = at(ev); stroke(drawing.p, p); drawing.p = p; }
        const now = performance.now();
        if (now - lastCheck > 120) { lastCheck = now; progress(); }
      });
      const up = e => { if (!drawing || (e && e.pointerId !== drawing.id)) return; drawing = null; if (!revealed) progress(); };
      bag.on(foil, 'pointerup', up); bag.on(foil, 'pointercancel', up); bag.on(foil, 'lostpointercapture', up);
      /* the keyboard: a held key scratches along rows across the foil, left to right, then back */
      const kb = { x: 0, row: 0, dir: 1, n: 0 };
      bag.on(foil, 'pointerdown', () => { kb.used = false; });
      bag.on(foil, 'keydown', e => {
        if (revealed || [' ', 'Spacebar', 'Enter', 'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].indexOf(e.key) < 0) return;
        e.preventDefault(); touched = true; kb.used = true;
        const k = scale(), step = 22 * k, gap = 22 * k, rows = Math.max(1, Math.ceil(foil.height / gap));
        const y = Math.min(foil.height - 6 * k, 10 * k + kb.row * gap);
        const a = [kb.x, y];
        kb.x += step * kb.dir;
        if (kb.x > foil.width || kb.x < 0) { kb.x = clamp(kb.x, 0, foil.width); kb.row = (kb.row + 1) % rows; kb.dir = -kb.dir; }
        stroke(a, [kb.x, Math.min(foil.height - 6 * k, 10 * k + kb.row * gap)]);
        if (++kb.n % 4 === 0) progress();
      });
      bag.on(foil, 'keyup', e => { if (!revealed && [' ', 'Enter'].indexOf(e.key) >= 0) progress(); });
      kbKey = () => !!kb.used;
    }

    /* ---------- reveal, copy ---------- */
    function reveal() {
      if (revealed) return;
      revealed = true; drawing = null;
      // the keyboard way: focus moves on to the button (with its ring). A finger or the mouse: the focus
      // rests on the card, so nothing new lights up and nothing is lost when the foil goes
      const kbd = document.activeElement === foil && kbKey();
      wrap.classList.add('is-revealed');
      if (prize) prize.removeAttribute('aria-hidden');
      foil.setAttribute('aria-valuenow', 100); foil.setAttribute('aria-valuetext', progTxt(100));
      copyBtn.disabled = false; copyBtn.removeAttribute('aria-describedby');
      say(cfg.live || '');
      bag.later(() => { foil.hidden = true; }, reduce ? 0 : 420);
      if (kbd) copyBtn.focus({ preventScroll: true });
      else if (open && (!card.contains(document.activeElement) || document.activeElement === foil)) card.focus({ preventScroll: true });
      vib(12);
    }
    let copyT0 = null;
    bag.on(copyBtn, 'click', async () => {
      if (!revealed) return;
      const code = String(cfg.code || ''); let ok = false;
      try { if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(code); ok = true; } } catch (e) { ok = false; }
      if (!ok) {
        try {
          const ta = document.createElement('textarea'); ta.value = code; ta.setAttribute('readonly', ''); ta.style.cssText = 'position:fixed;left:0;top:0;opacity:0;font-size:16px';
          card.appendChild(ta); ta.select(); ok = document.execCommand('copy'); ta.remove(); copyBtn.focus({ preventScroll: true });
        } catch (e) { ok = false; }
      }
      if (copyT0 == null) copyT0 = copyT.textContent;
      copyT.textContent = ok ? (cfg.copied || 'Kimásolva ✓') : (cfg.copyFail || copyT0);
      copyBtn.classList.toggle('is-done', ok);
      if (!ok) { const b = q('[data-nx-sc-code]'); if (b) { const r = document.createRange(); r.selectNodeContents(b); const sel = getSelection(); sel.removeAllRanges(); sel.addRange(r); } }
      say(copyT.textContent);
      bag.later(() => { copyT.textContent = copyT0; copyBtn.classList.remove('is-done'); }, 2000);
    });

    /* ---------- open, close ---------- */
    let ported = false;
    function show(force) {
      if (open) return;
      if (!ported) { ported = true; portal(bag, 'scratch', [wrap]); }
      back = document.activeElement;
      wrap.hidden = false; open = true; Still.locked = true; wake();     // the sticky bar steps aside
      html.classList.add('nx-sc-open');
      if (!force) { shown = true; mark(); }
      paintFoil();
      wrap.getBoundingClientRect();
      requestAnimationFrame(() => wrap.classList.add('is-on'));
      card.focus({ preventScroll: true });                   // the dialog itself: read out by name, no ring; Tab goes on to its buttons
    }
    function close() {
      if (!open) return;
      open = false; Still.locked = false; dragEnd(null, true); wake();
      wrap.classList.remove('is-on'); html.classList.remove('nx-sc-open');
      bag.later(() => { if (!open) wrap.hidden = true; }, reduce ? 0 : 460);
      if (back && back.focus && document.contains(back)) { try { back.focus({ preventScroll: true }); } catch (e) { back.focus(); } }
    }
    $$('[data-nx-sc-close]', wrap).forEach(b => bag.on(b, 'click', close));
    bag.on(document, 'keydown', e => {
      if (!open) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      const f = $$('button:not([disabled]), a[href], [tabindex="0"]', card).filter(el => !el.hidden && !el.closest('[hidden]') && el.getClientRects().length);
      if (!f.length) return;
      const i = f.indexOf(document.activeElement);
      if (e.shiftKey && i <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && (i < 0 || i === f.length - 1)) { e.preventDefault(); f[0].focus(); }
    });
    // the page behind holds still (the card itself may scroll on a very short screen)
    const hold = e => {
      if (!open) return;
      const sc = card.scrollHeight > card.clientHeight + 1 && card.contains(e.target) && !(e.target.closest && e.target.closest('canvas'));
      if (!sc && e.cancelable) e.preventDefault();
    };
    bag.on(wrap, 'wheel', hold, { passive: false });
    bag.on(wrap, 'touchmove', hold, { passive: false });
    bag.on(window, 'resize', () => { if (open) paintFoil(); });

    /* ---------- phones: swipe the sheet down to close ---------- */
    let drag = null;
    function dragEnd(e, cancel) {
      if (!drag) return;
      const d = drag; drag = null;
      card.style.transition = '';
      const a = d.t[0], b = d.t[d.t.length - 1], v = (b[1] - a[1]) / Math.max(1, b[0] - a[0]) * 1000;
      card.style.transform = '';
      if (!cancel && (d.dy > 110 || (v > 650 && d.dy > 24))) close();
    }
    bag.on(card, 'pointerdown', e => {
      if (!sheetQ.matches || drag || (e.target.closest && e.target.closest('canvas, button, a, [data-nx-sc-code]'))) return;
      drag = { id: e.pointerId, y0: e.clientY, dy: 0, t: [[performance.now(), e.clientY]] };
      try { card.setPointerCapture(e.pointerId); } catch (err) { /* fine */ }
      card.style.transition = 'none';
    });
    bag.on(card, 'pointermove', e => {
      if (!drag || e.pointerId !== drag.id) return;
      let dy = e.clientY - drag.y0;
      if (dy < 0) dy = -(-dy * 80 * .55) / (80 + .55 * -dy);             // upwards it resists
      drag.dy = dy; card.style.transform = 'translate3d(0,' + dy.toFixed(1) + 'px,0)';
      drag.t.push([performance.now(), e.clientY]); if (drag.t.length > 6) drag.t.shift();
    });
    bag.on(card, 'pointerup', e => dragEnd(e)); bag.on(card, 'pointercancel', e => dragEnd(e, true));

    /* ---------- when ---------- */
    function scrolled() { const span = pageH() - innerHeight; return span > 0 ? pageY() / span : 0; }
    function check() {
      if (shown || open || edit || document.hidden) return;
      if (performance.now() - t0 < (cfg.delay == null ? 5 : +cfg.delay) * 1000) return;
      if (Still.sheetOpen || Still.locked || Still.codOpened || overlayOpen()) return;   // 16.0: not over the Releasit order form either
      if (scrolled() * 100 < (+cfg.trigger || 25)) return;
      show(false);
    }
    if (edit) {
      // the theme editor: a note in the page, the card opens when the section is selected (or by its button)
      const note = $('[data-nx-sc-edit]', root);
      if (note) { note.hidden = false; bag.on($('[data-nx-sc-open]', note), 'click', () => show(true)); }
      bag.on(document, 'shopify:section:select', e => { if (e.target && e.target.contains(root)) show(true); });
      bag.on(document, 'shopify:section:deselect', e => { if (e.target && e.target.contains(root)) close(); });
    } else if (!seen()) {
      let raf = 0;
      bag.on(document, 'scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; check(); }); }, { passive: true, capture: true });
      bag.every(check, 1000);
    }
    Still.scratch = { show: () => show(true), close };       // for the preview and for testing
    bag.add(() => { if (open) { Still.locked = false; html.classList.remove('nx-sc-open'); } open = false; });
    return {};
  };
  /* ============================================================
     17.0 · EGY NAP, HÁROM VÁROS (STILL · Egy nap): the screen stays
     while the page scrolls past; the scroll position picks the city.
     Each city holds for a while, then the next one cross-fades in;
     scrolling up runs it all back. Only opacity and transform move.
     Reduced motion (or no script): three still panels (CSS).
     ============================================================ */
  MODULES.cities = function (root, cfg, bag) {
    const track = $('[data-nx-city-track]', root), stage = $('[data-nx-city-stage]', root);
    if (!track || !stage) return {};
    const N = 3, idx = [0, 1, 2];
    const pick = sel => idx.map(i => $(sel + '[data-c="' + i + '"]', root));
    const sc = pick('.nx-city-sc'), gl = pick('.nx-city-gl'), tx = pick('.nx-city-tx'), bars = pick('.nx-city-bar span');
    const lay = sc.map(el => (el ? { far: $('.nx-city-far', el), near: $('.nx-city-near', el), mv: $('.nx-city-mv', el) } : {}));
    const fill = $('.nx-city-fill', root);
    const hands = $$('[data-nx-hand]', root).map(h => ({ el: h, h: h.getAttribute('data-nx-hand') === 'h', rest: h.style.transform }));
    const MOVE = [1, 1, -1];                       // the boat and the tram go right, the car drives left
    /* the button: the Havana lens of the page's product (the Releasit form, or the bag) */
    const s = cfg.variants && cfg.variants.length ? storeFor(cfg) : null;
    if (s) bag.add(() => releaseStore(s));
    const btn = cartButton($('[data-nx-add]', root), cfg, () => cfg.addLabel || 'Megrendelem — [price]'), errEl = $('[data-nx-err]', root);
    const lensV = () => (s ? variantByKey(s, 'dusk') || curOf(s) : null);
    if (btn) {
      if (!s) btn.el.hidden = true;
      else {
        btn.set('idle', lensV());
        bag.on(btn.el, 'click', () => runAdd(s, lensV(), cfg, btn, [btn], errEl, bag));
        ctas.add(btn.el); bag.add(() => ctas.delete(btn.el));      // the sticky bar steps aside while it is on screen
      }
    }
    /* scroll share p (0…1) → the city on show, t (0…2): each city holds for a fifth, the cross-fades take a fifth */
    const HOLD = .2, FADE = .2;
    const smooth = x => x * x * (3 - 2 * x);
    function cityAt(p) {
      let a = 0;
      for (let i = 0; i < N; i++) {
        if (p <= a + HOLD || i === N - 1) return i;
        a += HOLD;
        if (p < a + FADE) return i + smooth((p - a) / FADE);
        a += FADE;
      }
      return N - 1;
    }
    // the small clocks run from 09:00 to 20:30 as the page scrolls (minutes of the day)
    const MIN = [540, 960, 1230];
    const clockAt = q => (q <= 1 ? lerp(MIN[0], MIN[1], q) : lerp(MIN[1], MIN[2], q - 1));
    let on = false, r = null, sh = 0, w = 0, t = 0, q = 0, first = true, lastKey = '';
    function setMode() {
      const want = !reduce;
      if (want === on) return;
      on = want; root.classList.toggle('is-scroll', on); first = true; lastKey = '';
      if (!on) clear();
      kick();
    }
    function clear() {
      [sc, gl, tx].forEach(list => list.forEach(el => { if (el) { el.style.opacity = ''; el.style.transform = ''; el.style.visibility = ''; el.classList.remove('is-on'); } }));
      lay.forEach(L => Object.values(L).forEach(el => { if (el) el.style.transform = ''; }));
      hands.forEach(h => { h.el.style.transform = h.rest; });
      bars.forEach(b => b && b.classList.remove('is-cur'));
      if (fill) fill.style.transform = '';
    }
    setMode();
    try { reduceQ.addEventListener('change', setMode); bag.add(() => reduceQ.removeEventListener('change', setMode)); } catch (e) { /* old Safari */ }
    bag.add(() => { root.classList.remove('is-scroll'); clear(); });
    const op = v => (v > .999 ? '1' : v < .001 ? '0' : v.toFixed(3));
    function paint() {
      // the scenes: the one below stays, the next one fades in over it (and settles from a slight zoom)
      const O = idx.map(i => (i === 0 ? 1 : smooth(clamp(t - (i - 1)))));      // twice eased: the blend of two cities passes quickly
      for (let i = 0; i < N; i++) {
        const o = O[i];
        // a scene that cannot be seen (faded out, or under one that is fully in) is not drawn at all
        const seen = o > 0 && !(i < N - 1 && O[i + 1] >= 1);
        if (sc[i]) { sc[i].style.visibility = seen ? '' : 'hidden'; sc[i].style.opacity = op(o); sc[i].style.transform = i && o < 1 ? 'scale(' + (1 + .045 * (1 - o)).toFixed(4) + ')' : ''; }
        // depth: the far layer drifts a little, the near one more, the boat / tram / car travel
        const d = clamp(q - i, -1, 1), L = lay[i];
        if (L.far) L.far.style.transform = 'translate3d(' + (-d * w * .018).toFixed(1) + 'px,0,0)';
        if (L.near) L.near.style.transform = 'translate3d(' + (-d * w * .045).toFixed(1) + 'px,0,0)';
        if (L.mv) L.mv.style.transform = 'translate3d(' + (d * w * .2 * MOVE[i]).toFixed(1) + 'px,0,0)';
        // the glasses: the lens of the next city lies over the last one, so the colour turns rather than blinks
        const base = Math.min(N - 1, Math.floor(t)), f = t - base;
        const g = i === base ? 1 : i === base + 1 ? f : 0;
        if (gl[i]) { gl[i].style.visibility = g > 0 ? '' : 'hidden'; gl[i].style.opacity = op(g); gl[i].style.transform = 'translate3d(0,' + (-(q - 1) * 12).toFixed(1) + 'px,0)'; }
        // the words: one city's leave before the next one's arrive
        const c = clamp(1 - Math.abs(t - i) * 2.2);
        if (tx[i]) { tx[i].style.opacity = op(c); tx[i].style.transform = 'translate3d(0,' + ((t - i) * -26).toFixed(1) + 'px,0)'; tx[i].classList.toggle('is-on', c > .6); }
        if (bars[i]) bars[i].classList.toggle('is-cur', Math.round(t) === i);
      }
      if (fill) fill.style.transform = 'scaleX(' + (q / 2).toFixed(4) + ')';
      const m = clockAt(q);
      hands.forEach(h => { h.el.style.transform = 'rotate(' + (h.h ? (m / 60 % 12) * 30 : (m % 60) * 6).toFixed(1) + 'deg)'; });
    }
    return {
      measure() { if (!on) return; r = track.getBoundingClientRect(); sh = stage.offsetHeight; w = stage.offsetWidth; },
      frame(f) {
        if (!on || !r) return;
        if (r.bottom < -f.vh || r.top > f.vh * 2) return;
        const p = clamp((f.top - r.top) / Math.max(1, r.height - sh));
        const tt = cityAt(p), tq = p * (N - 1);
        if (first) { t = tt; q = tq; first = false; }
        else {
          const k = 1 - Math.exp(-f.dt * 9);           // eases out the steps of a mouse wheel
          t += (tt - t) * k; q += (tq - q) * k;
          if (Math.abs(tt - t) < .0004) t = tt;
          if (Math.abs(tq - q) < .0004) q = tq;
        }
        const key = t.toFixed(4) + '|' + q.toFixed(4) + '|' + w;
        if (key !== lastKey) { lastKey = key; paint(); }
        return t !== tt || q !== tq;
      }
    };
  };

  /* ============================================================
     17.0 · POSTCARDS (STILL · Városok, home page): three city cards.
     With a mouse they tilt towards the pointer and catch a glow; on
     touch screens they drift slowly by themselves (CSS). Reduced
     motion: still.
     ============================================================ */
  MODULES.postcards = function (root, cfg, bag) {
    const cards = $$('[data-nx-pc]', root);
    if (!finePtr) return {};
    cards.forEach(c => {
      let raf = 0, ev = null;
      const apply = () => {
        raf = 0; if (!ev || reduce) return;
        const r = c.getBoundingClientRect(), x = clamp((ev.clientX - r.left) / r.width), y = clamp((ev.clientY - r.top) / r.height);
        c.style.setProperty('--rx', ((.5 - y) * 9).toFixed(2) + 'deg');
        c.style.setProperty('--ry', ((x - .5) * 11).toFixed(2) + 'deg');
        c.style.setProperty('--gx', (x * 100).toFixed(1) + '%');
        c.style.setProperty('--gy', (y * 100).toFixed(1) + '%');
      };
      bag.on(c, 'pointerenter', e => { if (e.pointerType === 'mouse') c.classList.add('is-tilt'); });
      bag.on(c, 'pointermove', e => { if (e.pointerType !== 'mouse') return; ev = e; if (!raf) raf = requestAnimationFrame(apply); });
      bag.on(c, 'pointerleave', () => {
        ev = null; if (raf) { cancelAnimationFrame(raf); raf = 0; }
        c.classList.remove('is-tilt'); ['--rx', '--ry', '--gx', '--gy'].forEach(k => c.style.removeProperty(k));
      });
      bag.add(() => { if (raf) cancelAnimationFrame(raf); });
    });
    return {};
  };
  /* ============================================================
     public API + boot
     ============================================================ */
  const Still = window.Still = {
    version: '13.0.0',
    lens: null, locked: false, sheetOpen: false,
    mount: mountAll, unmount: scope => within(scope || document, unmount),
    formatMoney
  };
  /* every STILL section links the same stylesheet and font rules: keep one copy of each, in <head>,
     so deleting or hiding the section that brought it (theme editor) never unstyles the others */
  function dedupeCss() {
    const seen = new Set();
    $$('link[data-nx-css],style[data-nx-font]').forEach(el => {
      const k = el.tagName + '|' + (el.getAttribute('href') || el.textContent.trim());
      if (seen.has(k)) { el.remove(); return; }
      seen.add(k);
      if (el.parentNode === document.head) return;
      if (el.tagName === 'STYLE') { document.head.appendChild(el); return; }
      const c = el.cloneNode(false);                       // the copy loads from cache; the original goes once it has
      c.addEventListener('load', () => el.remove(), { once: true });
      document.head.appendChild(c);
    });
  }
  document.addEventListener('shopify:section:load', dedupeCss);
  /* the STILL · Photos section changed in the editor (a photo uploaded, a block added or removed):
     every photo on the page follows */
  const photosIn = t => !!(t && t.querySelector && t.querySelector('[data-nx-photos]'));
  document.addEventListener('shopify:section:load', e => { if (photosIn(e.target)) { readPhotos(); wake(); } });
  document.addEventListener('shopify:section:unload', e => { if (photosIn(e.target)) setTimeout(() => { readPhotos(); wake(); }, 0); });
  /* ============================================================
     ZOOM (13.0): an infographic opens large over the page. A tap on
     the picture zooms in further (it then scrolls inside the layer);
     the close button, Escape or a tap beside the picture closes it,
     and the focus goes back to where it was.
     ============================================================ */
  const Zoom = (() => {
    let el = null, img = null, back = null, open = false;
    function build() {
      if (el && el.isConnected) return;
      el = document.createElement('div'); el.className = 'nx nx-zoom'; el.setAttribute('role', 'dialog'); el.setAttribute('aria-modal', 'true'); el.hidden = true;
      el.innerHTML = '<button type="button" class="nx-zoom-x" aria-label="Bezárás">×</button><div class="nx-zoom-sc"><img alt="" draggable="false"></div>';
      document.body.appendChild(el);
      img = $('img', el);
      $('.nx-zoom-x', el).addEventListener('click', close);
      el.addEventListener('click', e => { if (e.target === el || e.target.classList.contains('nx-zoom-sc')) close(); });
      img.addEventListener('click', e => {
        const sc = img.parentNode, r = img.getBoundingClientRect(), fx = (e.clientX - r.left) / r.width, fy = (e.clientY - r.top) / r.height;
        el.classList.toggle('is-big');
        if (el.classList.contains('is-big')) requestAnimationFrame(() => { sc.scrollLeft = fx * img.offsetWidth - sc.clientWidth / 2; sc.scrollTop = fy * img.offsetHeight - sc.clientHeight / 2; });
      });
    }
    function show(src, alt, from) {
      build(); back = from || document.activeElement;
      img.src = src; img.alt = alt || ''; el.setAttribute('aria-label', (alt || '').slice(0, 80));
      el.classList.remove('is-big'); el.hidden = false; open = true; Still.locked = true;
      html.classList.add('nx-zoom-open');
      requestAnimationFrame(() => el.classList.add('is-on'));
      $('.nx-zoom-x', el).focus({ preventScroll: true });
    }
    function close() {
      if (!open) return; open = false; Still.locked = false;
      el.classList.remove('is-on'); html.classList.remove('nx-zoom-open');
      setTimeout(() => { if (!open) el.hidden = true; }, reduce ? 0 : 250);
      if (back && back.focus) try { back.focus({ preventScroll: true }); } catch (e) { back.focus(); }
    }
    document.addEventListener('click', e => {
      const b = e.target.closest && e.target.closest('[data-nx-zoom]'); if (!b) return;
      e.preventDefault(); show(b.getAttribute('data-nx-zoom'), b.getAttribute('data-nx-zoom-alt'), b);
    });
    document.addEventListener('keydown', e => {
      if (!open) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') { e.preventDefault(); $('.nx-zoom-x', el).focus(); }         // the close button is the only stop inside
    });
    return { show, close };
  })();
  /* 18.0: the lenses' filter percentages are rendered in Liquid in every section (the card snippet reads
     STILL · Product's fields or Theme settings > STILL), so the page needs no script for them. Still.facts
     stays as a no-op for anything that still calls it. */
  function applyFacts() {}
  Still.facts = applyFacts;
  const boot = () => { dedupeCss(); readPhotos(); mountAll(document); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
